import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import React__default, { useLayoutEffect, useEffect } from 'react';
import classNames from 'classnames';
import { ExclamationCircle, InfoCircle, CheckCircle, Warning, Close, Check, ArrowDown, ArrowUp, ExclamationCircleFill, CaretDown } from '@livingdesign/icons';
import { CSSTransition } from 'react-transition-group';
import { useSSRSafeId, SSRProvider } from '@react-aria/ssr';
import * as ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var style$1n = {"visuallyHidden":"VisuallyHidden-module_visuallyHidden__lKGid"};

/**
 * Assert state which your program assumes to be true.
 *
 * @private
 * @param {boolean} condition State to assert.
 * @param {string} message Message to be thrown when not in production.
 * @returns {undefined}
 */
const invariant = (condition, message) => {
  if (condition) {
    return;
  }

  const error = `LD: ${message || "unknown violation"}`;

  if (process.env.NODE_ENV === "production") {
    /* eslint-disable-next-line no-console */
    console.error(error);
  } else {
    throw new Error(error);
  }
};

/**
 * Tranform common props to properties for use with a HTMLElement.
 *
 * @todo Deprecate `className` and `style` in the next major release.
 */

/* eslint-disable-next-line @typescript-eslint/ban-types */

const applyCommonProps = (props,
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
Component, isWarnDisabled = false, isAriaLabelBanned = false) => {
  const componentName = Component.displayName || Component.name || "Unknown";
  /* istanbul ignore if */

  if (process.env.NODE_ENV !== "production") {
    if (!isWarnDisabled) {
      if (props.className) {
        /* eslint-disable-next-line no-console */
        console.warn(`${componentName}.className property will be deprecated in the next major version. Please use the "UNSAFE_className" property.`);
      }

      if (props.style) {
        /* eslint-disable-next-line no-console */
        console.warn(`${componentName}.style property will be deprecated in the next major version. Please use the "UNSAFE_style" property.`);
      }
      /* eslint-enable no-console */

    }
  }

  if (isAriaLabelBanned) {
    invariant(props["aria-label"] === undefined, `\`${componentName}\` accessibility violation. \`${componentName}\` is banned from using \`aria-label\`.`);
  }

  const appliedProps = {};

  for (const key in props) {
    // eslint-disable-next-line no-prototype-builtins
    if (props.hasOwnProperty(key)) {
      const value = props[key];

      if (key === "UNSAFE_className" || key === "className") {
        appliedProps.className = classNames(appliedProps.className, value);
      } else if (key === "UNSAFE_style" || key === "style") {
        appliedProps.style = appliedProps.style ? Object.assign(Object.assign({}, appliedProps.style), value) : value;
      } else {
        appliedProps[key] = value;
      }
    }
  }

  return appliedProps;
};

/**
 * A utility component that makes content available for screen readers only.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/visually-hidden/react/}
 */

const VisuallyHidden = props => {
  const _a = applyCommonProps(props, VisuallyHidden),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("span", Object.assign({
    className: classNames(style$1n.visuallyHidden, className)
  }, rest), void 0);
};
VisuallyHidden.displayName = "VisuallyHidden";

const A11yAnnouncementContext = React.createContext({
  announceAssertive:
  /* istanbul ignore next */
  () => undefined,
  announcePolite:
  /* istanbul ignore next */
  () => undefined
});
const useA11yAnnouncement = () => React.useContext(A11yAnnouncementContext);
/**
 * A utility component that allows messages to be programmatically broadcast to assistive technologies.
 *
 * - {@link https://livingdesign.walmart.com/utilities/a11y-announcement/react/ React documentation}
 *
 * ### A11yAnnouncementProvider
 *
 * Add the provider or the [Living Design Provider](https://livingdesign.walmart.com/utilities/living-design-provider/react) to your application's root. The provider must be in the DOM on page load to provide assistive technology announcement. It cannot be added dynamically.
 *
 * ```tsx
 * import * as React from "react";
 * import * as ReactDOM from "react-dom";
 * import {A11yAnnouncementProvider} from "@walmart-web/livingdesign-components";
 *
 * import {App} from "./App";
 *
 * ReactDOM.render(
 *   <A11yAnnouncementProvider>
 *     <App />
 *   </A11yAnnouncementProvider>,
 *   document.getElementById("root"),
 * );
 * ```
 *
 * ### useA11yAnnouncement
 *
 * Consume the provider via the hook. The hook provides two APIs:
 *
 * - `announceAssertive(annoucement: string)`: This should only be used for time-sensitive/critical notifications that absolutely require the user's immediate attention.
 * - `announcePolite(announcement: string)`: The screen reader will speak changes whenever the user is idle.
 *
 * ```tsx
 * import * as React from "react";
 * import {
 *   Button,
 *   useA11yAnnouncement,
 * } from "@walmart-web/livingdesign-components";
 *
 * export const MyComponent = () => {
 *   const {announceAssertive} = useA11yAnnouncement();
 *
 *   return (
 *     <Button
 *       onClick={() => {
 *         const date = new Date();
 *
 *         announceAssertive(`The time is: ${date.toLocaleTimeString()}`);
 *       }}
 *       variant="primary"
 *     >
 *       Make Announcement
 *     </Button>
 *   );
 * };
 * ```
 */

const A11yAnnouncementProvider = props => {
  const {
    children
  } = props;
  const [assertive, announceAssertive] = React.useState();
  const [polite, announcePolite] = React.useState();
  const contextValue = React.useRef({
    announceAssertive,
    announcePolite
  });
  return jsxs(A11yAnnouncementContext.Provider, Object.assign({
    value: contextValue.current
  }, {
    children: [children, jsx(VisuallyHidden, Object.assign({
      "aria-live": "assertive"
    }, {
      children: assertive
    }), void 0), jsx(VisuallyHidden, Object.assign({
      "aria-live": "polite"
    }, {
      children: polite
    }), void 0)]
  }), void 0);
};
A11yAnnouncementProvider.displayName = "A11yAnnouncementProvider";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentAlertIconIconSize = "small";

var style$1m = {"alert":"Alert-module_alert__wMX1Z","content":"Alert-module_content__9wB11","leading":"Alert-module_leading__RMvmn","error":"Alert-module_error__qqMKL","info":"Alert-module_info__aQojH","success":"Alert-module_success__YWowL","warning":"Alert-module_warning__-qBzu"};

var style$1l = {"action":"AlertActionButton-module_action__QglTg"};

/**
 * Get secure anchor props given a `target`.
 * @private
 *
 * @param {string} [target] The anchor's `target`
 * @returns {Object} The secure anchor props
 */
const getTarget = target => {
  if (target === "") {
    /* istanbul ignore next */
    return {};
  }
  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Security_and_privacy_concerns}
   */


  if (target === "_blank") {
    /* istanbul ignore next */
    return {
      target,
      rel: "noopener noreferrer"
    };
  }

  return {
    target
  };
};

/**
 * Looks like a button should act like a button.
 *
 * @see {@link https://jira.walmart.com/browse/LD-374?focusedCommentId=12802856&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-12802856}
 * @param anchorProps
 * @private
 */
const onAnchorKeyDown = anchorProps => {
  const {
    href,
    onClick,
    onKeyDown
  } = anchorProps;
  return event => {
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);

    if (event.defaultPrevented || event.key !== " ") {
      return;
    }

    onClick === null || onClick === void 0 ? void 0 : onClick(event); // anchorProps.onClick could call preventDefault
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition

    if (event.defaultPrevented || !href) {
      return;
    }

    location.assign(href);
  };
};

const isAnchor$2 = props => "href" in props;
/**
 * @private
 */


const AlertActionButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, AlertActionButton),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  const classes = classNames(style$1l.action, className);

  if (isAnchor$2(props)) {
    const _b = rest,
          {
      children,
      href,
      onClick,
      onKeyDown: anchorOnKeyDown,
      target
    } = _b,
          anchorProps = __rest(_b, ["children", "href", "onClick", "onKeyDown", "target"]); // Looks like a button should act like a button.
    //
    // @see {@link https://jira.walmart.com/browse/LD-374?focusedCommentId=12802856&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-12802856}


    const onKeyDown = onAnchorKeyDown({
      href,
      onClick,
      onKeyDown: anchorOnKeyDown
    });
    return jsx("a", Object.assign({
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps, {
      children: children
    }), void 0);
  }

  const buttonProps = __rest(rest, []);

  return jsx("button", Object.assign({
    className: classes,
    ref: ref,
    type: "button"
  }, buttonProps), void 0);
});
AlertActionButton.displayName = "AlertActionButton";

/**
 * Alerts provide brief information and feedback to a user.
 *
 * - {@link https://livingdesign.walmart.com/components/alert/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/alert/react/ React documentation}
 *
 * ### Action button
 *
 * The Alert has an optional action button that allows users to interact. Set the `actionButtonProps` prop to enable the action button:
 *
 * ```tsx
 * <Alert
 *   actionButtonProps={{
 *     children: "Action button",
 *     onClick(event) {
 *       console.log(event);
 *     }
 *   }}
 * >
 *   Alert
 * </Alert>
 * ```
 *
 * You can also change the action button to an `HTMLAnchorElement` by setting `href`:
 *
 * ```tsx
 * <Alert
 *   actionButtonProps={{
 *     children: "Go to account",
 *     href: "https://www.walmart.com/account",
 *     target: "_blank",
 *   }}
 * >
 *   Alert
 * </Alert>
 * ```
 *
 * ### Variant
 *
 * The Alert comes with four variants:
 *
 * - `error`: Conveys a negative message to the user.
 * - `info`: Provides general information in a highlighted way.
 * - `success`: Conveys a positive message to the user.
 * - `warning`: Draws the users attention to something that may affect their experience and maybe require their attention.
 *
 * Set the Alert's variant with the `variant` prop:
 *
 * ```tsx
 * <Alert variant="error">Error</Alert>
 * <Alert variant="info">Info</Alert>
 * <Alert variant="success">Success</Alert>
 * <Alert variant="warning">Warning</Alert>
 * ```
 */

const Alert = props => {
  const _a = applyCommonProps(props, Alert, true),
        {
    a11yIconLabel,
    actionButtonProps,
    children,
    className,
    variant = "success"
  } = _a,
        rest = __rest(_a, ["a11yIconLabel", "actionButtonProps", "children", "className", "variant"]);

  return jsxs("div", Object.assign({
    className: classNames(style$1m.alert, variant === "error" && style$1m.error, variant === "info" && style$1m.info, variant === "success" && style$1m.success, variant === "warning" && style$1m.warning, className)
  }, rest, {
    children: [jsxs("span", Object.assign({
      className: style$1m.leading
    }, {
      children: [jsx(VisuallyHidden, {
        children: `${a11yIconLabel !== null && a11yIconLabel !== void 0 ? a11yIconLabel : variant}:`
      }, void 0), variant === "error" && jsx(ExclamationCircle, {
        size: componentAlertIconIconSize
      }, void 0), variant === "info" && jsx(InfoCircle, {
        size: componentAlertIconIconSize
      }, void 0), variant === "success" && jsx(CheckCircle, {
        size: componentAlertIconIconSize
      }, void 0), variant === "warning" && jsx(Warning, {
        size: componentAlertIconIconSize
      }, void 0)]
    }), void 0), jsxs("div", Object.assign({
      className: style$1m.content
    }, {
      children: [jsx("div", {
        children: children
      }, void 0), actionButtonProps && jsx(AlertActionButton, Object.assign({}, actionButtonProps), void 0)]
    }), void 0)]
  }), void 0);
};
Alert.displayName = "Alert";

var style$1k = {"badge":"Badge-module_badge__pyKYK","blue":"Badge-module_blue__-iW-U","gray":"Badge-module_gray__0nMjz","green":"Badge-module_green__vUDgK","purple":"Badge-module_purple__AGQxq","red":"Badge-module_red__7MFUc","spark":"Badge-module_spark__81UXO","white":"Badge-module_white__REV9I","hasTextLabel":"Badge-module_hasTextLabel__aYXaF"};

/**
 * Badges are visual indicators of status or count.
 *
 * - {@link https://livingdesign.walmart.com/components/badge/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/badge/react/ React documentation}
 *
 * ### Color
 *
 * The Badge comes in seven colors. Set the color with the `color` prop:
 *
 * ```tsx
 * <Badge color="blue" />
 * <Badge color="gray" />
 * <Badge color="green" />
 * <Badge color="purple" />
 * <Badge color="red" />
 * <Badge color="spark" />
 * <Badge color="white" />
 * ```
 *
 * ### Content
 *
 * The Badge accepts optional content through the `children` prop:
 *
 * ```tsx
 * <Badge>10</Badge>
 * ```
 */

const Badge = props => {
  const _a = applyCommonProps(props, Badge),
        {
    children,
    color = "blue",
    className
  } = _a,
        rest = __rest(_a, ["children", "color", "className"]);

  const hasTextLabel = typeof children !== "boolean" && children !== null && typeof children !== "undefined";
  return jsx("span", Object.assign({
    className: classNames(style$1k.badge, hasTextLabel && style$1k.hasTextLabel, color === "blue" && style$1k.blue, color === "gray" && style$1k.gray, color === "green" && style$1k.green, color === "purple" && style$1k.purple, color === "red" && style$1k.red, color === "spark" && style$1k.spark, color === "white" && style$1k.white, className)
  }, rest, {
    children: children
  }), void 0);
};
Badge.displayName = "Badge";

var style$1j = {"banner":"Banner-module_banner__CLJsq","contentContainer":"Banner-module_contentContainer__4QBKp","content":"Banner-module_content__4RwrB","error":"Banner-module_error__awimp","info":"Banner-module_info__pMqlK","success":"Banner-module_success__WamPx","warning":"Banner-module_warning__Rc-5O"};

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentBannerCloseButtonIconSize = "small";

var style$1i = {"close":"BannerCloseButton-module_close__4K12p","closeIcon":"BannerCloseButton-module_closeIcon__sRIOx"};

/**
 * @private
 */

const BannerCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, BannerCloseButton, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    className: classNames(style$1i.close, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      size: componentBannerCloseButtonIconSize,
      className: style$1i.closeIcon
    }, void 0)
  }), void 0);
});

/**
 * Banners provide brief information about a significant incident affecting large numbers (or all) of the population.
 *
 * - {@link https://livingdesign.walmart.com/components/banner/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/banner/react/ React documentation}
 *
 * ### On close
 *
 * The Banner has a close button. Add a callback handler function using the `onClose` prop:
 *
 * ```tsx
 * <Banner onClose={(event) => console.log(event)}>
 *   Banner
 * </Banner>
 * ```
 *
 * ### Variant
 *
 * The Banner comes with four variants:
 *
 * - `error`: Use to convey a high emphasis or urgent message, such as a blocking error that needs to be resolved.
 * - `info`: Use to convey a message with default urgency or emphasis.
 * - `success`: Used to convey a positive message.
 * - `warning`: Used to a convey an emphasized warning message, such as something that the user should consider as it may impede their progress.
 *
 * Set the Banner's variant with the `variant` prop:
 *
 * ```tsx
 * <Banner variant="error">Error</Banner>
 * <Banner variant="info">Info</Banner>
 * <Banner variant="success">Success</Banner>
 * <Banner variant="warning">Warning</Banner>
 * ```
 */

const Banner = props => {
  const _a = applyCommonProps(props, Banner, true),
        {
    children,
    className,
    closeButtonProps,
    onClose,
    variant = "info"
  } = _a,
        rest = __rest(_a, ["children", "className", "closeButtonProps", "onClose", "variant"]);

  return jsxs("div", Object.assign({
    className: classNames(style$1j.banner, variant === "error" && style$1j.error, variant === "info" && style$1j.info, variant === "success" && style$1j.success, variant === "warning" && style$1j.warning, className),
    role: "alert"
  }, rest, {
    children: [jsx("div", Object.assign({
      className: style$1j.contentContainer
    }, {
      children: jsx("div", Object.assign({
        className: style$1j.content
      }, {
        children: children
      }), void 0)
    }), void 0), jsx(BannerCloseButton, Object.assign({
      "aria-label": "Close banner"
    }, closeButtonProps, {
      onClick: event => {
        var _a;

        (_a = closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(closeButtonProps, event);
        onClose(event);
      }
    }), void 0)]
  }), void 0);
};
Banner.displayName = "Banner";

var style$1h = {"container":"BottomSheet-module_container__---oN","bottomSheet":"BottomSheet-module_bottomSheet__l7oko","transitionEnter":"BottomSheet-module_transitionEnter__awM3c","transitionEnterActive":"BottomSheet-module_transitionEnterActive__TcuKm","transitionExit":"BottomSheet-module_transitionExit__QRIZO","transitionExitActive":"BottomSheet-module_transitionExitActive__-MXuU","header":"BottomSheet-module_header__Mwj3S","title":"BottomSheet-module_title__smbQn","content":"BottomSheet-module_content__BWa7J","contentInner":"BottomSheet-module_contentInner__AeUXT","actions":"BottomSheet-module_actions__DVHYh","scrim":"BottomSheet-module_scrim__-FSjf"};

/**
 * @private
 *
 * Boundary to prevent rendering of nested components during SSR. (This is necessary for
 * components that access DOM elements, e.g., component adds a portal element to the DOM.)
 *
 */

const OverlaySsrBoundary = props => {
  const {
    children
  } = props;
  const [isSafeToRender, setIsSafeToRender] = React.useState(false);
  React.useEffect(() => {
    setIsSafeToRender(true);
  }, []); // For an explanation of this eslint-disable, see: https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/jsx-no-useless-fragment.md#allowexpressions
  // eslint-disable-next-line react/jsx-no-useless-fragment

  return isSafeToRender ? jsx(Fragment, {
    children: children
  }, void 0) : null;
};

var style$1g = {"container":"Overlay-module_container__Nt8zd","trap":"Overlay-module_trap__8--S5"};

/**
 * The Focus Trap is a utility component that isolates browser focus for accessibility purposes.
 *
 * - {@link https://livingdesign.walmart.com/utilities/focus-trap/react/ React documentation}
 *
 * ### Content
 *
 * The Focus Trap accepts content for the focus lock through the `children` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import {
 *   Button,
 *   ButtonGroup,
 *   FocusTrap
 * } from "@walmart-web/livingdesign-components";
 *
 * const [disabled, setDisabled] = React.useState(true);
 *
 * <FocusTrap disabled={disabled}>
 *   <ButtonGroup>
 *     <Button>it's</Button>
 *     <Button>a</Button>
 *     <Button>trap</Button>
 *     <Button
 *       onClick={() => setDisabled(!disabled)}
 *       variant="tertiary"
 *     >
 *       {disabled ? "Arm" : "Disarm"}
 *     </Button>
 *   </ButtonGroup>
 * </FocusTrap>
 * ```
 *
 * ### Focus Return
 *
 * Whether or not focus should be returned to the triggering element is configurable through the `hasFocusReturn` prop.
 *
 * <FocusTrap hasFocusReturn={false}>
 *   {...}
 * </FocusTrap>
 */

const FocusTrap = props => {
  const _a = applyCommonProps(props, FocusTrap, true),
        {
    hasFocusReturn = true
  } = _a,
        rest = __rest(_a, ["hasFocusReturn"]); // Focus return conflicts with all UI announcements.
  //
  // @see {@link https://jira.walmart.com/browse/LD-607}
  //
  // 1. Focus return will stomp over aria-live. Screen reader can only read one thing at a time.
  // 2. Focus return is required for all modal-like components.
  // 3. Recommend manage all UI announcement within children or outside experience entirely.


  return jsx(FocusLock, Object.assign({
    returnFocus: hasFocusReturn
  }, rest), void 0);
};
FocusTrap.displayName = "FocusTrap";

/**
 * Create and attach element to the body to be used for portal.
 * @private
 *
 * @see {@link https://www.jayfreestone.com/writing/react-portals-with-hooks}
 *
 * @param {Object} [options] Configuration for the portal
 * @returns {React.Ref} A ref to the portal element
 */

const usePortal = (options = {}) => {
  const {
    a11yHidden = true
  } = options;
  const portalRef = React.useRef(null);
  const container = document.body;

  if (!portalRef.current) {
    portalRef.current = document.createElement("div");
  } // istanbul ignore next


  React.useEffect(() => {
    // istanbul ignore next
    if (!a11yHidden) {
      return () => undefined;
    } // We need to add `aria-hidden` to all children of <body>,
    // except for the modal. We .filter() the list to find only
    // <div>-like elements, ignoring <script>, <style>, etc.
    // We also don't want to affect any elements that may already
    // have `aria-hidden` set to true.


    const bodyChildren = Array.from(container.children).filter(child => child.innerHTML && [null, "false"].includes(child.getAttribute("aria-hidden")));
    bodyChildren.forEach(el => el.setAttribute("aria-hidden", "true"));
    return () => {
      bodyChildren.forEach(el => el.removeAttribute("aria-hidden"));
    };
  }, [a11yHidden, container]);
  React.useEffect(() => {
    container.appendChild(portalRef.current);
    return () => {
      // istanbul ignore next
      if (!portalRef.current) {
        return;
      } // NOTE: IE 11 needs removeChild


      container.removeChild(portalRef.current);
    };
  }, [container, portalRef]);
  return portalRef;
};

/**
 * @private
 */

const OverlayPortal = props => {
  const {
    children,
    className
  } = props,
        rest = __rest(props, ["children", "className"]);

  const overlayPortal = usePortal();
  return ReactDOM.createPortal(jsx("div", Object.assign({
    className: classNames(style$1g.container, className)
  }, rest, {
    children: jsx(FocusTrap, Object.assign({
      className: style$1g.trap
    }, {
      children: children
    }), void 0)
  }), void 0), overlayPortal.current);
};

/**
 * @private
 */

const Overlay = props => {
  const {
    children
  } = props,
        rest = __rest(props, ["children"]);

  return jsx(OverlaySsrBoundary, {
    children: jsx(OverlayPortal, Object.assign({}, rest, {
      children: children
    }), void 0)
  }, void 0);
};

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentOverlayCloseButtonIconSize = "medium";

var style$1f = {"closeButton":"OverlayCloseButton-module_closeButton__MvsqD"};

/**
 * @private
 */

const OverlayCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, OverlayCloseButton, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    "aria-label": "Close dialog",
    className: classNames(style$1f.closeButton, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      size: componentOverlayCloseButtonIconSize
    }, void 0)
  }), void 0);
});
OverlayCloseButton.displayName = "OverlayCloseButton";

var style$1e = {"scrim":"OverlayScrim-module_scrim__NVCgq"};

/**
 * @private
 */

const OverlayScrim = props => {
  const {
    className
  } = props,
        rest = __rest(props, ["className"]);

  return jsx("div", Object.assign({
    className: classNames(style$1e.scrim, className)
  }, rest), void 0);
};

/* istanbul ignore next */

const allowTouchMove = el => el.tagName === "INPUT" && el.type === "range" || el.tagName === "TEXTAREA";
/**
 * Locks the `body` from scrolling except for provided element.
 * @private
 *
 * @see {@link https://github.com/willmcpo/body-scroll-lock}
 *
 * @example
 * const [setScrollableContent] = useLockBodyScroll();
 *
 * return (
 *  <div ref={setScrollableContent} ....>...</div>
 * );
 */


const useLockBodyScroll = () => {
  const [contentElement, setContentElement] = React.useState(null);
  React.useEffect(() => {
    /**
     * @note On Safari iOS, body-scroll-lock locks scrolling on all elements except for the passed in ref. We need to pass in the element that can scroll, Which is why we pass in our own `allowTouchMove`.
     *
     * In order to do this it swallows touchmove events, which is a problem for child elements that have behavior that depends on touchmove (e.g. range inputs, text areas).
     *
     * @see {@link https://github.com/reakit/reakit/issues/469#issuecomment-549013422}
     * @see {@link https://github.com/willmcpo/body-scroll-lock/blob/fe6bfea3a134f337e77e62ae380cdf29eac7216b/src/bodyScrollLock.js#L52}
     * @see {@link https://github.com/willmcpo/body-scroll-lock/issues/111}
     * @see {@link https://github.com/willmcpo/body-scroll-lock/issues/76}
     */
    if (contentElement) {
      disableBodyScroll(contentElement, {
        allowTouchMove
      });
    }

    return () => clearAllBodyScrollLocks();
  }, [contentElement]);
  return [setContentElement];
};

/**
 * Trigger click event when clicking a given ref.
 * @private
 *
 * @see {@link https://github.com/gragland/usehooks/blob/master/src/pages/useOnClickOutside.md}
 *
 * @example
 * useOnClick(ref1);
 * useOnClick([ref1, ref2]);
 *
 * @param {Function} onClick Click event handler
 * @param {Array} [dependencies=[]] Use effect dependencies
 * @param {boolean} [useCapture=false] Whether to dispatch before any events beneath it in the DOM tree
 * @returns {undefined}
 */

const useOnClick = (onClick, dependencies = [], useCapture = false) => React.useEffect(() => {
  /* istanbul ignore if: JSDOM has open issue for PointerEvent, which is undefined and mocking window.PointerEvent doesn't work properly */
  if (window.PointerEvent) {
    /* istanbul ignore next */
    document.addEventListener("pointerdown", onClick, useCapture);
  } else {
    document.addEventListener("mousedown", onClick, useCapture);
    document.addEventListener("touchstart", onClick, useCapture);
  }

  return () => {
    /* istanbul ignore if: JSDOM has open issue for PointerEvent, which is undefined and mocking window.PointerEvent doesn't work properly */
    if (window.PointerEvent) {
      /* istanbul ignore next */
      document.removeEventListener("pointerdown", onClick, useCapture);
    } else {
      document.removeEventListener("mousedown", onClick, useCapture);
      document.removeEventListener("touchstart", onClick, useCapture);
    }
  };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
}, [onClick, ...dependencies, useCapture]);

/**
 * Trigger click event when clicking outside of a given ref.
 * @private
 *
 * @see {@link https://github.com/gragland/usehooks/blob/master/src/pages/useOnClickOutside.md}
 *
 * @example
 * const ref = useRef();
 *
 * useOnClickOutside(ref, onClick);
 *
 * @param {React.RefObject | React.RefObject[]} ref Ref to manage outside click
 * @param {Function} onClick Click event handler
 * @param {Boolean} [useCapture=false] Whether to dispatch before any events beneath it in the DOM tree
 * @returns {undefined}
 */

const useOnClickOutside = (ref, onClick, useCapture = false) => {
  const listener = event => {
    const elementRef = Array.isArray(ref) ? ref : [ref];

    if (elementRef.some(ref2 => {
      var _a;

      return (_a = ref2.current) === null || _a === void 0 ? void 0 : _a.contains(event.target);
    })) {
      return;
    }

    onClick(event);
  };

  useOnClick(listener, [ref, listener], useCapture);
};

/**
 * Trigger key down event.
 * @private
 *
 * @param {string[]} keys Collection of `KeyboardEvent.key`s to filter
 * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
 * @param {Function} onKeyDown Listener for filtered `keydown` events
 * @returns {undefined}
 */

const useOnKeyDown = (keys, onKeyDown) => React.useEffect(() => {
  const listener = event => {
    if (keys.includes(event.key) && typeof onKeyDown === "function") {
      onKeyDown(event);
    }
  };

  document.addEventListener("keydown", listener);
  return () => document.removeEventListener("keydown", listener);
}, [keys, onKeyDown]);

/**
 * @private
 */

const BottomSheetPortal = props => {
  const _a = applyCommonProps(props, BottomSheetPortal, true),
        {
    a11yLabel,
    actions,
    closeButtonProps,
    children,
    isOpen = false,
    onClose,
    onClosed,
    title
  } = _a,
        rest = __rest(_a, ["a11yLabel", "actions", "closeButtonProps", "children", "isOpen", "onClose", "onClosed", "title"]);

  const labelCount = (a11yLabel ? 1 : 0) + (title ? 1 : 0) === 1;
  invariant(labelCount, "`BottomSheet` accessibility violation. `BottomSheet` requires a `title` OR an `a11yLabel`.");

  const _b = closeButtonProps || {},
        {
    onClick: closeButtonOnClick
  } = _b,
        closeButtonRest = __rest(_b, ["onClick"]);

  const bottomSheetRef = React.useRef(null);
  const titleId = useSSRSafeId();
  const [setScrollableContentElement] = useLockBodyScroll();
  useOnClickOutside(bottomSheetRef, onClose);
  useOnKeyDown(["Esc", "Escape"], onClose);
  return jsx(CSSTransition, Object.assign({
    classNames: {
      enter: style$1h.transitionEnter,
      enterActive: style$1h.transitionEnterActive,
      exit: style$1h.transitionExit,
      exitActive: style$1h.transitionExitActive
    },
    in: isOpen,
    mountOnEnter: true,
    onExited: () => onClosed === null || onClosed === void 0 ? void 0 : onClosed(),
    timeout: 900
  }, {
    children: jsxs(Overlay, Object.assign({}, rest, {
      children: [jsx("div", Object.assign({
        className: style$1h.container
      }, {
        children: jsxs("div", Object.assign({
          className: style$1h.bottomSheet,
          ref: bottomSheetRef,
          role: "dialog"
        }, a11yLabel && {
          "aria-label": a11yLabel
        }, title && {
          "aria-labelledby": titleId
        }, {
          children: [typeof title === "function" ? title({
            titleId
          }) : jsxs("div", Object.assign({
            className: style$1h.header
          }, {
            children: [jsx(OverlayCloseButton, Object.assign({
              onClick: event => {
                closeButtonOnClick === null || closeButtonOnClick === void 0 ? void 0 : closeButtonOnClick(event);
                onClose(event);
              }
            }, closeButtonRest), void 0), title && jsx("h2", Object.assign({
              className: style$1h.title,
              id: titleId
            }, {
              children: title
            }), void 0)]
          }), void 0), jsx("div", Object.assign({
            className: style$1h.content,
            ref: setScrollableContentElement
          }, {
            children: jsx("div", Object.assign({
              className: style$1h.contentInner
            }, {
              children: children
            }), void 0)
          }), void 0), actions && jsx("div", Object.assign({
            className: style$1h.actions
          }, {
            children: actions
          }), void 0)]
        }), void 0)
      }), void 0), jsx(OverlayScrim, {
        className: style$1h.scrim
      }, void 0)]
    }), void 0)
  }), void 0);
};

/**
 * Bottom sheets are surfaces containing supplementary content that are anchored to the bottom of the screen.
 *
 * @see {@link https://livingdesign.walmart.com/components/bottom-sheet/react/}
 */

const BottomSheet = props => {
  const {
    isOpen,
    onClosed
  } = props,
        rest = __rest(props, ["isOpen", "onClosed"]);

  const [isTransitioning, setIsTransitioning] = React.useState(isOpen);
  const previousIsOpen = React.useRef(isOpen);
  /**
   * This hook delays `isOpen` property changes such that the
   * `BottomSheetPortal` component can transition in and out.
   */

  React.useEffect(() => {
    if (previousIsOpen.current === isOpen) {
      return;
    }

    previousIsOpen.current = isOpen;
    const handle = setTimeout(() => {
      setIsTransitioning(true);
    }, 0);
    /* eslint-disable-next-line consistent-return */

    return () => clearTimeout(handle);
  }, [isOpen]);

  if (!isOpen && !isTransitioning) {
    return null;
  }

  return jsx(BottomSheetPortal, Object.assign({
    isOpen: isOpen && isTransitioning,
    onClosed: () => {
      setIsTransitioning(false);
      onClosed === null || onClosed === void 0 ? void 0 : onClosed();
    }
  }, rest), void 0);
};
BottomSheet.displayName = "BottomSheet";

var style$1d = {"breadcrumb":"Breadcrumb-module_breadcrumb__DUnuq","item":"Breadcrumb-module_item__8Ig15","separator":"Breadcrumb-module_separator__azRAW"};

/**
 * The breadcrumb is a secondary navigation pattern that helps a user understand the hierarchy among levels and navigate back through them. Breadcrumbs show users their current location relative to the information architecture and enable them to quickly move up to a parent level or previous step.
 *
 * @see {@link https://livingdesign.walmart.com/components/breadcrumb/react/}
 */

const Breadcrumb = props => {
  const _a = applyCommonProps(props, Breadcrumb, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  const breadcrumbItemsCount = React.Children.count(children);
  const hasBreadcrumbItems = breadcrumbItemsCount > 0;
  invariant(hasBreadcrumbItems, "Invalid prop `children` supplied to `Breadcrumb`.");

  if (!hasBreadcrumbItems) {
    return null;
  }

  return jsx("nav", Object.assign({
    "aria-label": "breadcrumb",
    className: className
  }, rest, {
    children: jsx("ol", Object.assign({
      className: style$1d.breadcrumb
    }, {
      children: React.Children.map(children, (child, index) => jsxs("li", Object.assign({
        className: style$1d.item
      }, {
        children: [child, index < breadcrumbItemsCount - 1 && jsx("span", Object.assign({
          "aria-hidden": true,
          className: style$1d.separator
        }, {
          children: "/"
        }), void 0)]
      }), void 0))
    }), void 0)
  }), void 0);
};
Breadcrumb.displayName = "Breadcrumb";

var style$1c = {"breadcrumbItem":"BreadcrumbItem-module_breadcrumbItem__Q0Vao","current":"BreadcrumbItem-module_current__3R8bA"};

/**
 * @see {@link https://livingdesign.walmart.com/components/breadcrumb/react/}
 */

const BreadcrumbItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, BreadcrumbItem, true),
        {
    children,
    className,
    isCurrent = false,
    target
  } = _a,
        rest = __rest(_a, ["children", "className", "isCurrent", "target"]);

  return jsx("a", Object.assign({
    className: classNames(style$1c.breadcrumbItem, isCurrent && style$1c.current, className),
    itemProp: "item",
    ref: ref
  }, isCurrent && {
    "aria-current": "location"
  }, getTarget(target), rest, {
    children: jsx("span", Object.assign({
      itemProp: "name"
    }, {
      children: children
    }), void 0)
  }), void 0);
});
BreadcrumbItem.displayName = "BreadcrumbItem";

var style$1b = {"button":"Button-module_button__h0KNG","destructive":"Button-module_destructive__RpWPp","primary":"Button-module_primary__3gZpw","secondary":"Button-module_secondary__zie7r","tertiary":"Button-module_tertiary__qKUp2","isFullWidth":"Button-module_isFullWidth__kkUOD","small":"Button-module_small__H3bip","medium":"Button-module_medium__XTIF-","large":"Button-module_large__tzQHe","leading":"Button-module_leading__pc-A5","trailing":"Button-module_trailing__HdLq7"};

const isAnchor$1 = props => "href" in props;
/**
 * A button allows users to take actions and make choices, with a single press or a tap. Buttons are used as calls-to-action (CTA) across the experience. They are organized in the order or their importance: primary, secondary, and tertiary.
 *
 * @see {@link https://livingdesign.walmart.com/components/button/react/}
 */


const Button = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Button, true),
        {
    className,
    children,
    isFullWidth = false,
    leading,
    size = "small",
    trailing,
    variant = "secondary"
  } = _a,
        rest = __rest(_a, ["className", "children", "isFullWidth", "leading", "size", "trailing", "variant"]);

  const classes = classNames(style$1b.button, variant === "destructive" && style$1b.destructive, variant === "primary" && style$1b.primary, variant === "secondary" && style$1b.secondary, variant === "tertiary" && style$1b.tertiary, isFullWidth && style$1b.isFullWidth, size === "large" && style$1b.large, size === "medium" && style$1b.medium, size === "small" && style$1b.small, className);

  const content = jsxs(Fragment, {
    children: [leading && jsx("span", Object.assign({
      className: style$1b.leading
    }, {
      children: leading
    }), void 0), children, trailing && jsx("span", Object.assign({
      className: style$1b.trailing
    }, {
      children: trailing
    }), void 0)]
  }, void 0);

  if (isAnchor$1(props)) {
    const _b = rest,
          {
      href,
      onClick,
      onKeyDown: anchorOnKeyDown,
      target
    } = _b,
          anchorProps = __rest(_b, ["href", "onClick", "onKeyDown", "target"]); // Looks like a button should act like a button.
    //
    // @see {@link https://jira.walmart.com/browse/LD-374?focusedCommentId=12802856&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-12802856}


    const onKeyDown = onAnchorKeyDown({
      href,
      onClick,
      onKeyDown: anchorOnKeyDown
    });
    return jsx("a", Object.assign({
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps, {
      children: content
    }), void 0);
  }

  const _c = rest,
        {
    type = "button"
  } = _c,
        buttonProps = __rest(_c, ["type"]);

  return jsx("button", Object.assign({
    className: classes,
    ref: ref,
    type: type
  }, buttonProps, {
    children: content
  }), void 0);
});
Button.displayName = "Button";

var style$1a = {"buttonGroup":"ButtonGroup-module_buttonGroup__4Ul6x"};

/**
 * Button group displays multiple related actions in a horizontal row to help with arrangement and spacing.
 *
 * @see {@link https://livingdesign.walmart.com/components/button-group/react/}
 */

const ButtonGroup = props => {
  const _a = applyCommonProps(props, ButtonGroup, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("div", Object.assign({
    className: classNames(style$1a.buttonGroup, className)
  }, rest, {
    children: children
  }), void 0);
};
ButtonGroup.displayName = "ButtonGroup";

var style$19 = {"callout":"Callout-module_callout__4tB06","enter":"Callout-module_enter__-8t9l","bottomCenter":"Callout-module_bottomCenter__vgOKy","bottomLeft":"Callout-module_bottomLeft__HscKW","bottomRight":"Callout-module_bottomRight__oQNLB","left":"Callout-module_left__Wg1lY","right":"Callout-module_right__6dk9w","topCenter":"Callout-module_topCenter__aCLRA","topLeft":"Callout-module_topLeft__dWQHM","topRight":"Callout-module_topRight__akgpe","enterActive":"Callout-module_enterActive__hstj4","exit":"Callout-module_exit__V8De3","exitActive":"Callout-module_exitActive__QGSTc"};

var style$18 = {"baseCallout":"BaseCallout-module_baseCallout__-knqw","bottomCenter":"BaseCallout-module_bottomCenter__UWc2h","bottomLeft":"BaseCallout-module_bottomLeft__-cMwB","bottomRight":"BaseCallout-module_bottomRight__Lb4z-","left":"BaseCallout-module_left__k4BlY","right":"BaseCallout-module_right__t0sFp","topCenter":"BaseCallout-module_topCenter__pYBUK","topLeft":"BaseCallout-module_topLeft__EgzG9","topRight":"BaseCallout-module_topRight__-kNoy","content":"BaseCallout-module_content__t5oJU","label":"BaseCallout-module_label__LwYwq","actions":"BaseCallout-module_actions__pPBPR"};

var style$17 = {"closeButton":"CalloutCloseButton-module_closeButton__1ca1a"};

/**
 * @private
 */

const CalloutCloseButton = React.forwardRef((props, ref) => {
  const {
    className
  } = props,
        rest = __rest(props, ["className"]);

  return jsx("button", Object.assign({
    className: classNames(style$17.closeButton, className),
    ref: ref,
    type: "button"
  }, rest), void 0);
});

/**
 * @private
 */

const BaseCallout = React.forwardRef((props, ref) => {
  const {
    children,
    className,
    closeButtonProps,
    onClose,
    position
  } = props,
        rest = __rest(props, ["children", "className", "closeButtonProps", "onClose", "position"]);

  return jsx("div", Object.assign({
    className: classNames(style$18.baseCallout, position === "bottomCenter" && style$18.bottomCenter, position === "bottomLeft" && style$18.bottomLeft, position === "bottomRight" && style$18.bottomRight, position === "left" && style$18.left, position === "right" && style$18.right, position === "topCenter" && style$18.topCenter, position === "topLeft" && style$18.topLeft, position === "topRight" && style$18.topRight, className),
    ref: ref
  }, rest, {
    children: jsxs("div", Object.assign({
      className: style$18.content
    }, {
      children: [jsx("div", Object.assign({
        className: style$18.label
      }, {
        children: children
      }), void 0), jsx("div", Object.assign({
        className: style$18.actions
      }, {
        children: jsx(CalloutCloseButton, Object.assign({
          children: "Close"
        }, closeButtonProps, {
          onClick: event => {
            var _a;

            (_a = closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(closeButtonProps, event);
            onClose(event);
          }
        }), void 0)
      }), void 0)]
    }), void 0)
  }), void 0);
});

var style$16 = {"layoutManager":"CalloutLayoutManager-module_layoutManager__N32ub","bottom":"CalloutLayoutManager-module_bottom__v0kcF","left":"CalloutLayoutManager-module_left__kZZQi","right":"CalloutLayoutManager-module_right__MMgpO","top":"CalloutLayoutManager-module_top__5yVoZ"};

/**
 * Get position styles for a referrer element and a target element.
 * @private
 *
 * @param {Object} options
 * @param {Function} options.calculatePosition Get the left and top positions
 * @param {React.RefObject<HTMLElement>} options.referrerRef The element that refers to the target
 * @param {React.RefObject<HTMLElement>} options.targetRef The target element
 * @returns {Object} The alignment styles
 */
const getPositionStyle = ({
  calculatePosition,
  referrerRef,
  targetRef
}) => {
  var _a, _b;

  const referrerDOMRect = ((_a = referrerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) || {
    height: 0,
    width: 0
  };
  const targetDOMRect = ((_b = targetRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) || {
    height: 0,
    width: 0
  };
  const {
    left,
    top
  } = calculatePosition({
    referrerHeight: referrerDOMRect.height,
    referrerWidth: referrerDOMRect.width,
    targetHeight: targetDOMRect.height,
    targetWidth: targetDOMRect.width
  });
  return {
    // NOTE: Leverage GPU for positioning
    // @see {@link https://www.paulirish.com/2012/why-moving-elements-with-translate-is-better-than-posabs-topleft/}
    // @see {@link https://www.oreilly.com/content/animating-movement-with-translate3d/}
    transform: `translate3d(${left}px, ${top}px, 0)`
  };
};

const getCalloutPositionStyle = ({
  position,
  referrerRef,
  targetRef
}) => getPositionStyle({
  calculatePosition({
    referrerHeight,
    referrerWidth,
    targetHeight,
    targetWidth
  }) {
    let left;

    if (position === "left") {
      left = -1 * referrerWidth;
    } else if (position === "right") {
      left = targetWidth;
    } else if (position === "bottomLeft" || position === "topLeft") {
      left = targetWidth / 2 - referrerWidth + 24;
    } else if (position === "bottomRight" || position === "topRight") {
      left = targetWidth / 2 - 24;
    } else {
      left = (targetWidth - referrerWidth) / 2;
    }

    let top;

    if (position.startsWith("bottom")) {
      top = targetHeight;
    } else if (position.startsWith("top")) {
      top = -1 * referrerHeight;
    } else {
      top = (targetHeight - referrerHeight) / 2;
    }

    return {
      left,
      top
    };
  },

  referrerRef,
  targetRef
});

/**
 * @private
 */

const CalloutLayoutManager = props => {
  const {
    className,
    position,
    targetRef
  } = props,
        rest = __rest(props, ["className", "position", "targetRef"]);

  const [rootStyle, setRootStyle] = React.useState({
    // NOTE: hide the callout until it has been mounted and positioned
    visibility: "hidden"
  });
  const referrerRef = React.useRef(null);
  React.useEffect(() => {
    setRootStyle(getCalloutPositionStyle({
      position,
      referrerRef,
      targetRef
    }));
  }, [position, referrerRef, targetRef]);
  return jsx("div", Object.assign({
    className: classNames(style$16.layoutManager, position === "bottomCenter" && style$16.bottom, position === "bottomLeft" && style$16.bottom, position === "bottomRight" && style$16.bottom, position === "left" && style$16.left, position === "right" && style$16.right, position === "topCenter" && style$16.top, position === "topLeft" && style$16.top, position === "topRight" && style$16.top, className),
    ref: referrerRef,
    style: rootStyle
  }, rest), void 0);
};

/**
 * Callouts are overlays that display onboarding or coaching content.
 *
 * @see {@link https://livingdesign.walmart.com/components/callout/react/}
 */

const Callout = props => {
  const _a = applyCommonProps(props, Callout, true),
        {
    a11yContentLabel,
    closeButtonProps,
    children,
    className,
    content,
    isOpen = false,
    onClose,
    position = "bottomCenter",
    targetRef
  } = _a,
        rest = __rest(_a, ["a11yContentLabel", "closeButtonProps", "children", "className", "content", "isOpen", "onClose", "position", "targetRef"]);

  const child = React.Children.only(children);
  invariant(!!a11yContentLabel.trim(), "`Callout` accessibility violation. `Callout` requires an `a11yContentLabel`.");
  const buttonId = useSSRSafeId(child.props.id);
  const contentId = useSSRSafeId();
  const previousIsOpen = React.useRef(isOpen);
  const ref = React.useRef(null);
  React.useEffect(() => {
    var _a;

    if (!isOpen && previousIsOpen.current) {
      (_a = targetRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }

    previousIsOpen.current = isOpen;
  }, [isOpen, previousIsOpen, targetRef]);
  return jsxs("span", Object.assign({
    className: classNames(style$19.callout, position === "bottomCenter" && style$19.bottomCenter, position === "bottomLeft" && style$19.bottomLeft, position === "bottomRight" && style$19.bottomRight, position === "left" && style$19.left, position === "right" && style$19.right, position === "topCenter" && style$19.topCenter, position === "topLeft" && style$19.topLeft, position === "topRight" && style$19.topRight, className)
  }, rest, {
    children: [React.cloneElement(child, {
      "aria-labelledby": isOpen ? `${buttonId} ${contentId}` : undefined,

      /**
       * @note Emitting `id` when Callout is server-side rendered causes
       * problems with client-side hydration when `id`s to not match. Passing
       * `child.props.id` when the callout is closed gets around the issue.
       *
       * @see {@link https://reactjs.org/docs/react-dom.html#hydrate}
       * @see {@link https://jira.walmart.com/browse/LD-2066}
       */
      id: isOpen ? buttonId : child.props.id
    }), jsx(CSSTransition, Object.assign({
      classNames: {
        enter: style$19.enter,
        enterActive: style$19.enterActive,
        exit: style$19.exit,
        exitActive: style$19.exitActive
      },
      in: isOpen,
      mountOnEnter: true,
      nodeRef: ref,
      timeout: 200,
      unmountOnExit: true
    }, {
      children: jsx(CalloutLayoutManager, Object.assign({
        position: position,
        targetRef: targetRef
      }, {
        children: jsxs(BaseCallout, Object.assign({
          closeButtonProps: Object.assign({
            "aria-label": `Close ${a11yContentLabel}`
          }, closeButtonProps),
          onClose: onClose,
          ref: ref,
          position: position
        }, {
          children: [jsx(VisuallyHidden, {
            children: a11yContentLabel
          }, void 0), jsx("span", Object.assign({
            id: contentId
          }, {
            children: content
          }), void 0)]
        }), void 0)
      }), void 0)
    }), void 0)]
  }), void 0);
};
Callout.displayName = "Callout";

var style$15 = {"card":"Card-module_card__mmlzD"};

const CardSizeContext = React.createContext("small");

/**
 * Cards organize and present similiar content that users can scan quickly and interact with. They always show relevant content and actionable information. They contain text, images, icons and buttons placed with hierarchy, and may be placed as a series or feed of similar content.
 *
 * @see {@link https://livingdesign.walmart.com/components/card/react/}
 */

const Card = props => {
  const _a = applyCommonProps(props, Card, true),
        {
    children,
    className,
    size = "small"
  } = _a,
        rest = __rest(_a, ["children", "className", "size"]);

  return jsx(CardSizeContext.Provider, Object.assign({
    value: size
  }, {
    children: jsx("div", Object.assign({
      className: classNames(style$15.card, className)
    }, rest, {
      children: children
    }), void 0)
  }), void 0);
};
Card.displayName = "Card";

var style$14 = {"cardActions":"CardActions-module_cardActions__KyUOd","large":"CardActions-module_large__MmP9E","small":"CardActions-module_small__6NYBq","cardActionsSeparator":"CardActions-module_cardActionsSeparator__eHRs2"};

const CardActions = props => {
  const _a = applyCommonProps(props, CardActions, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  const size = React.useContext(CardSizeContext);
  return jsxs("div", Object.assign({
    className: classNames(style$14.cardActions, size === "large" && style$14.large, size === "small" && style$14.small, className)
  }, rest, {
    children: [jsx("div", {
      className: classNames(style$14.cardActionsSeparator, className)
    }, void 0), children]
  }), void 0);
};
CardActions.displayName = "CardActions";

var style$13 = {"large":"CardContent-module_large__lr5-F","small":"CardContent-module_small__ib33z"};

/**
 * @see {@link https://livingdesign.walmart.com/components/card/react/}
 */

const CardContent = props => {
  const _a = applyCommonProps(props, CardContent, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  const size = React.useContext(CardSizeContext);
  return jsx("div", Object.assign({
    className: classNames(size === "large" && style$13.large, size === "small" && style$13.small, className)
  }, rest, {
    children: children
  }), void 0);
};
CardContent.displayName = "CardContent";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentCardHeaderTitleAliasOptionsSize = "small";

var style$12 = {"body":"Body-module_body__qv1Uz","isMonospace":"Body-module_isMonospace__GqkpK","large":"Body-module_large__15R1s","medium":"Body-module_medium__KeFL9","small":"Body-module_small__QxxQp","weight400":"Body-module_weight400__5kNSQ","weight700":"Body-module_weight700__zurL5"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/body/react/}
 */

const Body = props => {
  const _a = applyCommonProps(props, Body),
        {
    as: Component = "span",
    className,
    isMonospace = false,
    size = "medium",
    weight = 400
  } = _a,
        rest = __rest(_a, ["as", "className", "isMonospace", "size", "weight"]);

  return jsx(Component, Object.assign({
    className: classNames(style$12.body, isMonospace && style$12.isMonospace, size === "large" && style$12.large, size === "medium" && style$12.medium, size === "small" && style$12.small, weight === 400 && style$12.weight400, weight === 700 && style$12.weight700, className)
  }, rest), void 0);
};
Body.displayName = "Body";

var style$11 = {"caption":"Caption-module_caption__9dXb-","isMonospace":"Caption-module_isMonospace__P4-b9","weight400":"Caption-module_weight400__H9Exj","weight700":"Caption-module_weight700__arHcE"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/caption/react/}
 */

const Caption = props => {
  const _a = applyCommonProps(props, Caption),
        {
    as: Component = "span",
    className,
    isMonospace = false,
    weight = 400
  } = _a,
        rest = __rest(_a, ["as", "className", "isMonospace", "weight"]);

  return jsx(Component, Object.assign({
    className: classNames(style$11.caption, isMonospace && style$11.isMonospace, weight === 400 && style$11.weight400, weight === 700 && style$11.weight700, className)
  }, rest), void 0);
};
Caption.displayName = "Caption";

var style$10 = {"display":"Display-module_display__NNvwQ","large":"Display-module_large__wNGCc","small":"Display-module_small__tezW0","weight400":"Display-module_weight400__sgSkx","weight700":"Display-module_weight700__bdcVX"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/display/react/}
 */

const Display = props => {
  const _a = applyCommonProps(props, Display),
        {
    as: Component = "span",
    className,
    size = "large",
    weight = 400
  } = _a,
        rest = __rest(_a, ["as", "className", "size", "weight"]);

  return jsx(Component, Object.assign({
    className: classNames(style$10.display, size === "large" && style$10.large, size === "small" && style$10.small, weight === 400 && style$10.weight400, weight === 700 && style$10.weight700, className)
  }, rest), void 0);
};
Display.displayName = "Display";

var style$$ = {"heading":"Heading-module_heading__zecdK","large":"Heading-module_large__YEwQF","medium":"Heading-module_medium__cks3s","small":"Heading-module_small__VkJcq","weight400":"Heading-module_weight400__nv9Tg","weight700":"Heading-module_weight700__rr2zh"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/heading/react/}
 */

const Heading = props => {
  const _a = applyCommonProps(props, Heading),
        {
    as: Component = "span",
    className,
    size = "medium",
    weight = 700
  } = _a,
        rest = __rest(_a, ["as", "className", "size", "weight"]);

  return jsx(Component, Object.assign({
    className: classNames(style$$.heading, size === "large" && style$$.large, size === "medium" && style$$.medium, size === "small" && style$$.small, weight === 400 && style$$.weight400, weight === 700 && style$$.weight700, className)
  }, rest), void 0);
};
Heading.displayName = "Heading";

var style$_ = {"container":"CardHeader-module_container__v--Va","large":"CardHeader-module_large__xJJcs","small":"CardHeader-module_small__5A7Jw","leadingIcon":"CardHeader-module_leadingIcon__u0oF5","title":"CardHeader-module_title__0h4fZ","trailing":"CardHeader-module_trailing__OBbzJ"};

const CardHeader = props => {
  const _a = applyCommonProps(props, CardHeader, true),
        {
    className,
    leadingIcon,
    title,
    trailing
  } = _a,
        rest = __rest(_a, ["className", "leadingIcon", "title", "trailing"]);

  const size = React.useContext(CardSizeContext);
  return jsxs("div", Object.assign({
    className: classNames(style$_.container, size === "large" && style$_.large, size === "small" && style$_.small, className)
  }, rest, {
    children: [leadingIcon && jsx("span", Object.assign({
      className: style$_.leadingIcon
    }, {
      children: leadingIcon
    }), void 0), jsx(Heading, Object.assign({
      size: componentCardHeaderTitleAliasOptionsSize,
      UNSAFE_className: style$_.title
    }, {
      children: title
    }), void 0), trailing && jsx("span", Object.assign({
      className: style$_.trailing
    }, {
      children: trailing
    }), void 0)]
  }), void 0);
};
CardHeader.displayName = "CardHeader";

const CardMedia = props => {
  const transformedProps = applyCommonProps(props, CardMedia, true);
  return jsx("div", Object.assign({}, transformedProps), void 0);
};
CardMedia.displayName = "CardMedia";

var style$Z = {"label":"Checkbox-module_label__bjQY1","checked":"Checkbox-module_checked__wUYYU","indeterminate":"Checkbox-module_indeterminate__DIwTi","disabled":"Checkbox-module_disabled__hG2m-","input":"Checkbox-module_input__GJmvM","checkbox":"Checkbox-module_checkbox__uJr64","labelText":"Checkbox-module_labelText__GB457"};

/**
 * Checkboxes are used for a list of options where the user may select multiple options, including all or none.
 *
 * @see {@link https://livingdesign.walmart.com/components/checkbox/react/}
 */

const Checkbox = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Checkbox, true),
        {
    a11yLabelledBy,
    checkboxProps = {},
    checked = false,
    className,
    disabled = false,
    indeterminate = false,
    id: initialId,
    label,
    name,
    onChange,
    value
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "checkboxProps", "checked", "className", "disabled", "indeterminate", "id", "label", "name", "onChange", "value"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`Checkbox` accessibility violation. `Checkbox` requires a `label` OR an `a11yLabelledBy`.");

  const {
    className: checkboxClassName
  } = checkboxProps,
        checkboxRest = __rest(checkboxProps, ["className"]);

  const id = useSSRSafeId(initialId);
  const inputRef = React.useRef(null);
  React.useImperativeHandle(ref, () => {
    // istanbul ignore next
    return inputRef.current;
  });
  React.useEffect(() => {
    // istanbul ignore next
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return jsxs("label", Object.assign({
    className: classNames(style$Z.label, checked && !indeterminate && style$Z.checked, disabled && style$Z.disabled, indeterminate && style$Z.indeterminate, className),
    htmlFor: id
  }, rest, {
    children: [jsx("input", Object.assign({
      checked: checked,
      className: classNames(style$Z.input, checkboxClassName),
      disabled: disabled,
      id: id,
      name: name,
      onChange: onChange,
      ref: inputRef,
      type: "checkbox"
    }, a11yLabelledBy && {
      "aria-labelledby": a11yLabelledBy
    }, value && {
      value
    }, checkboxRest), void 0), jsx("i", {
      className: style$Z.checkbox
    }, void 0), label && jsx("span", Object.assign({
      className: style$Z.labelText
    }, {
      children: label
    }), void 0)]
  }), void 0);
});
Checkbox.displayName = "Checkbox";

var style$Y = {"chip":"Chip-module_chip__5969U","selected":"Chip-module_selected__MuYhP","small":"Chip-module_small__eC2gH","large":"Chip-module_large__y-yKC","leading":"Chip-module_leading__bV3cM","trailing":"Chip-module_trailing__FJoji"};

/**
 * Chips allow users to make selections, filter content, or trigger actions.
 *
 * @see {@link https://livingdesign.walmart.com/components/chip/react/}
 */

const Chip = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Chip, true),
        {
    children,
    className,
    leading,
    trailing,
    selected = false,
    size = "small"
  } = _a,
        rest = __rest(_a, ["children", "className", "leading", "trailing", "selected", "size"]);

  return jsxs("button", Object.assign({
    "aria-pressed": selected,
    className: classNames(style$Y.chip, size === "large" && style$Y.large, size === "small" && style$Y.small, selected && style$Y.selected, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$Y.leading
    }, {
      children: leading
    }), void 0), children, trailing && jsx("span", Object.assign({
      className: style$Y.trailing
    }, {
      children: trailing
    }), void 0)]
  }), void 0);
});
Chip.displayName = "Chip";

var style$X = {"chipGroup":"ChipGroup-module_chipGroup__dt84h"};

/**
 * Chip group displays multiple related chips in a horizontal row to help with arrangement and spacing.
 *
 * @see {@link https://livingdesign.walmart.com/components/chip-group/react/}
 */

const ChipGroup = props => {
  const _a = applyCommonProps(props, ChipGroup, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("div", Object.assign({
    className: classNames(style$X.chipGroup, className)
  }, rest, {
    children: children
  }), void 0);
};
ChipGroup.displayName = "ChipGroup";

var style$W = {"collapse":"Collapse-module_collapse__bQAcR","closed":"Collapse-module_closed__5XE9I"};

/**
 * Suppress the warnings for useLayoutEffect.
 * @private
 *
 * We need useLayoutEffect to perform layout calculations. Else, async/queued React rendering to DOM will cause non-deterministic behavior.
 * React currently throws a warning when using useLayoutEffect on the server about how its effect cannot be encoded into the server renderer's output format. This
 * will lead to a mismatch between the initial, non-hydrated UI and the intended UI.
 * To get around it, we can conditionally use useEffect on the server (no-op) and useLayoutEffect in the browser. This way, the UI will not appear broken before hydration.
 * It matches logic in React's `shared/ExecutionEnvironment` file.
 * The package react-layout-effect does the same internally, hence there is no need to install additional package.
 * @see {@link https://jira.walmart.com/browse/LD-2031}
 * @see {@link https://reactjs.org/docs/hooks-reference.html#uselayouteffect}
 * @see {@link https://github.com/facebook/react/issues/14927#issuecomment-572717051}
 * @see {@link https://github.com/facebook/react/issues/14927#issuecomment-552205874}
 * @see {@link https://www.npmjs.com/package/react-layout-effect}
 */

const isDomEnvironment = typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.document.createElement !== "undefined"; // istanbul ignore next

const useIsomorphicLayoutEffect = isDomEnvironment ? useLayoutEffect : useEffect;

/**
 * Height transitions for the `Collapse` component.
 * @private
 *
 * @note This hook is separate from the `Collapse` component to permit mocking
 * such that `setTimeout` doesn't fire during testing.
 *
 * @param {boolean} isOpen Whether the collapse is open
 * @param {React.RefObject} ref The collapse's element's React ref
 * @returns {Object}
 */

const useCollapseTransition = (isOpen, ref) => {
  const [height, setHeight] = React.useState(isOpen ? "auto" : 0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const previousIsOpen = React.useRef(isOpen);
  /**
   * Observe changes to `isOpen` and initiate the appropriate state changes.
   *
   * @note `React.useLayoutEffect` is necessary for transitions to work.
   */

  useIsomorphicLayoutEffect(() => {
    const {
      current: collapse
    } = ref;

    if (isOpen === previousIsOpen.current || !collapse) {
      return;
    }

    previousIsOpen.current = isOpen;
    setHeight(collapse.scrollHeight);
    setIsTransitioning(true);

    if (!isOpen) {
      const timeoutId = setTimeout(() => setHeight(0), 0);
      /* eslint-disable-next-line consistent-return */

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, ref]);
  return {
    height,
    isTransitioning,

    onTransitionEnd() {
      setIsTransitioning(false);
      /**
       * Set `height: auto` when the Collapse is open so it resizes when its
       * childrens' layout changes.
       */

      if (isOpen) {
        setHeight("auto");
      }
    }

  };
};

/**
 * Trigger click event when clicking inside of a given ref.
 * @private
 *
 * @see {@link https://github.com/gragland/usehooks/blob/master/src/pages/useOnClickOutside.md}
 *
 * @example
 * const ref1 = useRef();
 * const ref2 = useRef();
 *
 * useOnClickInside(ref1, onClick);
 * useOnClickInside([ref1, ref2], onClick);
 *
 * @param {React.RefObject | React.RefObject[]} ref Ref to manage inside click
 * @param {Function} onClick Click event handler
 * @param {Boolean} [useCapture=false] Whether to dispatch before any events beneath it in the DOM tree
 * @returns {undefined}
 */

const useOnClickInside = (ref, onClick, useCapture = false) => {
  const listener = event => {
    const elementRef = Array.isArray(ref) ? ref : [ref];

    if (elementRef.some(ref2 => {
      var _a;

      return (_a = ref2.current) === null || _a === void 0 ? void 0 : _a.contains(event.target);
    }) === false) {
      return;
    }

    onClick(event);
  };

  useOnClick(listener, [ref, listener], useCapture);
};

/**
 * A utility component that is used to put long sections of information under a block that users can expand or collapse.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/collapse/react/}
 */

const Collapse = props => {
  const _a = applyCommonProps(props, Collapse, true),
        {
    className,
    isOpen = false,
    onTransitionEnd: initialOnTransitionEnd,
    style: initialStyle
  } = _a,
        rest = __rest(_a, ["className", "isOpen", "onTransitionEnd", "style"]);

  const ref = React.useRef(null);
  const {
    height,
    isTransitioning,
    onTransitionEnd
  } = useCollapseTransition(isOpen, ref);
  return jsx("div", Object.assign({
    className: classNames(style$W.collapse, !isOpen && style$W.closed, className),
    onTransitionEnd: event => {
      onTransitionEnd();
      initialOnTransitionEnd === null || initialOnTransitionEnd === void 0 ? void 0 : initialOnTransitionEnd(event);
    },
    ref: ref,
    style: Object.assign(Object.assign({
      height
    }, !isTransitioning && height === 0 && {
      visibility: "hidden"
    }), initialStyle)
  }, rest), void 0);
};
Collapse.displayName = "Collapse";

var style$V = {"container":"Container-module_container__VkBiM"};

/**
 * A utility component that sets a maximum width for layouts.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/container/react/}
 */

const Container = props => {
  const _a = applyCommonProps(props, Container, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("div", Object.assign({
    className: classNames(style$V.container, className)
  }, rest, {
    children: children
  }), void 0);
};
Container.displayName = "Container";

var style$U = {"dataTable":"DataTable-module_dataTable__56RbE"};

/**
 * Data Table displays a set of related information in rows and columns.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * The Data Table accepts content through the `children` prop.
 *
 * ```tsx
 * <DataTable>
 *   <DataTableHead>
 *     <DataTableRow>
 *       <DataTableHeader>First Name</DataTableHead>
 *       <DataTableHeader>Last Name</DataTableHead>
 *     </DataTableRow>
 *   <DataTableHead>
 *   <DataTableBody>
 *     <DataTableRow>
 *       <DataTableCell>James</DataTableCell>
 *       <DataTableCell>Kirk</DataTableCell>
 *     </DataTableRow>
 *   </DataTableBody>
 * </DataTable>
 * ```
 */

const DataTable = props => {
  const _a = applyCommonProps(props, DataTable),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("table", Object.assign({
    className: classNames(style$U.dataTable, className)
  }, rest), void 0);
};

/**
 * Data Table Body is the container for Data Table Rows.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * Data Table Body accepts content (Data Table Rows) through the `children` prop.
 *
 * ```tsx
 * <DataTable>
 *   <DataTableBody>
 *     <DataTableRow>
 *       <DataTableCell>James</DataTableCell>
 *       <DataTableCell>Kirk</DataTableCell>
 *     </DataTableRow>
 *   </DataTableBody>
 * </DataTable>
 * ```
 */

const DataTableBody = props => {
  const rest = applyCommonProps(props, DataTableBody);
  return jsx("tbody", Object.assign({}, rest), void 0);
};

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentDataTableBulkActionsIconIconSize = "medium";
const componentDataTableBulkActionsTextLabelAliasOptionsWeight = 700;
const componentDataTableCellAliasOptionsSize = "medium";
const componentDataTableCellActionsMenuItemLeadingIconIconSize = "small";
const componentDataTableHeaderIconIconSize = "small";
const componentDataTableHeaderTextLabelAliasOptionsSize = "medium";
const componentDataTableHeaderTextLabelAliasOptionsWeight = 700;

var style$T = {"dataTableCell":"DataTableCell-module_dataTableCell__euOxs","alphanumeric":"DataTableCell-module_alphanumeric__c3srw","numeric":"DataTableCell-module_numeric__Rwt3V"};

/**
 * Data Table Cell.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * The Data Table Cell accepts content through the `children` prop.
 *
 * ```tsx
 * <DataTableCell>James T Kirk</DataTableCell>
 * ```
 *
 * * ### Variant
 *
 * The Data Table Cell has two variants: alphanumeric (default) and numeric. The variant can be set through the `variant` prop.
 *
 * ```tsx
 * <DataTableCell variant="numeric">503</DataTableCell>
 * ```
 */

const DataTableCell = props => {
  const _a = applyCommonProps(props, DataTableCell),
        {
    children,
    className,
    variant = "alphanumeric"
  } = _a,
        rest = __rest(_a, ["children", "className", "variant"]);

  return jsx("td", Object.assign({
    className: classNames(style$T.dataTableCell, variant === "alphanumeric" && style$T.alphanumeric, variant === "numeric" && style$T.numeric, className)
  }, rest, {
    children: jsx(Body, Object.assign({
      isMonospace: variant === "numeric",
      size: componentDataTableCellAliasOptionsSize
    }, {
      children: children
    }), void 0)
  }), void 0);
};
DataTableCell.displayName = "DataTableCell";

var style$S = {"dataTableBulkActions":"DataTableBulkActions-module_dataTableBulkActions__Rk-3Y","content":"DataTableBulkActions-module_content__xunpz","icon":"DataTableBulkActions-module_icon__0MVeA","textLabel":"DataTableBulkActions-module_textLabel__YehMt","actionContent":"DataTableBulkActions-module_actionContent__oDdh5"};

/**
 * Data Table Bulk Actions is a set of actions that can be taken on selected Data Table Rows.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Select All
 *
 * A "Select All" button will be displayed when a callback is provided through the `onSelectAll` prop.
 *
 * ### Clear Selected
 *
 * Behavior for the "Clear Selected" button can be provided through the `onClearSelected` prop.
 *
 * ```tsx
 * import * as React from 'react';
 * import { DataTableBulkActions } from '@walmart-web/livingdesign-components'
 *
 * const DATA = [{ id: 1, color: "blue" }, { id: 2, color: "green" }, { id: 3, color: "purple" }];
 *
 * const MyComponent = () => {
 *   const [selected, setSelected] = React.useState([]);
 *   const selectAll = setSelected(DATA.map((datum) => datum.id));
 *   const clearSelected = setSelected([]);
 *
 *   return <DataTableBulkActions onSelectAll={selectAll} onClearSelected={clearSelected} />
 * }
 * ```
 *
 * ### Action Content
 *
 * Data Table Bulk Actions accepts a group of buttons with bulk action behavior through the `actionContent` prop.
 *
 * ```tsx
 * import * as React from 'react';
 * import { Button, ButtonGroup, DataTableBulkActions } from '@walmart-web/livingdesign-components'
 *
 * const MyComponent = () => {
 *   <DataTableBulkActions
 *     actionContent={
 *       <ButtonGroup>
 *         <Button onClick={...}>Button 1</Button>
 *         <Button onClick={...}>Button 2</Button>
 *         <Button onClick={...} variant="primary">Button 3</Button>
 *       </ButtonGroup>
 *     }
 *   />
 * }
 * ```
 *
 * ### Count and Count Label
 *
 * The number of rows selected can be provided through the `count` prop.
 *
 * By default, a text label with this information is displayed (e.g. "1 row selected" or "3 rows selected"). This default count label can be overridden through the `countLabel` prop.
 *
 * ```tsx
 * import * as React from 'react';
 * import { DataTableBulkActions } from '@walmart-web/livingdesign-components'
 *
 * const DATA = [{ id: 1, color: "blue" }, { id: 2, color: "green" }, { id: 3, color: "purple" }];
 *
 * const MyComponent = () => {
 *   const [selected, setSelected] = React.useState([]);
 *   const selectAll = setSelected(DATA.map((datum) => datum.id));
 *   const clearSelected = setSelected([]);
 *
 *   const countLabel = `${selected.length} klingon warrior${selected.length === 1 ? '' : 's'}`
 *
 *   return <DataTableBulkActions countLabel={countLabel} onSelectAll={selectAll} onClearSelected={clearSelected} />
 * }
 * ```
 *
 * ### A11y Label
 *
 * Data Table Bulk Actions accepts a custom aria-label through the `a11yLabel` prop. (By default, the aria-label is "Table actions").
 */

const DataTableBulkActions = props => {
  const _a = applyCommonProps(props, DataTableBulkActions),
        {
    a11yLabel = "Table actions",
    actionContent,
    onClearSelectedButtonProps,
    count = 0,
    countLabel = `${count} ${count === 1 ? "row" : "rows"} selected`,
    onClearSelected,
    onSelectAll,
    selectAllButtonProps,
    className
  } = _a,
        rest = __rest(_a, ["a11yLabel", "actionContent", "onClearSelectedButtonProps", "count", "countLabel", "onClearSelected", "onSelectAll", "selectAllButtonProps", "className"]);

  const {
    announcePolite
  } = useA11yAnnouncement();
  const previousCount = React.useRef(count);
  React.useEffect(() => {
    if (previousCount.current !== count) {
      previousCount.current = count;
      announcePolite(countLabel);
    }
  }, [count, announcePolite, countLabel]);
  return jsxs("div", Object.assign({
    "aria-label": a11yLabel,
    className: classNames(style$S.dataTableBulkActions, classNames),
    role: "region"
  }, rest, {
    children: [jsxs("div", Object.assign({
      className: style$S.content
    }, {
      children: [jsx(Check, {
        className: style$S.icon,
        size: componentDataTableBulkActionsIconIconSize
      }, void 0), jsx(Body, Object.assign({
        UNSAFE_className: style$S.textLabel,
        weight: componentDataTableBulkActionsTextLabelAliasOptionsWeight
      }, {
        children: countLabel
      }), void 0), jsxs("div", {
        children: [onSelectAll && jsx(Button, Object.assign({
          children: "Select all"
        }, selectAllButtonProps, {
          onClick: event => {
            var _a;

            (_a = selectAllButtonProps === null || selectAllButtonProps === void 0 ? void 0 : selectAllButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(selectAllButtonProps, event);
            onSelectAll(event);
          },
          variant: "tertiary"
        }), void 0), jsx(Button, Object.assign({
          children: "Clear selected"
        }, onClearSelectedButtonProps, {
          onClick: event => {
            var _a;

            (_a = onClearSelectedButtonProps === null || onClearSelectedButtonProps === void 0 ? void 0 : onClearSelectedButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(onClearSelectedButtonProps, event);
            onClearSelected === null || onClearSelected === void 0 ? void 0 : onClearSelected(event);
          },
          variant: "tertiary"
        }), void 0)]
      }, void 0)]
    }), void 0), actionContent && jsx("div", Object.assign({
      className: style$S.actionContent
    }, {
      children: actionContent
    }), void 0)]
  }), void 0);
};
DataTableBulkActions.displayName = "DataTableBulkActions";

var style$R = {"dataTableCellActions":"DataTableCellActions-module_dataTableCellActions__b-y9c","content":"DataTableCellActions-module_content__0Mvt6"};

/**
 * Data Table Cell Actions is a container for interactive elements that perform actions on a selected cell.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * Data Table Cell Actions accepts content through the `children` prop.
 *
 * ```tsx
 * import { Gear, Pencil, TrashCan } from "@livingdesign/icons";
 * import { DataTableCellActions, IconButton } from "@walmart-web/livingdesign-components";
 *
 * <DataTableCellActions>
 *   <IconButton a11yLabel="Edit" onClick={...}>
 *     <Pencil />
 *   </IconButton>
 *   <IconButton a11yLabel="Delete" onClick={...}>
 *     <TrashCan />
 *   </IconButton>
 *   <IconButton a11yLabel="Settings" onClick={...}>
 *     <Gear />
 *   </IconButton>
 * </DataTableCellActions>
 * ```
 */

const DataTableCellActions = props => {
  const _a = applyCommonProps(props, DataTableCellActions),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("td", Object.assign({
    className: classNames(style$R.dataTableCellActions, className)
  }, rest, {
    children: jsx("span", Object.assign({
      className: style$R.content
    }, {
      children: children
    }), void 0)
  }), void 0);
};
DataTableCellActions.displayName = "DataTableCellActions";

var style$Q = {"container":"DataTableCellActionsMenuContainer-module_container__B6ZiX","content":"DataTableCellActionsMenuContainer-module_content__hxJru"};

/**
 * @private
 */

const DataTableCellActionsMenuContainer = props => {
  const {
    children,
    className,
    menuProps,
    onClose
  } = props,
        rest = __rest(props, ["children", "className", "menuProps", "onClose"]);

  const ref = React.useRef(null);
  useOnClickOutside(ref, onClose);
  return jsx("div", Object.assign({
    className: classNames(style$Q.container, className)
  }, rest, {
    children: jsx("div", Object.assign({}, menuProps, {
      className: classNames(style$Q.content, menuProps === null || menuProps === void 0 ? void 0 : menuProps.className),
      ref: element => {
        ref.current = element;
        /* istanbul ignore if */

        if (typeof (menuProps === null || menuProps === void 0 ? void 0 : menuProps.ref) === "function") {
          menuProps.ref(element);
        } else if (!!(menuProps === null || menuProps === void 0 ? void 0 : menuProps.ref) && typeof (menuProps === null || menuProps === void 0 ? void 0 : menuProps.ref) === "object") {
          menuProps.ref.current = element;
        }
      },
      role: "menu"
    }, {
      children: children
    }), void 0)
  }), void 0);
};

var style$P = {"layoutContainer":"LayoutContainer-module_layoutContainer__XoDs0","content":"LayoutContainer-module_content__jTcIG"};

/**
 * Debounce calls to a function.
 * @private
 *
 * @param {Function} fn The function to call
 * @param {number} timeout The time to debounce calls
 * @returns {Function} The debounced function
 */
const debounce = (fn, timeout) => {
  let timerID;

  const cancel = () => {
    clearTimeout(timerID);
  };

  const debounced = (...args) => {
    clearTimeout(timerID);
    timerID = setTimeout(() => {
      fn(...args);
    }, timeout);
  };

  debounced.cancel = cancel;
  return debounced;
};

/**
 * Handle debounced `window` resize events.
 * @private
 *
 * @param {Function} onResize The function called when the window is resized.
 * @returns {undefined}
 */

const useDebouncedWindowResize = onResize => React.useEffect(() => {
  const listener = debounce(onResize, 250);
  window.addEventListener("resize", listener);
  return () => {
    window.removeEventListener("resize", listener);
    listener.cancel();
  };
}, [onResize]);

/**
 * A layout container positioning manager for components with a trigger and
 * content (Callout, Menu, Popover, etc.)
 *
 * @private
 */

const LayoutContainer = props => {
  const {
    calculatePosition,
    className,
    content,
    trigger,
    triggerRef
  } = props,
        rest = __rest(props, ["calculatePosition", "className", "content", "trigger", "triggerRef"]);

  const contentRef = React.useRef(null);
  const [positionStyle, setPositionStyle] = React.useState(undefined);
  const doSetPositionStyle = React.useCallback(() => {
    setPositionStyle(content ? getPositionStyle({
      calculatePosition,
      referrerRef: contentRef,
      targetRef: triggerRef
    }) : undefined);
  }, [calculatePosition, content, triggerRef]);
  useIsomorphicLayoutEffect(doSetPositionStyle, [doSetPositionStyle]);
  useDebouncedWindowResize(doSetPositionStyle);
  return jsxs("div", Object.assign({
    className: classNames(style$P.layoutContainer, className)
  }, rest, {
    children: [trigger, jsx("div", Object.assign({
      className: style$P.content,
      ref: contentRef,
      style: positionStyle
    }, {
      children: content
    }), void 0)]
  }), void 0);
};

/**
 * Menu focus management hook.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const menuFocus = useMenuFocus(children, isOpen, menuRef, triggerRef);
 *
 *   // Get the currently focused item's index:
 *   menuFocus.focusedItemIndex;
 *
 *   // Change the focused item:
 *   menuFocus.focusFirstItem();
 *
 *   // ...
 * };
 * ```
 *
 * @param {React.ReactNode} children The `children` of the menu element.
 * @param {boolean} isOpen If the menu is open.
 * @param {React.RefObject} menuRef The ref for the menu element.
 * @param {React.RefObject} triggerRef The ref for the trigger element.
 * @returns {Object}
 */

const useMenuFocus = (children, isOpen, menuRef, triggerRef) => {
  const [focusedItemIndex, setFocusedItemIndex] = React.useState(0);
  const previousFocusedItemIndex = React.useRef(focusedItemIndex);
  const previousIsOpen = React.useRef(isOpen);
  const lastIndex = React.Children.count(children) - 1;
  React.useEffect(() => {
    var _a, _b;

    if (isOpen === previousIsOpen.current) {
      return;
    }

    previousIsOpen.current = isOpen;

    if (isOpen) {
      const child = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.children[focusedItemIndex];
      child === null || child === void 0 ? void 0 : child.focus();
    } else {
      (_b = triggerRef.current) === null || _b === void 0 ? void 0 : _b.focus();
    }
  }, [focusedItemIndex, isOpen, menuRef, previousIsOpen, triggerRef]);
  React.useEffect(() => {
    var _a;

    if (!isOpen || focusedItemIndex === previousFocusedItemIndex.current) {
      return;
    }

    previousFocusedItemIndex.current = focusedItemIndex;
    const child = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.children[focusedItemIndex];
    child === null || child === void 0 ? void 0 : child.focus();
  }, [focusedItemIndex, isOpen, menuRef, previousFocusedItemIndex]);
  const focusFirstItem = React.useCallback(() => setFocusedItemIndex(0), [setFocusedItemIndex]);
  const focusLastItem = React.useCallback(() => setFocusedItemIndex(lastIndex), [lastIndex, setFocusedItemIndex]);
  const focusNextItem = React.useCallback(() => {
    setFocusedItemIndex(index => index + 1 > lastIndex ? 0 : index + 1);
  }, [lastIndex, setFocusedItemIndex]);
  const focusPreviousItem = React.useCallback(() => {
    setFocusedItemIndex(index => index - 1 < 0 ? lastIndex : index - 1);
  }, [lastIndex, setFocusedItemIndex]);
  const menuFocus = React.useRef({
    focusFirstItem,
    focusedItemIndex,
    focusLastItem,
    focusNextItem,
    focusPreviousItem
  });
  return menuFocus.current;
};

/**
 * Data Table Cell Actions Menu is a dropdown menu containing interactive elements that perform actions on a selected cell.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * The Data Table Cell Actions Menu accepts content through the `children` prop.
 *
 * ### Open/Close
 *
 * The Data Table Cell Actions Menu is set to open or close through the `isOpen` prop.
 *
 * The logic for opening and closing the Data Table Cell Actions Menu is controlled through the `onOpen` and `onClose` props.
 *
 * ### Trigger
 *
 * The Data Table Cell Actions Menu requires that a triggering element be provided through the `trigger` prop.
 * A ref to the triggering element is also required to be provided through the `triggerRef` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import { More } from "@livingdesign/icons"
 * import { DataTableCellActionsMenu, DataTableCellActionsMenuItem, IconButton } from "@walmart-web/livingdesign-components"
 *
 * const MyComponent = () => {
 *   const [isOpen, setIsOpen] = React.useState(false);
 *   const triggerRef = React.useRef<HTMLButtonElement>(null);
 *
 *   return (<DataTableCellActionsMenu
 *     isOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *     onOpen={() => setIsOpen(true)}
 *     trigger={
 *       <IconButton a11yLabel="Toggle menu" ref={triggerRef}>
 *         <More />
 *       </IconButton>
 *     }
 *     triggerRef={triggerRef}
 *   >
 *     <DataTableCellActionsMenuItem>Action 1</DataTableCellActionsMenuItem>
 *     <DataTableCellActionsMenuItem>Action 2</DataTableCellActionsMenuItem>
 *     <DataTableCellActionsMenuItem>Action 3</DataTableCellActionsMenuItem>
 *   </DataTableCellActionsMenu>);
 * }
 * ```
 */

const DataTableCellActionsMenu = props => {
  const _a = applyCommonProps(props, DataTableCellActionsMenu),
        {
    children,
    isOpen = false,
    onClose,
    onKeyDown,
    onOpen,
    trigger,
    triggerRef
  } = _a,
        rest = __rest(_a, ["children", "isOpen", "onClose", "onKeyDown", "onOpen", "trigger", "triggerRef"]);

  const triggerElement = React.Children.only(trigger);
  const menuId = useSSRSafeId();
  const menuRef = React.useRef(null);
  const triggerId = useSSRSafeId(triggerElement.props.id);
  const menuFocus = useMenuFocus(children, isOpen, menuRef, triggerRef);
  const calculatePosition = React.useCallback(({
    referrerWidth,
    targetHeight,
    targetWidth
  }) => ({
    left: -1 * referrerWidth + targetWidth,
    top: targetHeight
  }), []);
  return jsx(LayoutContainer, Object.assign({
    calculatePosition: calculatePosition,
    content: isOpen && jsx(DataTableCellActionsMenuContainer, Object.assign({
      menuProps: {
        "aria-labelledby": triggerId,
        id: menuId,

        onKeyDown(event) {
          const {
            code
          } = event;

          if (code === "ArrowDown") {
            menuFocus.focusNextItem();
          } else if (code === "ArrowUp") {
            menuFocus.focusPreviousItem();
          } else if (code === "End") {
            menuFocus.focusLastItem();
          } else if (code === "Home") {
            menuFocus.focusFirstItem();
          }
        },

        ref: menuRef
      },
      onClose: onClose
    }, {
      children: React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) {
          return child;
        }

        return React.cloneElement(child, {
          key: index,

          onClick(event) {
            var _a, _b;

            (_b = (_a = child.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            onClose(event);
          },

          tabIndex: index === menuFocus.focusedItemIndex ? undefined : "-1"
        });
      })
    }), void 0),
    onKeyDown: event => {
      onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);

      if (isOpen && event.code === "Escape") {
        onClose(event);
      }
    },
    trigger: React.cloneElement(triggerElement, {
      "aria-controls": menuId,
      "aria-expanded": isOpen,
      "aria-haspopup": true,
      id: triggerId,

      onClick(event) {
        var _a, _b;

        (_b = (_a = triggerElement.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        onOpen(event);
        menuFocus.focusFirstItem();
      },

      onKeyDown(event) {
        var _a, _b;

        (_b = (_a = triggerElement.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        const {
          code
        } = event;

        if (code === "ArrowDown") {
          onOpen(event);
          menuFocus.focusFirstItem();
        } else if (code === "ArrowUp") {
          onOpen(event);
          menuFocus.focusLastItem();
        }
      },

      ref: triggerRef
    }),
    triggerRef: triggerRef
  }, rest), void 0);
};
DataTableCellActionsMenu.displayName = "DataTableCellActionsMenu";

var style$O = {"menuItem":"DataTableCellActionsMenuItem-module_menuItem__wvGrh","leadingIcon":"DataTableCellActionsMenuItem-module_leadingIcon__pXK5x"};

/**
 * Data Table Cell Actions Menu Item.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * The Data Table Cell Actions Menu Item accepts content (text label) through the `children` prop.
 *
 * ```tsx
 * <DataTableCellActionsMenuItem>Delete</DataTableCellActionsMenuItem>
 * ```
 *
 * ### Leading Icon
 *
 * The Data Table Cell Actions Menu Item accepts an optional leading icon through the `leadingIcon` prop.
 *
 * ```tsx
 * import { TrashCan } from "@livingdesign/icons";
 * import { DataTableCellActions } from "@walmart-web/livingdesign-components";
 *
 * <DataTableCellActionsMenuItem leadingIcon={<TrashCan />}>Delete</DataTableCellActionsMenuItem>
 * ```
 */

const DataTableCellActionsMenuItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, DataTableCellActionsMenuItem),
        {
    children,
    className,
    leadingIcon
  } = _a,
        rest = __rest(_a, ["children", "className", "leadingIcon"]);

  return jsxs("button", Object.assign({
    className: classNames(style$O.menuItem, className),
    ref: ref,
    role: "menuitem",
    type: "button"
  }, rest, {
    children: [leadingIcon && jsx("span", Object.assign({
      className: style$O.leadingIcon
    }, {
      children: React.isValidElement(leadingIcon) ? React.cloneElement(leadingIcon, {
        size: componentDataTableCellActionsMenuItemLeadingIconIconSize
      }) : leadingIcon
    }), void 0), children]
  }), void 0);
});
DataTableCellActionsMenuItem.displayName = "DataTableCellActionsMenuItem";

var style$N = {"dataTableCellStatus":"DataTableCellStatus-module_dataTableCellStatus__VhsrU"};

/**
 * Data Table Cell Status displays a Tag with status information for the containing row.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * The Data Table Cell accepts content (a Tag component) through the `children` prop.
 *
 * ```tsx
 * import { DataTableCellStatus, Tag } from "@walmart-web/livingdesign-components"
 *
 * <DataTableCellStatus>
 *   <Tag children="Red" color="red" />
 * </DataTableCellStatus>
 * ```
 */

const DataTableCellStatus = props => {
  const _a = applyCommonProps(props, DataTableCellStatus),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("td", Object.assign({
    className: classNames(style$N.dataTableCellStatus, className)
  }, rest), void 0);
};
DataTableCellStatus.displayName = "DataTableCellStatus";

var style$M = {"cellSelect":"DataTableCellSelect-module_cellSelect__wUrJD"};

/**
 * Data Table Cell Select contains a checkbox to select/deselect the containing row.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Checkbox
 *
 * The Data Table Cell Select controls the state of the checkbox through the `checked` prop.
 *
 * The Data Table Cell Select requires a callback be provided through the `onChange` prop to handle interaction with the checkbox.
 *
 * The Data Table Cell Select accepts additional control of the checkbox state and configuration through the `disabled`, `name`, `value`, and `checkboxProps` props.
 *
 * ```tsx
 * import * as React from "react";
 * import { DataTableCellSelect } from "@walmart-web/livingdesign-components";
 *
 * const MyComponent = () => {
 *   const [checked, setChecked] = React.useState(false)
 *
 *   return <DataTableCellSelect
 *     a11yLabelledBy="aria-label-reference"
 *     checked={checked}
 *     onChange={() => setChecked((previousState) => !previousState)}
 *   />
 * }
 *
 * ### A11y Label
 *
 * The Data Table Cell Select requires an accessible label that references a unique identifier for the containing row that is visible on the screen. A reference to the labelling element can be provided through the `a11yLabelledBy` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import { DataTableCellSelect } from "@walmart-web/livingdesign-components";
 *
 * const MyComponent = () => {
 *   const [checked, setChecked] = React.useState(false)
 *
 *   return (<DataTableRow>
 *     <DataTableCellSelect
 *       a11yLabelledBy="label-cell-id"
 *         checked={checked}
 *         onChange={() => setChecked((previousState) => !previousState)}
 *       />
 *     <DataTableCell id="label-cell-id">
 *       This is the Label
 *     </DataTableCell>
 *   </DataTableRow>)
 * }
 * ```
 */

const DataTableCellSelect = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, DataTableCellSelect),
        {
    a11yLabelledBy,
    checkboxProps = {},
    checked = false,
    className,
    disabled = false,
    name,
    onChange,
    value
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "checkboxProps", "checked", "className", "disabled", "name", "onChange", "value"]);

  invariant(!!a11yLabelledBy, "`DataTableCellSelect` accessibility violation. `DataTableCellSelect` requires an `a11yLabelledBy`.");
  return jsx("td", Object.assign({
    className: classNames(style$M.cellSelect, className)
  }, rest, {
    children: jsx(Checkbox, {
      a11yLabelledBy: a11yLabelledBy,
      checkboxProps: checkboxProps,
      checked: checked,
      disabled: disabled,
      name: name,
      onChange: onChange,
      value: value,
      ref: ref
    }, void 0)
  }), void 0);
});
DataTableCellSelect.displayName = "DataTableCellSelect";

/**
 * Data Table Head is the container for a row of Data Table Headers.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * Data Table Head accepts content (a Data Table Row) through the `children` prop.
 *
 * ```tsx
 * <DataTable>
 *   <DataTableHead>
 *     <DataTableRow>
 *       <DataTableHeader>ID</DataTableHeader>
 *       <DataTableHeader>Name</DataTableHeader>
 *       <DataTableHeader>Status</DataTableHeader>
 *     </DataTableRow>
 *   </DataTableHead>
 * </DataTable>
 * ```
 */

const DataTableHead = props => {
  const rest = applyCommonProps(props, DataTableHead);
  return jsx("thead", Object.assign({}, rest), void 0);
};

var style$L = {"tableHeader":"DataTableHeader-module_tableHeader__SwudZ","isSortable":"DataTableHeader-module_isSortable__AwjV0","button":"DataTableHeader-module_button__hHuvN","icon":"DataTableHeader-module_icon__WnGEr","sorted":"DataTableHeader-module_sorted__ZNysF","dataTableHeader":"DataTableHeader-module_dataTableHeader__JpXZj","left":"DataTableHeader-module_left__ZvMfR","right":"DataTableHeader-module_right__YnmK3"};

/**
 * Data Table Cell Header is a header cell in a Data Table Head
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * The Data Table Cell Header accepts content through the `children` prop.
 *
 * ```tsx
 * <DataTable>
 *   <DataTableHead>
 *     <DataTableRow>
 *       <DataTableHeader>ID</DataTableHeader>
 *       <DataTableHeader>Name</DataTableHeader>
 *       <DataTableHeader>Status</DataTableHeader>
 *     </DataTableRow>
 *   </DataTableHead>
 * </DataTable>
 *
 * ### Alignment
 *
 * The content of the Data Table Header can be aligned to the left (default) or right. Alignment is controlled through the `alignment` prop.
 *
 * ```tsx
 * <DataTableHeader alignment="right">ID</DataTableHeader>
 * ```
 *
 * ### Sorting
 *
 * The Data Table Header includes controls for sorting the Data Table.
 *
 * The Data Table Header accepts a callback with sorting logic through the `onSort` prop.
 *
 * The sort direction of a column can be one of three values: ascending, descending or none (default). The Data Table Header accepts sort direction through the `sort` prop.
 *
 * @see {@link https://livingdesign.walmart.com/resources/code-sandbox/?code=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 Data Table Code Recipe}
 *
 * ### Width
 *
 * The width of the Data Table Header can be controlled through the `width` prop.
 *
 * ```tsx
 * <DataTableHeader width="50px">ID</DataTableHeader>
 * ```
 */

const DataTableHeader = props => {
  const _a = applyCommonProps(props, DataTableHeader),
        {
    alignment = "left",
    children,
    className,
    onSort,
    sort = "none"
  } = _a,
        rest = __rest(_a, ["alignment", "children", "className", "onSort", "sort"]);

  const {
    announcePolite
  } = useA11yAnnouncement();
  const previousSort = React.useRef(sort);
  let icon;

  if (sort === "descending") {
    icon = jsx(ArrowDown, {
      size: componentDataTableHeaderIconIconSize,
      className: style$L.icon
    }, void 0);
  } else if (sort === "ascending" || onSort) {
    icon = jsx(ArrowUp, {
      size: componentDataTableHeaderIconIconSize,
      className: style$L.icon
    }, void 0);
  }

  React.useEffect(() => {
    if (previousSort.current !== sort) {
      previousSort.current = sort;
      announcePolite(`Sorted ${children} by ${sort}`);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */

  }, [children, sort]);
  return jsx("th", Object.assign({
    className: classNames(style$L.tableHeader, alignment === "left" && style$L.left, alignment === "right" && style$L.right, onSort && style$L.isSortable, className),
    scope: "col"
  }, onSort && {
    "aria-sort": sort
  }, rest, {
    children: onSort ? jsxs("button", Object.assign({
      className: classNames(style$L.dataTableHeader, style$L.button, (sort === "ascending" || sort === "descending") && style$L.sorted),
      onClick: onSort,
      type: "button"
    }, {
      children: [alignment === "right" && icon, jsx(Body, Object.assign({
        size: componentDataTableHeaderTextLabelAliasOptionsSize,
        weight: componentDataTableHeaderTextLabelAliasOptionsWeight
      }, {
        children: children
      }), void 0), alignment === "left" && icon]
    }), void 0) : jsx("span", Object.assign({
      className: style$L.dataTableHeader
    }, {
      children: jsx(Body, Object.assign({
        size: componentDataTableHeaderTextLabelAliasOptionsSize,
        weight: componentDataTableHeaderTextLabelAliasOptionsWeight
      }, {
        children: children
      }), void 0)
    }), void 0)
  }), void 0);
};

var style$K = {"headerSelect":"DataTableHeaderSelect-module_headerSelect__cH71C","checkbox":"DataTableHeaderSelect-module_checkbox__0Hqyi"};

/**
 * Data Table Header Select contains a checkbox to select/deselect Data Table Rows in bulk.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Checkbox
 *
 * The Data Table Header Select controls the state of the checkbox through the `checked` and `indeterminate` props.
 *
 * The Data Table Header Select requires a callback be provided through the `onChange` prop to handle interaction with the checkbox.
 *
 * The Data Table Header Select accepts additional control of checkbox state and configuration through the `disabled`, `name`, `value`, and `checkboxProps` props.
 *
 * ```tsx
 * import * as React from "react";
 * import { DataTableHeaderSelect } from "@walmart-web/livingdesign-components";
 *
 * const DATA = [{id: 1, color: "blue"}, {id: 2, color: "green"}, {id: 3, color: "purple"}]
 *
 * const MyComponent = () => {
 *   const [selected, setSelected] = React.useState([])
 *   const allSelected = selected.length === DATA.length;
 *   const indeterminate = selected.length > 0 && selected.length < DATA.length;
 *
 *   const handleChange = () => {
 *     const newSelectedState = allSelected || indeterminate ? [] : DATA.map((datum) => datum.id)
 *     setSelected(newSelectedState)
 *   }
 *
 *   return <DataTableHeaderSelect
 *     checked={allSelected}
 *     indeterminate={indeterminate}
 *     onChange={handleChange}
 *   />
 * }
 * ```
 *
 * ### A11y Label
 *
 * The Data Table Header Select accepts a custom accessibility label through the `a11yCheckboxLabel` prop.
 *
 * ```tsx
 * <DataTableHeaderSelect
 *   a11yCheckboxLabel="Select all rows"
 *   ...
 * />
 * ```
 */

const DataTableHeaderSelect = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, DataTableHeaderSelect),
        {
    a11yCheckboxLabel = "Toggle all rows",
    checkboxProps = {},
    checked = false,
    className,
    disabled = false,
    indeterminate = false,
    name,
    onChange,
    value
  } = _a,
        rest = __rest(_a, ["a11yCheckboxLabel", "checkboxProps", "checked", "className", "disabled", "indeterminate", "name", "onChange", "value"]);

  const id = useSSRSafeId();
  return (
    /*
     * The root component of DataTableHeaderSelect is a 'td' because the screen reader experience
     * on DataTable is more confusing when DataTableHeaderSelect is a 'th'.
     * For more details see https://jira.walmart.com/browse/LD-2633?focusedCommentId=20102819&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-20102819
     */
    jsxs("td", Object.assign({
      className: classNames(style$K.headerSelect, className)
    }, rest, {
      children: [jsx(Checkbox, {
        a11yLabelledBy: id,
        checkboxProps: checkboxProps,
        checked: checked,
        disabled: disabled,
        indeterminate: indeterminate,
        name: name,
        onChange: onChange,
        value: value,
        ref: ref,
        UNSAFE_className: style$K.checkbox
      }, void 0), jsx(VisuallyHidden, Object.assign({
        id: id
      }, {
        children: a11yCheckboxLabel
      }), void 0)]
    }), void 0)
  );
});
DataTableHeaderSelect.displayName = "DataTableHeaderSelect";

var styles$3 = {"dataTableRow":"DataTableRow-module_dataTableRow__4o7F9","selected":"DataTableRow-module_selected__OadVt"};

/**
 * Data Table Row contains a row of cells in a Data Table.
 *
 * - {@link https://livingdesign.walmart.com/components/data-table/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/data-table/react/ React documentation}
 *
 * ### Content
 *
 * The Data Table Row accepts content (Data Table Cells) through the `children` prop.
 *
 * ```tsx
 * <DataTableRow>
 *   <DataTableCell>Chuck</DataTableCell>
 *   <DataTableCell>Norris</DataTableCell>
 * </DataTableRow>
 * ```
 *
 * ### Selection
 *
 * The Data Table Row accepts the optional `selected` prop which indicates whether or not the row is selected.
 */

const DataTableRow = props => {
  const _a = applyCommonProps(props, DataTableRow),
        {
    className,
    selected = false
  } = _a,
        rest = __rest(_a, ["className", "selected"]);

  return jsx("tr", Object.assign({
    className: classNames(styles$3.dataTableRow, selected && styles$3.selected, className)
  }, rest), void 0);
};

var style$J = {"divider":"Divider-module_divider__A9t0y"};

/**
 * Dividers create visual breaks in content.
 *
 * - {@link https://livingdesign.walmart.com/components/divider/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/divider/react/ React documentation}
 *
 * ### Title
 *
 * The Divider accepts a title through the `title` prop to label the element for screen-readers.
 *
 * ```tsx
 * <Divider title="Descriptive Title" />;
 * ```
 */

const Divider = props => {
  const _a = applyCommonProps(props, Divider, true),
        {
    "aria-label": ariaLabel,
    className,
    title
  } = _a,
        rest = __rest(_a, ["aria-label", "className", "title"]); // Convert boolean strings to pure booleans. !bool can have unintended side-effects.
  // `ariaHidden` should default to `true` unless overridden.


  const ariaHidden = !(rest["aria-hidden"] === "false" || rest["aria-hidden"] === false);
  invariant(!!(ariaHidden || ariaLabel !== undefined || title !== undefined), "`Divider` accessibility violation. `Divider` requires an accessible label if `aria-hidden` is false.");
  return jsx("hr", Object.assign({
    "aria-hidden": ariaHidden,
    className: classNames(style$J.divider, className)
  }, title && {
    title
  }, rest), void 0);
};
Divider.displayName = "Divider";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentErrorMessageTextLabelAliasOptionsSize = "medium";
const componentErrorMessageTitleAliasOptionsSize = "large";
const componentErrorMessageTitleAliasOptionsWeight = 700;

var style$I = {"errorMessage":"ErrorMessage-module_errorMessage__OyfDm","media":"ErrorMessage-module_media__2waW0","title":"ErrorMessage-module_title__Uylbf","textLabel":"ErrorMessage-module_textLabel__M7suy","actions":"ErrorMessage-module_actions__0OC1f"};

/**
 * The Error Message is a familiar layout with descriptive content and suggested actions for error scenarios.
 *
 * - {@link https://livingdesign.walmart.com/components/error-message/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/error-message/react/ React documentation}
 *
 * ### Title
 *
 * The Error Message requires a title to be provided through the `title` prop.
 *
 * ### Text Label
 *
 * The Error Message requires a text label to be provided through the `children` prop.
 *
 * ```tsx
 * <ErrorMessage
 *   title="Service details aren’t available right now"
 * >
 *   Refresh this page to try again.
 * </ErrorMessage>
 * ```
 *
 * ### Action Content
 *
 * The Error Message accepts action content, such as a button, through the `actions` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import {
 *   Button,
 *   ErrorMessage
 * } from "@walmart-web/livingdesign-components";
 *
 * <ErrorMessage
 *   title="Service details aren’t available right now"
 *   actions={
 *     <Button size="medium" variant="primary" onClick={...}>
 *       Refresh page
 *     </Button>
 *   }
 * >
 *   Refresh this page to try again.
 * </ErrorMessage>
 * ```
 * ### Media
 *
 * The Error Message accepts media to display, such as an illustration, through the `media` prop.
 *
 * * ```tsx
 * <ErrorMessage
 *   title="Service details aren’t available right now"
 *   media={
 *     <img
 *       alt=""
 *       height="200"
 *       src="/SearchNotFoundCircle.svg"
 *       width="200"
 *     />
 *   }
 * >
 *   Refresh this page to try again.
 * </ErrorMessage>
 * ```
 */

const ErrorMessage = props => {
  const _a = applyCommonProps(props, ErrorMessage),
        {
    actions,
    children,
    media,
    title
  } = _a,
        rest = __rest(_a, ["actions", "children", "media", "title"]);

  return jsxs("div", Object.assign({
    className: style$I.errorMessage
  }, rest, {
    children: [media && jsx("div", Object.assign({
      className: style$I.media
    }, {
      children: media
    }), void 0), jsx(Heading, Object.assign({
      as: "div",
      size: componentErrorMessageTitleAliasOptionsSize,
      weight: componentErrorMessageTitleAliasOptionsWeight,
      UNSAFE_className: style$I.title
    }, {
      children: title
    }), void 0), jsx(Body, Object.assign({
      as: "div",
      size: componentErrorMessageTextLabelAliasOptionsSize,
      UNSAFE_className: style$I.textLabel
    }, {
      children: children
    }), void 0), actions && jsx("div", Object.assign({
      className: style$I.actions
    }, {
      children: actions
    }), void 0)]
  }), void 0);
};

var style$H = {"formGroup":"FormGroup-module_formGroup__VXsK2","formGroupLegend":"FormGroup-module_formGroupLegend__r-Xda","formGroupRow":"FormGroup-module_formGroupRow__bpN2w","label":"FormGroup-module_label__-y1no","helperTextContainer":"FormGroup-module_helperTextContainer__q2Nfi"};

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentFormHelperTextIconStateErrorIconSize = "small";
const componentFormLabelSizeLargeAliasOptionsSize = "small";
const componentFormLabelSizeLargeAliasOptionsWeight = 700;
const componentFormLabelSizeSmallAliasOptionsWeight = 700;

var style$G = {"helperText":"FormHelperText-module_helperText__Nddye","disabled":"FormHelperText-module_disabled__Nvb5h","hasError":"FormHelperText-module_hasError__5hgyd","icon":"FormHelperText-module_icon__4tTqk"};

/**
 * @private
 */

const FormHelperText = props => {
  const {
    UNSAFE_className,
    className,
    children,
    disabled = false,
    hasError = false
  } = props,
        rest = __rest(props, ["UNSAFE_className", "className", "children", "disabled", "hasError"]);

  return jsxs(Caption, Object.assign({
    UNSAFE_className: classNames(style$G.helperText, disabled && style$G.disabled, hasError && style$G.hasError, UNSAFE_className, className)
  }, rest, {
    children: [hasError && jsx(ExclamationCircleFill, {
      className: style$G.icon,
      size: componentFormHelperTextIconStateErrorIconSize
    }, void 0), children]
  }), void 0);
};

var style$F = {"label":"FormLabel-module_label__z5lTT","disabled":"FormLabel-module_disabled__iIV9r","text":"FormLabel-module_text__bib6h"};

/**
 * @private
 */

const FormLabel = props => {
  const {
    children,
    className,
    disabled = false,
    size = "large"
  } = props,
        rest = __rest(props, ["children", "className", "disabled", "size"]);

  return jsxs("label", Object.assign({
    className: classNames(style$F.label, disabled && style$F.disabled, className)
  }, rest, {
    children: [size === "small" && jsx(Caption, Object.assign({
      UNSAFE_className: style$F.text,
      weight: componentFormLabelSizeSmallAliasOptionsWeight
    }, {
      children: children
    }), void 0), size === "large" && jsx(Body, Object.assign({
      UNSAFE_className: style$F.text,
      size: componentFormLabelSizeLargeAliasOptionsSize,
      weight: componentFormLabelSizeLargeAliasOptionsWeight
    }, {
      children: children
    }), void 0)]
  }), void 0);
};

/**
 * A Form Group lets you create a list of grouped form elements. The form elements can be radio buttons, checkboxes, text area, etc.
 *
 * - {@link https://livingdesign.walmart.com/components/form-group/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/form-group/react/ React documentation}
 *
 * ### Form Content
 *
 * Content for the form is provided through the `children` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import {
 *   Checkbox,
 *   FormGroup
 * } from "@walmart-web/livingdesign-components";
 *
 * <FormGroup>
 *   <Checkbox label="Banana" />
 *   <Checkbox label="Apple" />
 *   <Checkbox label="Cookie" />
 * </FormGroup>
 * ```
 *
 * ### Label
 *
 * The Form Group accepts a text label through the `label` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import {
 *   Checkbox,
 *   FormGroup
 * } from "@walmart-web/livingdesign-components";
 *
 * <FormGroup
 *   label="What monster are you?"
 * >
 *   <Checkbox label="Banana" />
 *   <Checkbox label="Apple" />
 *   <Checkbox label="Cookie" />
 * </FormGroup>
 * ```
 *
 * ### Helper Text and Error
 *
 * The Form Group accepts helper text through the `helperText` prop, or an error message through the `error` prop. (NOTE: `error` overrides `helperText`.)
 *
 * * ```tsx
 * import * as React from "react";
 * import {
 *   Checkbox,
 *   FormGroup
 * } from "@walmart-web/livingdesign-components";
 *
 * <FormGroup
 *   error="Something is wrong"
 * >
 *   <Checkbox label="Banana" />
 *   <Checkbox label="Apple" />
 *   <Checkbox label="Cookie" />
 * </FormGroup>
 * ```
 */

const FormGroup = props => {
  const _a = applyCommonProps(props, FormGroup),
        {
    children,
    className,
    error,
    helperText,
    label
  } = _a,
        rest = __rest(_a, ["children", "className", "error", "helperText", "label"]);

  return jsxs("fieldset", Object.assign({
    className: classNames(style$H.formGroup, className)
  }, rest, {
    children: [(error || helperText || label) && jsxs("legend", Object.assign({
      className: style$H.formGroupLegend
    }, {
      children: [label && jsx("span", Object.assign({
        className: style$H.label
      }, {
        children: label
      }), void 0), (error || helperText) && jsx("div", Object.assign({
        className: style$H.helperTextContainer
      }, {
        children: jsx(FormHelperText, Object.assign({
          hasError: !!error
        }, {
          children: error || helperText
        }), void 0)
      }), void 0)]
    }), void 0), React.Children.map(children, child => jsx("div", Object.assign({
      className: style$H.formGroupRow
    }, {
      children: child
    }), void 0))]
  }), void 0);
};
FormGroup.displayName = "FormGroup";

var style$E = {"grid":"Grid-module_grid__UEm4S","gutter":"Grid-module_gutter__cPLBJ"};

/**
 * The Grid provides a framework for the rhythmic and consistent positioning of elements onscreen.
 *
 * The Grid contains 12 columns. There are no rows.
 *
 * - {@link https://livingdesign.walmart.com/components/grid/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/grid/react/ React documentation}
 *
 * ### Content
 *
 * The Grid accepts content (GridColumns) through the `children` prop.
 *
 * ```tsx
 * <Grid>
 *   <GridColumn>Column 1</GridColumn>
 *   <GridColumn>Column 2</GridColumn>
 *   <GridColumn>Column 3</GridColumn>
 * </Grid>
 * ```
 *
 * ### Gutters
 *
 * Spacing between columns in the Grid can be added through the `hasGutter` prop.
 *
 * ```
 * <Grid hasGutter>
 *   <GridColumn>Column 1</GridColumn>
 *   <GridColumn>Column 2</GridColumn>
 *   <GridColumn>Column 3</GridColumn>
 * </Grid>
 * ```
 */

const Grid = props => {
  const _a = applyCommonProps(props, Grid, true),
        {
    children,
    className,
    hasGutter = false
  } = _a,
        rest = __rest(_a, ["children", "className", "hasGutter"]);

  return jsx("div", Object.assign({
    className: classNames(style$E.grid, hasGutter && style$E.gutter, className)
  }, rest, {
    children: React.Children.map(children, child => React.isValidElement(child) ? React.cloneElement(child, {
      hasGutter
    }) : child)
  }), void 0);
};
Grid.displayName = "Grid";

var style$D = {"gridColumn":"GridColumn-module_gridColumn__kQA7O","small1":"GridColumn-module_small1__cSZKV","small2":"GridColumn-module_small2__F6Kde","small3":"GridColumn-module_small3__QEXya","small4":"GridColumn-module_small4__dWgs5","small5":"GridColumn-module_small5__MsMM-","small6":"GridColumn-module_small6__1ux4m","small7":"GridColumn-module_small7__tXLij","small8":"GridColumn-module_small8__C4Xkp","small9":"GridColumn-module_small9__o1H7O","small10":"GridColumn-module_small10__u0e-2","small11":"GridColumn-module_small11__wv60T","small12":"GridColumn-module_small12__Fm-Vl","medium1":"GridColumn-module_medium1__Vrqa-","medium2":"GridColumn-module_medium2__5mmJU","medium3":"GridColumn-module_medium3__QgQvE","medium4":"GridColumn-module_medium4__PQfaj","medium5":"GridColumn-module_medium5__9g-7D","medium6":"GridColumn-module_medium6__Li9FX","medium7":"GridColumn-module_medium7__ZMVdM","medium8":"GridColumn-module_medium8__dV8Ym","medium9":"GridColumn-module_medium9__FA6By","medium10":"GridColumn-module_medium10__MPxSK","medium11":"GridColumn-module_medium11__gxBlb","medium12":"GridColumn-module_medium12__k9ZU0","large1":"GridColumn-module_large1__i8yaB","large2":"GridColumn-module_large2__qsnMB","large3":"GridColumn-module_large3__4qhHU","large4":"GridColumn-module_large4__C6Z9E","large5":"GridColumn-module_large5__UYPiY","large6":"GridColumn-module_large6__BbDbH","large7":"GridColumn-module_large7__tX7mx","large8":"GridColumn-module_large8__LQ9Hi","large9":"GridColumn-module_large9__9094H","large10":"GridColumn-module_large10__lcm2l","large11":"GridColumn-module_large11__D0Zc4","large12":"GridColumn-module_large12__OMnll","xlarge1":"GridColumn-module_xlarge1__2DdIg","xlarge2":"GridColumn-module_xlarge2__FtJjV","xlarge3":"GridColumn-module_xlarge3__x-eYy","xlarge4":"GridColumn-module_xlarge4__F3t8u","xlarge5":"GridColumn-module_xlarge5__xPzBV","xlarge6":"GridColumn-module_xlarge6__Cr40s","xlarge7":"GridColumn-module_xlarge7__Ijbpf","xlarge8":"GridColumn-module_xlarge8__7J5GW","xlarge9":"GridColumn-module_xlarge9__R3K7B","xlarge10":"GridColumn-module_xlarge10__ACKvr","xlarge11":"GridColumn-module_xlarge11__umWfD","xlarge12":"GridColumn-module_xlarge12__CUjh5","xxlarge1":"GridColumn-module_xxlarge1__YPTHs","xxlarge2":"GridColumn-module_xxlarge2__DAFqS","xxlarge3":"GridColumn-module_xxlarge3__edh1P","xxlarge4":"GridColumn-module_xxlarge4__ehLIO","xxlarge5":"GridColumn-module_xxlarge5__rwWSJ","xxlarge6":"GridColumn-module_xxlarge6__zP6sw","xxlarge7":"GridColumn-module_xxlarge7__DGjfl","xxlarge8":"GridColumn-module_xxlarge8__PniG-","xxlarge9":"GridColumn-module_xxlarge9__idwdD","xxlarge10":"GridColumn-module_xxlarge10__DM7Nr","xxlarge11":"GridColumn-module_xxlarge11__GQN9b","xxlarge12":"GridColumn-module_xxlarge12__rlmfe","gutter":"GridColumn-module_gutter__Rx8Sy"};

/**
 * The Grid Column is a column within the Grid.
 *
 * - {@link https://livingdesign.walmart.com/components/grid/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/grid/react/ React documentation}
 *
 * ### Content
 *
 * The Grid Column accepts content through the `children` prop.
 *
 * ```tsx
 * <Grid>
 *   <GridColumn>Column 1</GridColumn>
 *   <GridColumn>Column 2</GridColumn>
 *   <GridColumn>Column 3</GridColumn>
 * </Grid>
 * ```
 *
 * ### Size
 *
 * Each column will automatically fill the available space specified. Utility properties (e.g. sm={12}, md={6}, etc.) specify how large each column should be at each breakpoint. The columns should add up to 12 for each device size.
 *
 * Utility properties: `sm`, `md`, `lg`, `xl`, `xxl`
 *
 * LivingDesign Breakpoints: https://livingdesign.walmart.com/foundations/layout/#breakpoints
 *
 * ```tsx
 * <Grid>
 *   <GridColumn sm={12} md={6} lg={4}>
 *     A
 *   </GridColumn>
 *   <GridColumn sm={12} md={6} lg={4}>
 *     B
 *   </GridColumn>
 *   <GridColumn sm={12} md={6} lg={4}>
 *     C
 *   </GridColumn>
 * </Grid>
 * ```
 */

const GridColumn = props => {
  const _a = applyCommonProps(props, GridColumn, true),
        {
    children,
    className,
    hasGutter = false,
    lg,
    md,
    sm = 12,
    xl,
    xxl
  } = _a,
        rest = __rest(_a, ["children", "className", "hasGutter", "lg", "md", "sm", "xl", "xxl"]);

  return jsx("div", Object.assign({
    className: classNames(style$D.gridColumn, hasGutter && style$D.gutter, style$D[`small${sm}`], md && style$D[`medium${md}`], lg && style$D[`large${lg}`], xl && style$D[`xlarge${xl}`], xxl && style$D[`xxlarge${xxl}`], className)
  }, rest, {
    children: children
  }), void 0);
};
GridColumn.displayName = "GridColumn";

var style$C = {"iconButton":"IconButton-module_iconButton__-Yoal","small":"IconButton-module_small__oTDOU","medium":"IconButton-module_medium__DPjOS","large":"IconButton-module_large__SFnP5"};

const isAnchor = props => "href" in props;
/**
 * The Icon Button triggers an action (similar to a button) and relies solely on an icon to convey its purpose.
 *
 * - {@link https://livingdesign.walmart.com/components/icon-button/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/icon-button/react/ React documentation}
 *
 * ### Content
 *
 * The Icon Button accepts an icon to display through the `children` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import { IconButton } from "@walmart-web/livingdesign-components";
 * import Icon from "@livingdesign/icons";
 *
 * <IconButton a11yLabel="icon">
 *   <Icon.Cart />
 * </IconButton>
 * ```
 *
 * ### A11y Label
 *
 * The Icon Button requires an accessible label be provided through the `a11yLabel` prop.
 *
 * ### Size
 *
 * The Icon Button comes in three sizes: small (default), medium and large. Set the Icon Button's size using the `size` prop.
 *
 * ```tsx
 * import * as React from "react";
 * import { IconButton } from "@walmart-web/livingdesign-components";
 * import Icon from "@livingdesign/icons";
 *
 * <IconButton a11yLabel="icon" size="large">
 *   <Icon.Cart />
 * </IconButton>
 * ```
 */


const IconButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, IconButton),
        {
    a11yLabel,
    children,
    className,
    size = "small"
  } = _a,
        rest = __rest(_a, ["a11yLabel", "children", "className", "size"]);

  invariant(!!a11yLabel, "`IconButton` accessibility violation. `IconButton` requires an `a11yLabel`.");
  const classes = classNames(style$C.iconButton, size === "large" && style$C.large, size === "medium" && style$C.medium, size === "small" && style$C.small, className);
  const content = React.isValidElement(children) ? React.cloneElement(children, {
    size
  }) : children;

  if (isAnchor(props)) {
    const _b = rest,
          {
      href,
      onClick,
      onKeyDown: anchorOnKeyDown,
      target
    } = _b,
          anchorProps = __rest(_b, ["href", "onClick", "onKeyDown", "target"]);

    const onKeyDown = onAnchorKeyDown({
      href,
      onClick,
      onKeyDown: anchorOnKeyDown
    });
    return jsx("a", Object.assign({
      "aria-label": a11yLabel,
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps, {
      children: content
    }), void 0);
  }

  return jsx("button", Object.assign({
    "aria-label": a11yLabel,
    className: classes,
    ref: ref,
    type: "button"
  }, rest, {
    children: content
  }), void 0);
});
IconButton.displayName = "IconButton";

var styles$2 = {"lineClamp":"LineClamp-module_lineClamp__-SsQL"};

/*
  We used to calculate this dynamically for whatever text style happened to be present.
  But since it's always Bogle, we've hardcoded it.
*/

const BOGLE_BASELINE_RATIO = 0.21739130434782605;

const getRelativeLineHeight = node => {
  const style = window.getComputedStyle(node);
  return parseFloat(style.lineHeight) / parseFloat(style.fontSize);
};
/**
 * A utility component that limits the number of visible lines of text.
 *
 * - {@link https://livingdesign.walmart.com/utilities/line-clamp/react/ React documentation}
 *
 * ### Content
 *
 * The Line Clamp accepts optional content through the `children` prop:
 *
 * ```tsx
 * <LineClamp>Content</LineClamp>
 * ```
 *
 * ### Lines
 *
 * The Line Clamp has an optional `lines` prop to limit the number of visible lines to display:
 *
 * ```tsx
 * <LineClamp lines={3}>
 *   Lorem ipsum dolor sit amet, consectetur adipiscing elit.
 * </LineClamp>
 * ```
 */


const LineClamp = props => {
  const _a = applyCommonProps(props, LineClamp, true),
        {
    className,
    lines = 2
  } = _a,
        rest = __rest(_a, ["className", "lines"]);

  const ref = React.createRef();
  const [descenderPadding, setDescenderPadding] = React.useState(0);
  React.useEffect(() => {
    /* istanbul ignore if: branch coverage fix */
    if (!ref.current) {
      /* istanbul ignore next */
      return;
    }

    const lineHeight = getRelativeLineHeight(ref.current);
    setDescenderPadding(Math.max(0, 1 - lineHeight + BOGLE_BASELINE_RATIO) / 2);
  }, [ref]);
  return jsx("span", Object.assign({
    className: classNames(className, styles$2.lineClamp),
    ref: ref,
    style: {
      WebkitLineClamp: lines,
      paddingBottom: `${descenderPadding}em`,
      marginBottom: `-${descenderPadding}em`
    }
  }, rest), void 0);
};
LineClamp.displayName = "LineClamp";

var style$B = {"link":"Link-module_link__ryNHW"};

/**
 * Links are navigational elements.
 *
 * - {@link https://livingdesign.walmart.com/components/link/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/link/react/ React documentation}
 *
 * ### Content
 *
 * The Link accepts content through the `children` prop:
 *
 * ```tsx
 * <Link>Content</Link>
 * ```
 *
 * ### Href
 *
 * The Link accepts an optional `href` prop:
 *
 * ```tsx
 * <Link href="https://www.walmart.com/plus">
 *   Walmart+
 * </Link>
 * ```
 *
 * ### On click
 *
 * The Link accepts an optional callback handler function using the `onClick` prop:
 *
 * ```tsx
 * <Link onClick={(event) => console.log(event)}>
 *   Content
 * </Link>
 * ```
 *
 * ### Target
 *
 * The Link accepts an optional `target` prop:
 *
 * ```tsx
 * <Link href="https://www.walmart.com/plus" target="_blank">
 *   Walmart+
 * </Link>
 * ```
 *
 */

const Link = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Link, true),
        {
    children,
    className,
    target
  } = _a,
        rest = __rest(_a, ["children", "className", "target"]);

  return jsx("a", Object.assign({
    className: classNames(style$B.link, className),
    ref: ref
  }, getTarget(target), rest, {
    children: children
  }), void 0);
});
Link.displayName = "Link";

var style$A = {"list":"List-module_list__-WKqY","divider":"List-module_divider__ij-DR"};

/**
 * Lists are continuous, vertical indexes of related information.
 *
 * - {@link https://livingdesign.walmart.com/components/list/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/list/react/ React documentation}
 *
 * ### Content
 *
 * The List accepts content as List Item components through the `children` prop:
 *
 * ```tsx
 * <List>
 *   <ListItem>Dog</ListItem>
 *   <ListItem>Cat</ListItem>
 *   <ListItem>Mouse</ListItem>
 * </List>
 * ```
 */

const List = props => {
  const _a = applyCommonProps(props, List),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  const childrenCount = React.Children.count(children);
  return jsx("ul", Object.assign({
    className: classNames(style$A.list, className)
  }, rest, {
    children: React.Children.map(children, (child, index) => jsxs("li", {
      children: [child, index < childrenCount - 1 && jsx("div", Object.assign({
        className: style$A.divider
      }, {
        children: jsx(Divider, {}, void 0)
      }), void 0)]
    }, void 0))
  }), void 0);
};

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentListItemTextLabelAliasOptionsSize = "medium";
const componentListItemTitleAliasOptionsSize = "large";
const componentListItemTitleAliasOptionsWeight = 700;

var style$z = {"listItem":"ListItem-module_listItem__B12Dw","container":"ListItem-module_container__iZD60","title":"ListItem-module_title__a3mqb","textLabel":"ListItem-module_textLabel__42JHF","leading":"ListItem-module_leading__GIr-Q","trailing":"ListItem-module_trailing__aU8z7"};

/**
 * List Items are the components that make up the children for Lists.
 *
 * - {@link https://livingdesign.walmart.com/components/list/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/list/react/ React documentation}
 *
 * ### Content
 *
 * The List Item accepts content through the `children` props:
 *
 * ```tsx
 * <ListItem>Content</ListItem>
 * ```
 *
 * ### Leading
 *
 * The List Item accepts optional leading content through the `leading` prop:
 *
 * ```tsx
 * <ListItem leading={<LeadingIcon />}>
 *   Content
 * </ListItem>
 * ```
 *
 * ### Title
 *
 * The List Item accepts an optional title through the `title` prop:
 *
 * ```tsx
 * <ListItem title="Title">
 *   Content
 * </ListItem>
 * ```
 *
 * ### Trailing
 *
 * The List Item accepts optional trailing content through the `trailing` prop:
 *
 * ```tsx
 * <ListItem trailing={<TrailingIcon />}>
 *   Content
 * </ListItem>
 * ```
 */

const ListItem = props => {
  const _a = applyCommonProps(props, ListItem),
        {
    children,
    className,
    leading,
    title,
    trailing
  } = _a,
        rest = __rest(_a, ["children", "className", "leading", "title", "trailing"]);

  return jsxs("span", Object.assign({
    className: classNames(style$z.listItem, className)
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$z.leading
    }, {
      children: leading
    }), void 0), jsxs("div", Object.assign({
      className: style$z.container
    }, {
      children: [title && jsx(Body, Object.assign({
        UNSAFE_className: style$z.title,
        size: componentListItemTitleAliasOptionsSize,
        weight: componentListItemTitleAliasOptionsWeight
      }, {
        children: title
      }), void 0), jsx(Body, Object.assign({
        UNSAFE_className: style$z.textLabel,
        size: componentListItemTextLabelAliasOptionsSize
      }, {
        children: children
      }), void 0)]
    }), void 0), trailing && jsx("span", Object.assign({
      className: style$z.trailing
    }, {
      children: trailing
    }), void 0)]
  }), void 0);
};

var style$y = {"snackbar":"Snackbar-module_snackbar__WqFiE","transitionEnter":"Snackbar-module_transitionEnter__7UL-X","transitionEnterActive":"Snackbar-module_transitionEnterActive__RT--T","transitionExit":"Snackbar-module_transitionExit__LEmm2","transitionExitActive":"Snackbar-module_transitionExitActive__NhA7B","snackContainer":"Snackbar-module_snackContainer__Y5G9Q","snack":"Snackbar-module_snack__XBu31"};

var style$x = {"snack":"Snack-module_snack__-h2XM","message":"Snack-module_message__9e-An"};

var style$w = {"action":"SnackActionButton-module_action__EAbGC"};

/**
 * @private
 */

const SnackActionButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, SnackActionButton, true),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("button", Object.assign({
    className: classNames(style$w.action, className),
    ref: ref,
    type: "button"
  }, rest), void 0);
});

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentSnackbarSnackCloseIconSize = "small";

var style$v = {"closeButton":"SnackCloseButton-module_closeButton__wrz2F","icon":"SnackCloseButton-module_icon__fs2lX"};

/**
 * @private
 */

const SnackCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, SnackCloseButton, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    className: classNames(style$v.closeButton, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      size: componentSnackbarSnackCloseIconSize,
      className: style$v.icon
    }, void 0)
  }), void 0);
});

/**
 * @private
 */

const Snack = props => {
  const _a = applyCommonProps(props, Snack),
        {
    actionButtonProps,
    className,
    message,
    onClose,
    closeButtonProps
  } = _a,
        rest = __rest(_a, ["actionButtonProps", "className", "message", "onClose", "closeButtonProps"]);

  const _b = actionButtonProps || {},
        {
    children: actionButtonLabel,
    onClick: actionButtonClick
  } = _b,
        actionButtonRest = __rest(_b, ["children", "onClick"]);

  const _c = closeButtonProps || {},
        {
    onClick: closeButtonClick
  } = _c,
        closeButtonRest = __rest(_c, ["onClick"]);

  return jsxs("div", Object.assign({
    className: classNames(style$x.snack, className)
  }, rest, {
    children: [jsx("div", Object.assign({
      className: style$x.message
    }, {
      children: message
    }), void 0), actionButtonProps && jsx(SnackActionButton, Object.assign({
      onClick: event => {
        actionButtonClick === null || actionButtonClick === void 0 ? void 0 : actionButtonClick(event);
        onClose(event);
      }
    }, actionButtonRest, {
      children: actionButtonLabel
    }), void 0), jsx(SnackCloseButton, Object.assign({
      "aria-label": "Close",
      onClick: event => {
        closeButtonClick === null || closeButtonClick === void 0 ? void 0 : closeButtonClick(event);
        onClose(event);
      }
    }, closeButtonRest), void 0)]
  }), void 0);
};

/**
 * This hook automatically initiates removal of the snackbar after a duration period. The period changes based on the length of the snack message.
 *
 * NOTE: This hook has been abstracted into a separate file to prevent
 * any `setTimeout` issues within glass and is not meant for reuse.
 * The implementation has been mocked within the glass `jestSetup`.
 *
 * @param snack
 * @param onClose
 */

const useRemoveSnackbar = (snack, onClose) => {
  React.useEffect(() => {
    /* istanbul ignore if */
    if (!snack) {
      return () => undefined;
    }

    const {
      message
    } = snack;
    const duration = message.length > 120 ? 3500 + (message.length - 120) * 60 : 3500;
    const timeoutID = setTimeout(() => {
      onClose();
    }, duration);
    return () => {
      clearTimeout(timeoutID);
    };
  }, [snack, onClose]);
};

/**
 * @private
 */

const Snackbar = props => {
  const {
    snack,
    setSnack
  } = props;

  const {
    actionButtonProps,
    closeButtonProps,
    onClose: snackOnClose
  } = snack,
        snackRest = __rest(snack, ["actionButtonProps", "closeButtonProps", "onClose"]);

  const snackbarRef = React.useRef(null);
  const [inProp, setInProp] = React.useState(false);

  const onClose = event => {
    setInProp(false);
    snackOnClose === null || snackOnClose === void 0 ? void 0 : snackOnClose(event);
  }; // ensure that snackbar do not propogate to overlay components. if stop propagation
  // is preventing clicks from being captured, a new event must be specified with
  // `useCapture` that is declared before this component is rendered.
  // @see {@link https://jira.walmart.com/browse/LD-1260}


  useOnClickInside(snackbarRef, event => event.stopPropagation(), true); // remove snackbar after X duration

  useRemoveSnackbar(snack, onClose); // new animation if snack is received

  React.useEffect(() => {
    setInProp(true);
  }, [snack]);
  return jsx(CSSTransition, Object.assign({
    classNames: {
      enter: style$y.transitionEnter,
      enterActive: style$y.transitionEnterActive,
      exit: style$y.transitionExit,
      exitActive: style$y.transitionExitActive
    },
    in: inProp,
    mountOnEnter: true,
    onExited: () => setSnack(undefined),
    timeout: {
      enter: 500,
      exit: 750
    }
  }, {
    children: jsx("div", Object.assign({
      className: style$y.snackbar,
      ref: snackbarRef
    }, {
      children: jsx("div", Object.assign({
        className: style$y.snackContainer
      }, {
        children: jsx(Snack, Object.assign({
          actionButtonProps: actionButtonProps,
          closeButtonProps: closeButtonProps,
          onClose: onClose,
          UNSAFE_className: style$y.snack
        }, snackRest), void 0)
      }), void 0)
    }), void 0)
  }), void 0);
};

const SnackbarContext = React.createContext({
  addSnack:
  /* istanbul ignore next */
  () => undefined
});
/**
 * Snackbars provide brief messages at the bottom of the screen regarding app processes. Snackbars contain brief text directly related to the operation performed. They may contain an action, but no icons.
 *
 * @see {@link https://livingdesign.walmart.com/components/snackbar/react/}
 */

const SnackbarProvider = props => {
  const {
    children
  } = props;
  const [snack, setSnack] = React.useState();
  const value = React.useRef({
    addSnack: setSnack
  });
  return jsxs(SnackbarContext.Provider, Object.assign({
    value: value.current
  }, {
    children: [children, jsx("div", Object.assign({
      "aria-live": "assertive"
    }, {
      children: snack && jsx(Snackbar, {
        snack: snack,
        setSnack: setSnack
      }, void 0)
    }), void 0)]
  }), void 0);
};
SnackbarProvider.displayName = "SnackbarProvider";

/**
 * Snackbars provide brief messages at the bottom of the screen regarding app processes. Snackbars contain brief text directly related to the operation performed. They may contain an action, but no icons.
 *
 * @see {@link https://livingdesign.walmart.com/components/snackbar/react/}
 */

const useSnackbar = () => React.useContext(SnackbarContext);

/**
 * A utility component that contains all the required React providers.
 *
 * - {@link https://livingdesign.walmart.com/utilities/living-design-provider/react/ React documentation}
 *
 * ### LivingDesignProvider
 *
 * Add the provider to your application's root. The provider must be in the DOM on page load to provide all the required React providers. It cannot be added dynamically.
 *
 * This provider adds [React Aria](https://react-spectrum.adobe.com/react-aria/ssr.html), [A11y Announcement](https://livingdesign.walmart.com/utilities/a11y-announcement/react/), and [Snackbar](https://livingdesign.walmart.com/components/snackbar/react/) to your application.
 *
 * ```tsx
 * import * as React from "react";
 * import * as ReactDOM from "react-dom";
 * import {LivingDesignProvider} from "@walmart-web/livingdesign-components";
 *
 * import {App} from "./App";
 *
 * ReactDOM.render(
 *   <LivingDesignProvider>
 *     <App />
 *   </LivingDesignProvider>,
 *   document.getElementById("app"),
 * );
 * ```
 */

const LivingDesignProvider = props => {
  const {
    children
  } = props;
  return jsx(SSRProvider, {
    children: jsx(A11yAnnouncementProvider, {
      children: jsx(SnackbarProvider, {
        children: children
      }, void 0)
    }, void 0)
  }, void 0);
};

var style$u = {"enter":"Menu-module_enter__EKrOR","bottomLeft":"Menu-module_bottomLeft__KnHza","bottomRight":"Menu-module_bottomRight__S0ulQ","topLeft":"Menu-module_topLeft__-gfRm","topRight":"Menu-module_topRight__-5l--","enterActive":"Menu-module_enterActive__xkOjC","exit":"Menu-module_exit__3tSNb","exitActive":"Menu-module_exitActive__UUuKV"};

var styles$1 = {"container":"MenuContainer-module_container__Fcu9J"};

/**
 * @private
 */

const MenuContainer = React.forwardRef((props, ref) => {
  const {
    className
  } = props,
        rest = __rest(props, ["className"]);

  return jsx("div", Object.assign({
    className: classNames(styles$1.container, className),
    ref: ref,
    role: "menu"
  }, rest), void 0);
});

var styles = {"bottomLeft":"MenuLayoutContainer-module_bottomLeft__c9wAg","bottomRight":"MenuLayoutContainer-module_bottomRight__UlNDS","topLeft":"MenuLayoutContainer-module_topLeft__tHXSf","topRight":"MenuLayoutContainer-module_topRight__sE-J8"};

/**
 * @private
 */

const MenuLayoutContainer = props => {
  const {
    className,
    onClose,
    position
  } = props,
        rest = __rest(props, ["className", "onClose", "position"]);

  const ref = React.useRef(null);
  useOnClickOutside([ref], onClose);
  return jsx("div", Object.assign({
    className: classNames(position === "bottomLeft" && styles.bottomLeft, position === "bottomRight" && styles.bottomRight, position === "topLeft" && styles.topLeft, position === "topRight" && styles.topRight, className),
    ref: ref
  }, rest), void 0);
};

const useMenuCalculatePosition = position => React.useCallback(({
  referrerHeight,
  referrerWidth,
  targetHeight,
  targetWidth
}) => ({
  left: position === "bottomRight" || position === "topRight" ? targetWidth - referrerWidth : 0,
  top: position === "topLeft" || position === "topRight" ? -1 * referrerHeight : targetHeight
}), [position]);

/**
 * A Menu is a container with items for user-triggered actions.
 *
 * - {@link https://livingdesign.walmart.com/components/menu/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/menu/react/ React documentation}
 *
 * The `Menu` must be configured as follows to work properly:
 *
 * - Must provide menu items to `children`
 * - Must provide a single, button-like element to `trigger`
 * - Must provide the trigger element's `ref` to `triggerRef`
 * - Must provide `isOpen` and manage its state with `onClose` and `onOpen` handlers
 *
 * ```tsx
 * import * as React from "react";
 * import * as Icon from "@livingdesign/icons";
 * import {
 *   IconButton,
 *   Menu,
 *   MenuItem
 * } from "@walmart-web/livingdesign-components";
 *
 * export default () => {
 *   const [isOpen, setIsOpen] = React.useState(false);
 *   const triggerRef = React.useRef<HTMLElement>();
 *
 *   return (
 *     <Menu
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *       onOpen={() => setIsOpen(true)}
 *       trigger={
 *         <IconButton a11yLabel="Options" ref={triggerRef}>
 *           <Icon.More />
 *         </IconButton>
 *       }
 *       triggerRef={triggerRef}
 *     >
 *       <MenuItem>One</MenuItem>
 *       <MenuItem>Two</MenuItem>
 *       <MenuItem>Three</MenuItem>
 *     </Menu>
 *   );
 * };
 * ```
 *
 * ### Menu item selection
 *
 * Pass an `onClick` handler to `MenuItem` to handle selection.
 *
 * ```tsx
 * <Menu
 *   // ...
 * >
 *   <MenuItem onClick={() => console.log("Menu item 'One' selected")}>
 *     One
 *   </MenuItem>
 * </Menu>
 * ```
 */

const Menu = props => {
  const _a = applyCommonProps(props, Menu),
        {
    children,
    className,
    isOpen = false,
    onClose,
    onKeyDown,
    onOpen,
    position = "bottomLeft",
    trigger,
    triggerRef
  } = _a,
        rest = __rest(_a, ["children", "className", "isOpen", "onClose", "onKeyDown", "onOpen", "position", "trigger", "triggerRef"]);

  const calculatePosition = useMenuCalculatePosition(position);
  const menuContainerRef = React.useRef(null);
  const menuId = useSSRSafeId();
  const triggerId = useSSRSafeId(trigger.props.id);
  const menuFocus = useMenuFocus(children, isOpen, menuContainerRef, triggerRef);
  return jsx(LayoutContainer, Object.assign({
    calculatePosition: calculatePosition,
    className: classNames(position === "bottomLeft" && style$u.bottomLeft, position === "bottomRight" && style$u.bottomRight, position === "topLeft" && style$u.topLeft, position === "topRight" && style$u.topRight),
    content: jsx(CSSTransition, Object.assign({
      classNames: {
        enter: style$u.enter,
        enterActive: style$u.enterActive,
        exit: style$u.exit,
        exitActive: style$u.exitActive
      },
      in: isOpen,
      mountOnEnter: true,
      timeout: 100,
      unmountOnExit: true
    }, {
      children: jsx(MenuLayoutContainer, Object.assign({
        onClose: onClose,
        position: position
      }, {
        children: jsx(MenuContainer, Object.assign({
          "aria-labelledby": triggerId,
          id: menuId,
          onKeyDown: event => {
            const {
              code
            } = event;

            if (code === "ArrowDown") {
              menuFocus.focusNextItem();
            } else if (code === "ArrowUp") {
              menuFocus.focusPreviousItem();
            } else if (code === "End") {
              menuFocus.focusLastItem();
            } else if (code === "Home") {
              menuFocus.focusFirstItem();
            }
          },
          ref: menuContainerRef
        }, {
          children: React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child)) {
              return child;
            }

            return React.cloneElement(child, {
              key: index,

              onClick(event) {
                var _a, _b;

                (_b = (_a = child.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                onClose(event);
              },

              tabIndex: index === menuFocus.focusedItemIndex ? undefined : -1
            });
          })
        }), void 0)
      }), void 0)
    }), void 0),
    onKeyDown: event => {
      onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);

      if (isOpen && event.code === "Escape") {
        onClose(event);
      }
    },
    trigger: React.cloneElement(trigger, {
      "aria-controls": menuId,
      "aria-expanded": isOpen,
      "aria-haspopup": true,
      id: triggerId,

      onClick(event) {
        var _a, _b;

        if (isOpen) {
          onClose(event);
          return;
        }

        (_b = (_a = trigger.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        onOpen(event);
        menuFocus.focusFirstItem();
      },

      onKeyDown(event) {
        var _a, _b;

        (_b = (_a = trigger.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        const {
          code
        } = event;

        if (code === "ArrowDown") {
          onOpen(event);
          menuFocus.focusFirstItem();
        } else if (code === "ArrowUp") {
          onOpen(event);
          menuFocus.focusLastItem();
        }
      }

    }),
    triggerRef: triggerRef
  }, rest), void 0);
};
Menu.displayName = "Menu";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentMenuItemLeadingIconIconSize = "small";

var style$t = {"menuItem":"MenuItem-module_menuItem__FeL-D","leadingIcon":"MenuItem-module_leadingIcon__kvfDB"};

/**
 * A Menu is a container with items for user-triggered actions.
 *
 * - {@link https://livingdesign.walmart.com/components/menu/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/menu/react/ React documentation}
 */

const MenuItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, MenuItem),
        {
    children,
    className,
    leadingIcon
  } = _a,
        rest = __rest(_a, ["children", "className", "leadingIcon"]);

  return jsxs("button", Object.assign({
    className: classNames(style$t.menuItem, className),
    ref: ref,
    role: "menuitem",
    type: "button"
  }, rest, {
    children: [leadingIcon && jsx("span", Object.assign({
      className: style$t.leadingIcon
    }, {
      children: React.isValidElement(leadingIcon) ? React.cloneElement(leadingIcon, {
        size: componentMenuItemLeadingIconIconSize
      }) : leadingIcon
    }), void 0), children]
  }), void 0);
});
MenuItem.displayName = "MenuItem";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentMetricLabelAliasOptionsSize = "small";
const componentMetricTimescopeAliasOptionsSize = "small";
const componentMetricTitleAliasOptionsSize = "medium";
const componentMetricTitleAliasOptionsWeight = 700;
const componentMetricUnitAliasOptionsSize = "small";
const componentMetricUnitAliasOptionsWeight = 400;
const componentMetricValueAliasOptionsSize = "small";
const componentMetricValueAliasOptionsWeight = 700;

var style$s = {"timescope":"Metric-module_timescope__2Algy","valueContainer":"Metric-module_valueContainer__R3o2Y","value":"Metric-module_value__8Q-Tg","unit":"Metric-module_unit__D-Wce","labelContainer":"Metric-module_labelContainer__hqQrl","neutral":"Metric-module_neutral__FVm0N","positiveUp":"Metric-module_positiveUp__xNkLl","positiveDown":"Metric-module_positiveDown__gOzd2","negativeUp":"Metric-module_negativeUp__iPvIr","negativeDown":"Metric-module_negativeDown__z9zQQ","trendIndicator":"Metric-module_trendIndicator__hlbbY","textLabel":"Metric-module_textLabel__DCbg-"};

/**
 * Metric displays the value of a significant data point.
 *
 * - {@link https://livingdesign.walmart.com/components/metric/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/metric/react/ React documentation}
 *
 * ### A11y trend indicator label
 *
 * The Metric accepts an optional accessible trend indicator label through the `allyTrendIndicatorLabel` prop:
 *
 * ```tsx
 * <Metric
 *   allyTrendIndicatorLabel="Trend Indicator Label"
 *   title="Title"
 *   value="Value"
 * />
 * ```
 *
 * ### Text label
 *
 * The Metric accepts an optional `textLabel` prop to provide a description of the metric:
 *
 * ```tsx
 * <Metric
 *   textLabel="Text Label"
 *   title="Title"
 *   value="Value"
 * />
 * ```
 *
 * ### Timescope
 *
 * Set the timescope for the Metric with the `timescope` prop:
 *
 * ```tsx
 * <Metric
 *   timescope="Timescope"
 *   title="Title"
 *   value="Value"
 * />
 * ```
 *
 * ### Title
 *
 * Set the title of the Metric with the `title` prop:
 *
 * ```tsx
 * <Metric
 *   title="Title"
 *   value="Value"
 * />
 * ```
 *
 * ### Unit
 *
 * Set the unit for the Metric with the `unit` prop:
 *
 * ```tsx
 * <Metric
 *   title="Title"
 *   unit="Unit"
 *   value="Value"
 * />
 * ```
 *
 * ### Value
 *
 * Set the value for the Metric with the `value` prop:
 *
 * ```tsx
 * <Metric
 *   title="Title"
 *   value="Value"
 * />
 * ```
 *
 * ### Variant
 *
 * The Metric comes with five variants:
 *
 * - `neutral`: Used to show data at a point in time without trend.
 * - `positiveUp`: Used to show an upward trend (an increase in value) that indicates a positive change.
 * - `positiveDown`: Used to show a downward trend (a decrease in value) that indicates a positive change.
 * - `negativeUp`: Used to show an upward trend (an increase in value) that indicates a negative change.
 * - `negativeDown`: Used to show a downward trend (a decrease in value) that indicates a negative change.
 *
 * Set the Metric's variant with the `variant` prop:
 *
 * ```tsx
 * <Metric
 *   title="Title"
 *   value="Value"
 *   variant="neutral"
 * >
 *   Neutral
 * </Metric>
 *
 * <Metric
 *   title="Title"
 *   value="Value"
 *   variant="positiveUp"
 * >
 *   PositiveUp
 * </Metric>
 *
 * <Metric
 *   title="Title"
 *   value="Value"
 *   variant="positiveDown"
 * >
 *   PositiveDown
 * </Metric>
 *
 * <Metric
 *   title="Title"
 *   value="Value"
 *   variant="negativeUp"
 * >
 *   NegativeUp
 * </Metric>
 *
 * <Metric
 *   title="Title"
 *   value="Value"
 *   variant="negativeDown"
 * >
 *   NegativeDown
 * </Metric>
 * ```
 */

const Metric = props => {
  const _a = applyCommonProps(props, Metric),
        {
    a11yTrendIndicatorLabel,
    className,
    textLabel,
    timescope,
    title,
    unit,
    value,
    variant = "neutral"
  } = _a,
        rest = __rest(_a, ["a11yTrendIndicatorLabel", "className", "textLabel", "timescope", "title", "unit", "value", "variant"]);

  return jsxs("div", Object.assign({
    className: classNames(variant === "negativeDown" && style$s.negativeDown, variant === "negativeUp" && style$s.negativeUp, variant === "neutral" && style$s.neutral, variant === "positiveDown" && style$s.positiveDown, variant === "positiveUp" && style$s.positiveUp, className)
  }, rest, {
    children: [jsx(Body, Object.assign({
      as: "div",
      size: componentMetricTitleAliasOptionsSize,
      weight: componentMetricTitleAliasOptionsWeight
    }, {
      children: title
    }), void 0), timescope && jsx(Body, Object.assign({
      as: "div",
      size: componentMetricTimescopeAliasOptionsSize,
      UNSAFE_className: style$s.timescope
    }, {
      children: timescope
    }), void 0), jsxs("div", Object.assign({
      className: style$s.valueContainer
    }, {
      children: [jsx(Display, Object.assign({
        as: "span",
        size: componentMetricValueAliasOptionsSize,
        weight: componentMetricValueAliasOptionsWeight,
        UNSAFE_className: style$s.value
      }, {
        children: value
      }), void 0), unit && jsx(Heading, Object.assign({
        as: "span",
        size: componentMetricUnitAliasOptionsSize,
        weight: componentMetricUnitAliasOptionsWeight,
        UNSAFE_className: style$s.unit
      }, {
        children: unit
      }), void 0)]
    }), void 0), textLabel && jsxs("div", Object.assign({
      className: style$s.labelContainer
    }, {
      children: [variant !== "neutral" && jsx("span", Object.assign({
        className: style$s.trendIndicator
      }, {
        children: variant === "negativeDown" || variant === "positiveDown" ? jsx(ArrowDown, {
          a11yLabel: a11yTrendIndicatorLabel !== null && a11yTrendIndicatorLabel !== void 0 ? a11yTrendIndicatorLabel : "Trending down"
        }, void 0) : jsx(ArrowUp, {
          a11yLabel: a11yTrendIndicatorLabel !== null && a11yTrendIndicatorLabel !== void 0 ? a11yTrendIndicatorLabel : "Trending up"
        }, void 0)
      }), void 0), jsx(Body, Object.assign({
        UNSAFE_className: style$s.textLabel,
        size: componentMetricLabelAliasOptionsSize
      }, {
        children: textLabel
      }), void 0)]
    }), void 0)]
  }), void 0);
};
Metric.displayName = "Metric";

var style$r = {"container":"ModalPortal-module_container__a5kaH","modal":"ModalPortal-module_modal__tFjLt","small":"ModalPortal-module_small__TE0H6","medium":"ModalPortal-module_medium__uWUA8","large":"ModalPortal-module_large__nzI-C","transitionEnter":"ModalPortal-module_transitionEnter__NC3-5","transitionEnterActive":"ModalPortal-module_transitionEnterActive__1NVYa","transitionExit":"ModalPortal-module_transitionExit__W2Cdp","transitionExitActive":"ModalPortal-module_transitionExitActive__ZrzjG","header":"ModalPortal-module_header__t6X0X","title":"ModalPortal-module_title__L-90q","content":"ModalPortal-module_content__3cmZA","contentInner":"ModalPortal-module_contentInner__mI6M8","actions":"ModalPortal-module_actions__P8yVN","scrim":"ModalPortal-module_scrim__jP8Yi"};

/**
 * @private
 */

const ModalPortal = props => {
  const _a = applyCommonProps(props, ModalPortal, true),
        {
    actions,
    children,
    closeButtonProps,
    isOpen,
    onClose,
    size = "small",
    title,
    onClosed
  } = _a,
        rest = __rest(_a, ["actions", "children", "closeButtonProps", "isOpen", "onClose", "size", "title", "onClosed"]);

  invariant(!!title, "Required property `title` not provided.");

  const _b = closeButtonProps || {},
        {
    onClick: closeButtonOnClick
  } = _b,
        closeButtonRest = __rest(_b, ["onClick"]);

  const modalRef = React.useRef(null);
  const [setScrollableContentElement] = useLockBodyScroll();
  const titleId = useSSRSafeId();
  useOnClickOutside(modalRef, onClose);
  useOnKeyDown(["Esc", "Escape"], onClose);
  return jsx(CSSTransition, Object.assign({
    classNames: {
      enter: style$r.transitionEnter,
      enterActive: style$r.transitionEnterActive,
      exit: style$r.transitionExit,
      exitActive: style$r.transitionExitActive
    },
    in: isOpen,
    mountOnEnter: true,
    onExited: () => onClosed === null || onClosed === void 0 ? void 0 : onClosed(),
    timeout: 500
  }, {
    children: jsxs(Overlay, Object.assign({}, rest, {
      children: [jsx("div", Object.assign({
        className: style$r.container
      }, {
        children: jsxs("div", Object.assign({
          "aria-labelledby": titleId,
          className: classNames(style$r.modal, size === "large" && style$r.large, size === "medium" && style$r.medium, size === "small" && style$r.small),
          ref: modalRef,
          role: "dialog"
        }, {
          children: [jsxs("div", Object.assign({
            className: style$r.header
          }, {
            children: [jsx(OverlayCloseButton, Object.assign({
              onClick: event => {
                closeButtonOnClick === null || closeButtonOnClick === void 0 ? void 0 : closeButtonOnClick(event);
                onClose(event);
              }
            }, closeButtonRest), void 0), jsx("h2", Object.assign({
              className: style$r.title,
              id: titleId
            }, {
              children: title
            }), void 0)]
          }), void 0), jsx("div", Object.assign({
            className: style$r.content,
            ref: setScrollableContentElement
          }, {
            children: jsx("div", Object.assign({
              className: style$r.contentInner
            }, {
              children: children
            }), void 0)
          }), void 0), actions && // NOTE: Safari needs a wrapping block element, otherwise a flex container
          // renders with no height.
          jsx("div", Object.assign({
            className: style$r.actions
          }, {
            children: actions
          }), void 0)]
        }), void 0)
      }), void 0), jsx(OverlayScrim, {
        className: style$r.scrim
      }, void 0)]
    }), void 0)
  }), void 0);
};

/**
 * Modals focus the user’s attention on a single task in a window that sits on top of the page content.
 *
 * - {@link https://livingdesign.walmart.com/components/modal/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/modal/react/ React documentation}
 *
 * ### Actions
 *
 * The Modal accepts actions through the `actions` prop:
 *
 * ```tsx
 * <Modal
 *   actions={
 *     <ButtonGroup>
 *       <Button>Action1</Button>
 *       <Button>Action2</Button>
 *     </ButtonGroup>
 *   }
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Content
 * </Modal>
 * ```
 *
 * ### Content
 *
 * The Modal accepts content through the `children` prop:
 *
 * ```tsx
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Content
 * </Modal>
 * ```
 *
 * ### Opening and closing
 *
 * The Modal is open based on the `isOpen` property. Add an `onClose` callback that configures `isOpen` state when the Modal requests to close.
 *
 * ```tsx
 * export default () => {
 *   const [isOpen, setIsOpen] = React.useState(false);
 *
 *   return (
 *     <>
 *       <Button onClick={() => setIsOpen(true)}>
 *         Open modal
 *       </Button>
 *
 *       <Modal
 *         isOpen={isOpen}
 *         onClose={() => setIsOpen(false)}
 *         title="Title"
 *       >
 *         Content
 *       </Modal>
 *     </>
 *   );
 * };
 * ```
 *
 * ### Size
 *
 * The Modal comes in three sizes. Set the size with the `size` prop:
 *
 * ```tsx
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   size="small"
 *   title="Title"
 * >
 *   Small
 * </Modal>
 *
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   size="medium"
 *   title="Title"
 * >
 *   Medium
 * </Modal>
 *
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   size="large"
 *   title="Title"
 * >
 *   Large
 * </Modal>
 * ```
 *
 * ### Title
 *
 * Set the title for the Modal with the `title` prop:
 *
 * ```tsx
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Modal
 * </Modal>
 * ```
 */

const Modal = props => {
  const {
    isOpen,
    onClosed
  } = props,
        rest = __rest(props, ["isOpen", "onClosed"]);

  const [isTransitioning, setIsTransitioning] = React.useState(isOpen);
  const previousIsOpen = React.useRef(isOpen);
  /**
   * This hook delays `isOpen` property changes such that the
   * `ModalPortal` component can transition in and out.
   */

  React.useEffect(() => {
    if (previousIsOpen.current === isOpen) {
      return;
    }

    previousIsOpen.current = isOpen;
    const handle = setTimeout(() => {
      setIsTransitioning(true);
    }, 0);
    /* eslint-disable-next-line consistent-return */

    return () => clearTimeout(handle);
  }, [isOpen]);

  if (!isOpen && !isTransitioning) {
    return null;
  }

  return jsx(ModalPortal, Object.assign({
    isOpen: isOpen && isTransitioning,
    onClosed: () => {
      setIsTransitioning(false);
      onClosed === null || onClosed === void 0 ? void 0 : onClosed();
    }
  }, rest), void 0);
};
Modal.displayName = "Modal";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentNudgeCloseButtonIconSize = "medium";
const componentNudgeContentAliasOptionsSize = "small";
const componentNudgeTitleAliasOptionsSize = "large";
const componentNudgeTitleAliasOptionsWeight = 700;

var style$q = {"nudge":"Nudge-module_nudge__REWa1","wrapperOuter":"Nudge-module_wrapperOuter__SeBSm","hasCloseButton":"Nudge-module_hasCloseButton__s3z3r","hasNoContent":"Nudge-module_hasNoContent__oiNt5","leading":"Nudge-module_leading__-rp4x","wrapperInner":"Nudge-module_wrapperInner__Y-alR","content":"Nudge-module_content__sOu7-","actions":"Nudge-module_actions__NUErJ"};

var style$p = {"closeButton":"NudgeCloseButton-module_closeButton__1TMXm"};

/**
 * @private
 */

const NudgeCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, NudgeCloseButton),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    className: classNames(style$p.closeButton, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      size: componentNudgeCloseButtonIconSize
    }, void 0)
  }), void 0);
});

/**
 * Nudges are used to remind, provide instructions or prompts for the next-step in a process, or as a mechanism to upsell or cross-sell. The information they provide should not be required for a user to complete a task.
 *
 * - {@link https://livingdesign.walmart.com/components/nudge/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/nudge/react/ React documentation}
 *
 * ### Actions
 *
 * The Nudge accepts actions through the `actions` prop:
 *
 * ```tsx
 * <Nudge
 *   actions={
 *     <ButtonGroup>
 *       <Button>Action1</Button>
 *       <Button>Action2</Button>
 *     </ButtonGroup>
 *   }
 *   title="Title"
 * >
 *   Content
 * </Nudge>
 * ```
 *
 * ### Content
 *
 * The Nudge accepts content through the `children` prop:
 *
 * ```tsx
 * <Nudge title="Title">Content</Nudge>
 * ```
 *
 * ### Leading
 *
 * The Nudge accepts optional leading content through the `leading` prop:
 *
 * ```tsx
 * <Nudge
 *   leading={<LeadingIcon />}
 *   title="Title"
 * >
 *   Content
 * </Nudge>
 * ```
 *
 * ### On close
 *
 * The Nudge has a close button. Add an `onClose` prop with a callback function to display the close button and handle user interaction:
 *
 * ```tsx
 * <Nudge
 *   onClose={(event) => console.log(event)}
 *   title="Title"
 * >
 *   Content
 * </Nudge>
 * ```
 *
 * ### Title
 *
 * Set the title for the Nudge with the `title` prop:
 *
 * ```tsx
 * <Nudge title="Title">Content</Nudge>
 * ```
 *
 */

const Nudge = props => {
  const _a = applyCommonProps(props, Nudge),
        {
    actions,
    closeButtonProps = {},
    children,
    className,
    leading,
    onClose,
    title
  } = _a,
        rest = __rest(_a, ["actions", "closeButtonProps", "children", "className", "leading", "onClose", "title"]);

  return jsxs("div", Object.assign({
    className: classNames(style$q.nudge, onClose && style$q.hasCloseButton, !actions && !children && style$q.hasNoContent, className)
  }, rest, {
    children: [jsxs("div", Object.assign({
      className: style$q.wrapperOuter
    }, {
      children: [leading && jsx("div", Object.assign({
        className: style$q.leading
      }, {
        children: leading
      }), void 0), jsxs("div", Object.assign({
        className: style$q.wrapperInner
      }, {
        children: [jsx(Body, Object.assign({
          size: componentNudgeTitleAliasOptionsSize,
          weight: componentNudgeTitleAliasOptionsWeight
        }, {
          children: title
        }), void 0), children && jsx(Body, Object.assign({
          UNSAFE_className: style$q.content,
          size: componentNudgeContentAliasOptionsSize
        }, {
          children: children
        }), void 0), actions && jsx("div", Object.assign({
          className: style$q.actions
        }, {
          children: actions
        }), void 0)]
      }), void 0)]
    }), void 0), onClose && jsx(NudgeCloseButton, Object.assign({
      "aria-label": "Close"
    }, closeButtonProps, {
      onClick: event => {
        var _a;

        (_a = closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(closeButtonProps, event);
        onClose(event);
      }
    }), void 0)]
  }), void 0);
};
Nudge.displayName = "Nudge";

var style$o = {"container":"PanelPortal-module_container__AD5v7","right":"PanelPortal-module_right__7dlnB","left":"PanelPortal-module_left__TWLp8","panel":"PanelPortal-module_panel__EdlAr","small":"PanelPortal-module_small__-DO-9","medium":"PanelPortal-module_medium__wv4uO","large":"PanelPortal-module_large__n-QEm","header":"PanelPortal-module_header__2wCaK","title":"PanelPortal-module_title__gjD0o","content":"PanelPortal-module_content__3qEJU","contentInner":"PanelPortal-module_contentInner__hNUU7","actions":"PanelPortal-module_actions__mkRH8"};

/**
 * @private
 */

const PanelPortal = props => {
  const _a = applyCommonProps(props, PanelPortal, true),
        {
    actions,
    children,
    onClose,
    closeButtonProps,
    position = "left",
    size = "small",
    title
  } = _a,
        rest = __rest(_a, ["actions", "children", "onClose", "closeButtonProps", "position", "size", "title"]);

  invariant(!!title, "Required property `title` not provided.");

  const _b = closeButtonProps || {},
        {
    onClick: closeButtonOnClick
  } = _b,
        closeButtonRest = __rest(_b, ["onClick"]);

  const panelRef = React.useRef(null);
  const titleId = useSSRSafeId(); // NOTE: On Safari iOS, body-scroll-lock locks scrolling on all elements
  // except for the passed in ref. Need to pass the element that can scroll.
  // https://github.com/reakit/reakit/issues/469#issuecomment-549013422

  const [setScrollableContentElement] = useLockBodyScroll();
  useOnClickOutside(panelRef, onClose);
  useOnKeyDown(["Esc", "Escape"], onClose);
  return jsxs(Overlay, Object.assign({}, rest, {
    children: [jsx("div", Object.assign({
      className: classNames(style$o.container, position === "left" && style$o.left, position === "right" && style$o.right)
    }, {
      children: jsxs("div", Object.assign({
        "aria-labelledby": titleId,
        className: classNames(style$o.panel, size === "small" && style$o.small, size === "medium" && style$o.medium, size === "large" && style$o.large),
        ref: panelRef,
        role: "dialog"
      }, {
        children: [jsxs("div", Object.assign({
          className: style$o.header
        }, {
          children: [jsx(OverlayCloseButton, Object.assign({
            onClick: event => {
              closeButtonOnClick === null || closeButtonOnClick === void 0 ? void 0 : closeButtonOnClick(event);
              onClose(event);
            }
          }, closeButtonRest), void 0), jsx("h2", Object.assign({
            className: style$o.title,
            id: titleId
          }, {
            children: title
          }), void 0)]
        }), void 0), jsx("div", Object.assign({
          className: style$o.content,
          ref: setScrollableContentElement
        }, {
          children: jsx("div", Object.assign({
            className: style$o.contentInner
          }, {
            children: children
          }), void 0)
        }), void 0), actions && jsx("div", Object.assign({
          className: style$o.actions
        }, {
          children: actions
        }), void 0)]
      }), void 0)
    }), void 0), jsx(OverlayScrim, {}, void 0)]
  }), void 0);
};

/**
 * A panel provides supplemental information or form inputs that relate to your primary canvas.
 *
 * - {@link https://livingdesign.walmart.com/components/panel/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/panel/react/ React documentation}
 *
 * ### Actions
 *
 * The Panel accepts actions through the `actions` prop:
 *
 * ```tsx
 * <Panel
 *   actions={
 *     <ButtonGroup>
 *       <Button>Action1</Button>
 *       <Button>Action2</Button>
 *     </ButtonGroup>
 *   }
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Content
 * </Panel>
 * ```
 *
 * ### Content
 *
 * The Panel accepts content through the `children` prop:
 *
 * ```tsx
 * <Panel
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Content
 * </Panel>
 * ```
 *
 * ### Opening and closing
 *
 * The Panel is open based on the `isOpen` property. Add an `onClose` callback that configures `isOpen` state when the Panel requests to close.
 *
 * ```tsx
 * export default () => {
 *   const [isOpen, setIsOpen] = React.useState(false);
 *
 *   return (
 *     <>
 *       <Button onClick={() => setIsOpen(true)}>
 *         Open panel
 *       </Button>
 *
 *       <Panel
 *         isOpen={isOpen}
 *         onClose={() => setIsOpen(false)}
 *         title="Title"
 *       >
 *         Content
 *       </Panel>
 *     </>
 *   );
 * };
 * ```
 *
 * ### Position
 *
 * The Panel has two different positions. Set the position through the `position` prop:
 *
 * ```tsx
 * <Panel
 *   onClose={() => setIsOpen(false)}
 *   position="left"
 *   title="Title"
 * >
 *   Left
 * </Panel>
 *
 * <Panel
 *   onClose={() => setIsOpen(false)}
 *   position="right"
 *   title="Title"
 * >
 *   Right
 * </Panel>
 * ```
 *
 * ### Size
 *
 * The Panel comes in three sizes. Set the size with the `size` prop:
 *
 * ```tsx
 * <Panel
 *   onClose={() => setIsOpen(false)}
 *   size="small"
 *   title="Title"
 * >
 *   Small
 * </Panel>
 *
 * <Panel
 *   onClose={() => setIsOpen(false)}
 *   size="medium"
 *   title="Title"
 * >
 *   Medium
 * </Panel>
 *
 * <Panel
 *   onClose={() => setIsOpen(false)}
 *   size="large"
 *   title="Title"
 * >
 *   Large
 * </Panel>
 * ```
 *
 * ### Title
 *
 * Set the title of the Panel with the `title` prop:
 *
 * ```tsx
 * <Panel
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Panel
 * </Panel>
 * ```
 */

const Panel = props => {
  const {
    isOpen = false
  } = props,
        rest = __rest(props, ["isOpen"]);

  if (!isOpen) {
    return null;
  }

  return jsx(PanelPortal, Object.assign({}, rest), void 0);
};
Panel.displayName = "Panel";

var style$n = {"popover":"Popover-module_popover__ClLJY"};

var style$m = {"layoutManager":"PopoverLayoutManager-module_layoutManager__86tf9","bottomCenter":"PopoverLayoutManager-module_bottomCenter__PY1Gb","hasNubbin":"PopoverLayoutManager-module_hasNubbin__lM1-D","bottomLeft":"PopoverLayoutManager-module_bottomLeft__5pzrr","bottomRight":"PopoverLayoutManager-module_bottomRight__IyKLD","left":"PopoverLayoutManager-module_left__gHHCM","right":"PopoverLayoutManager-module_right__br8AC","topCenter":"PopoverLayoutManager-module_topCenter__6mIO7","topLeft":"PopoverLayoutManager-module_topLeft__8Ksiz","topRight":"PopoverLayoutManager-module_topRight__YyRMi"};

var style$l = {"popover":"BasePopover-module_popover__kG50u","nubbin":"BasePopover-module_nubbin__7Uazp","bottomCenter":"BasePopover-module_bottomCenter__4XeZz","bottomLeft":"BasePopover-module_bottomLeft__T-L0C","bottomRight":"BasePopover-module_bottomRight__SqK4z","left":"BasePopover-module_left__1l3XX","right":"BasePopover-module_right__-h1OF","topCenter":"BasePopover-module_topCenter__BdQnm","topLeft":"BasePopover-module_topLeft__-G5RJ","topRight":"BasePopover-module_topRight__zc4aB","content":"BasePopover-module_content__CjkUt"};

/**
 * @private
 */

const BasePopover = props => {
  const {
    children,
    className,
    nubbin
  } = props,
        rest = __rest(props, ["children", "className", "nubbin"]);

  return jsx("div", Object.assign({
    className: classNames(style$l.popover, nubbin && style$l.nubbin, nubbin === "bottomCenter" && style$l.bottomCenter, nubbin === "bottomLeft" && style$l.bottomLeft, nubbin === "bottomRight" && style$l.bottomRight, nubbin === "left" && style$l.left, nubbin === "right" && style$l.right, nubbin === "topCenter" && style$l.topCenter, nubbin === "topLeft" && style$l.topLeft, nubbin === "topRight" && style$l.topRight, className)
  }, rest, {
    children: jsx("div", Object.assign({
      className: style$l.content
    }, {
      children: children
    }), void 0)
  }), void 0);
};

/**
 * @private
 */

const FocusGuard = props => {
  const {
    children,
    onGuard
  } = props,
        rest = __rest(props, ["children", "onGuard"]);

  const ref = React.useRef(null);
  React.useEffect(() => {
    var _a; // istanbul ignore next


    const focusableNodes = ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll( // @see {@link https://github.com/theKashey/focus-lock/blob/125a50bc53fdc2cbb7705327790ecff4f3289ba1/src/utils/tabbables.ts#L4-L24}
    "a[href], button:enabled, input:enabled:not([type=hidden]), select:enabled, textarea:enabled, [tabindex], [autofocus]")) || []; // NOTE: first and last node are focus guards, second node will be the first focusable item.

    setTimeout(() => focusableNodes[1].focus(), 0);
  }, []);
  return jsxs("div", Object.assign({
    ref: ref
  }, rest, {
    children: [jsx("div", {
      "aria-hidden": true,
      tabIndex: 0,
      onFocus: onGuard
    }, void 0), jsx("div", {
      "aria-hidden": true,
      tabIndex: -1
    }, void 0), children, jsx("div", {
      "aria-hidden": true,
      tabIndex: 0,
      onFocus: onGuard
    }, void 0)]
  }), void 0);
};

const getPopoverPositionStyle = ({
  popoverRef,
  position,
  triggerRef
}) => getPositionStyle({
  calculatePosition({
    referrerHeight,
    referrerWidth,
    targetHeight,
    targetWidth
  }) {
    let left;
    let top;

    if (position === "left") {
      left = -1 * referrerWidth;
    } else if (position === "right") {
      left = targetWidth;
    } else if (position === "bottomLeft" || position === "topLeft") {
      left = targetWidth / 2 - referrerWidth + 24;
    } else if (position === "bottomRight" || position === "topRight") {
      left = targetWidth / 2 - 24;
    } else {
      left = (targetWidth - referrerWidth) / 2;
    }

    if (position.startsWith("bottom")) {
      top = targetHeight;
    } else if (position.startsWith("top")) {
      top = -1 * referrerHeight;
    } else {
      top = (targetHeight - referrerHeight) / 2;
    }

    return {
      left,
      top
    };
  },

  referrerRef: popoverRef,
  targetRef: triggerRef
});

/**
 * @private
 */

const PopoverLayoutManager = props => {
  const {
    basePopoverProps = {},
    className,
    content,
    hasNubbin = false,
    onClose,
    position = "bottomCenter",
    triggerRef
  } = props,
        rest = __rest(props, ["basePopoverProps", "className", "content", "hasNubbin", "onClose", "position", "triggerRef"]);

  const [popoverStyle, setPopoverStyle] = React.useState({
    // NOTE: hide the popover until it has been mounted and positioned
    visibility: "hidden"
  });
  const surfaceRef = React.useRef(null);
  const onCloseWithFocusReturn = React.useCallback(event => {
    var _a;

    (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    onClose(event);
  }, [triggerRef, onClose]);
  useOnClickOutside([surfaceRef, triggerRef], onClose);
  useOnKeyDown(["Esc", "Escape"], onCloseWithFocusReturn);
  React.useEffect(() => {
    const positionStyle = getPopoverPositionStyle({
      popoverRef: surfaceRef,
      position,
      triggerRef
    });
    setPopoverStyle(positionStyle);
  }, [surfaceRef, position, triggerRef]);
  React.useEffect(() => {
    const onResize = () => {
      const positionStyle = getPopoverPositionStyle({
        popoverRef: surfaceRef,
        position,
        triggerRef
      });
      setPopoverStyle(positionStyle);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [surfaceRef, position, triggerRef]);
  const nubbin = {
    bottomCenter: "topCenter",
    bottomLeft: "topRight",
    bottomRight: "topLeft",
    left: "right",
    right: "left",
    topCenter: "bottomCenter",
    topLeft: "bottomRight",
    topRight: "bottomLeft"
  }[position];
  return jsx("div", Object.assign({
    className: classNames(style$m.layoutManager, position === "bottomCenter" && style$m.bottomCenter, position === "bottomLeft" && style$m.bottomLeft, position === "bottomRight" && style$m.bottomRight, position === "left" && style$m.left, position === "right" && style$m.right, position === "topCenter" && style$m.topCenter, position === "topLeft" && style$m.topLeft, position === "topRight" && style$m.topRight, hasNubbin && style$m.hasNubbin, className),
    ref: surfaceRef,
    style: popoverStyle
  }, rest, {
    children: jsx(FocusGuard, Object.assign({
      onGuard: onCloseWithFocusReturn
    }, {
      children: jsx(BasePopover, Object.assign({}, hasNubbin && {
        nubbin
      }, basePopoverProps, {
        children: content
      }), void 0)
    }), void 0)
  }), void 0);
};

/**
 * Popovers are overlays that display contextual content on demand.
 *
 * - {@link https://livingdesign.walmart.com/components/popover/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/popover/react/ React documentation}
 *
 *
 * The `Popover` component must be configured as follows to meet accessibly requirements:
 *
 * 1. Must provide a single button-like element as its `children`
 * 1. Must provide the button-like element's `ref` as its `triggerRef`
 * 1. Must provide at least one focusable element within its `content`
 *
 * ```tsx
 * import * as React from "react";
 * import {Button, Popover} from "@walmart-web/livingdesign-components";
 *
 * export const MyComponent = () => {
 *   const [state, setState] = React.useState(false);
 *   const ref = React.useRef();
 *
 *   return (
 *     <Popover
 *       content={
 *         <div>
 *           <Checkbox
 *             label="I must contain at least one focusable element."
 *             name="status"
 *             onChange={() => {}}
 *           />
 *         </div>
 *       }
 *       onClose={(event) => setState(false)}
 *       isOpen={state}
 *       triggerRef={ref}
 *     >
 *       <Button
 *         ref={ref}
 *         onClick={(event) => {
 *           setState((state) => !state);
 *         }}
 *       >
 *         Open Popover
 *       </Button>
 *     </Popover>
 *   );
 * };
 * ```
 *
 * ### Position
 *
 * The Popover has eight possible positions. The possible positions are `bottomCenter`, `bottomLeft`, `bottomRight`, `left`, `right`, `topCenter`, `topLeft`, or `topRight`. Set the position through the `position` prop:
 *
 * ```tsx
 * <Popover position="bottomCenter">
 *   <Trigger />
 * </Popover>
 * ```
 *
 * ### Has nubbin
 *
 * Give the Popover a nubbin through the `hasNubbin` prop:
 *
 * ```tsx
 * <Popover hasNubbin={true}>
 *   <Trigger />
 * </Popover>
 * ```
 */

const Popover = props => {
  const _a = applyCommonProps(props, Popover),
        {
    basePopoverProps,
    children,
    className,
    content,
    hasNubbin,
    isOpen = false,
    onClose,
    position,
    triggerRef
  } = _a,
        rest = __rest(_a, ["basePopoverProps", "children", "className", "content", "hasNubbin", "isOpen", "onClose", "position", "triggerRef"]);

  const child = React.Children.only(children);
  const surfaceId = useSSRSafeId();
  return jsxs("span", Object.assign({
    className: classNames(style$n.popover, className)
  }, rest, {
    children: [React.cloneElement(child, {
      "aria-controls": surfaceId,
      "aria-expanded": isOpen
    }), jsx("div", Object.assign({
      id: surfaceId
    }, {
      children: isOpen && jsx(PopoverLayoutManager, {
        basePopoverProps: basePopoverProps,
        content: content,
        hasNubbin: hasNubbin,
        onClose: onClose,
        position: position,
        triggerRef: triggerRef
      }, void 0)
    }), void 0)]
  }), void 0);
};
Popover.displayName = "Popover";

var style$k = {"progressIndicator":"ProgressIndicator-module_progressIndicator__dqRxb","track":"ProgressIndicator-module_track__nIXUZ","trackFill":"ProgressIndicator-module_trackFill__WBfaW","error":"ProgressIndicator-module_error__w1Uzo","info":"ProgressIndicator-module_info__hLeon","success":"ProgressIndicator-module_success__mSt3j","warning":"ProgressIndicator-module_warning__nNKfI","labelContainer":"ProgressIndicator-module_labelContainer__yE9yK","label":"ProgressIndicator-module_label__9YZBp","valueLabel":"ProgressIndicator-module_valueLabel__iPGdz"};

/**
 * Progress Indicators display a visual representation of the completion of a task or process.
 *
 * @see {@link https://livingdesign.walmart.com/components/progress-indicator/react/}
 */

const ProgressIndicator = props => {
  const _a = applyCommonProps(props, ProgressIndicator, false, true),
        {
    a11yLabelledBy,
    className,
    label,
    max = 100,
    min = 0,
    value = 0,
    valueLabel,
    variant = "info"
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "className", "label", "max", "min", "value", "valueLabel", "variant"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`ProgressIndicator` accessibility violation. `ProgressIndicator` requires a `label` OR an `a11yLabelledBy`.");
  const labelId = useSSRSafeId();
  const hasLabel = label || valueLabel;
  return jsxs("span", Object.assign({
    "aria-valuemax": max,
    "aria-valuemin": min,
    "aria-valuenow": value,
    className: classNames(style$k.progressIndicator, className),
    role: "progressbar"
  }, a11yLabelledBy && {
    "aria-labelledby": a11yLabelledBy
  }, label && {
    "aria-labelledby": labelId
  }, valueLabel && {
    "aria-valuetext": valueLabel
  }, rest, {
    children: [jsx("span", Object.assign({
      className: style$k.track
    }, {
      children: jsx("span", {
        className: classNames(style$k.trackFill, variant === "error" && style$k.error, variant === "info" && style$k.info, variant === "success" && style$k.success, variant === "warning" && style$k.warning),
        style: {
          width: `${(value - min) / (max - min) * 100}%`
        }
      }, void 0)
    }), void 0), hasLabel && jsxs("span", Object.assign({
      className: style$k.labelContainer
    }, {
      children: [jsx("span", Object.assign({
        className: style$k.label
      }, label && {
        id: labelId
      }, {
        children: label
      }), void 0), jsx("span", Object.assign({
        className: style$k.valueLabel
      }, {
        children: valueLabel
      }), void 0)]
    }), void 0)]
  }), void 0);
};
ProgressIndicator.displayName = "ProgressIndicator";

var style$j = {"progressTrackerItem":"ProgressTrackerItem-module_progressTrackerItem__LPmGB","hasLabel":"ProgressTrackerItem-module_hasLabel__45q6R","isCurrent":"ProgressTrackerItem-module_isCurrent__xVb8T","step":"ProgressTrackerItem-module_step__US4xY","stepInner":"ProgressTrackerItem-module_stepInner__RN7Tc","isActive":"ProgressTrackerItem-module_isActive__YEe2b","error":"ProgressTrackerItem-module_error__0u-a8","info":"ProgressTrackerItem-module_info__P0acn","success":"ProgressTrackerItem-module_success__UbsbY","warning":"ProgressTrackerItem-module_warning__6HJj5","stepOuter":"ProgressTrackerItem-module_stepOuter__lMrN2"};

var style$i = {"progressTracker":"ProgressTracker-module_progressTracker__JrPJ8","trackContainer":"ProgressTracker-module_trackContainer__4C0Cy","track":"ProgressTracker-module_track__pvoOH","indicator":"ProgressTracker-module_indicator__Su46K","error":"ProgressTracker-module_error__BcS-S","info":"ProgressTracker-module_info__aDA3D","success":"ProgressTracker-module_success__hjppX","warning":"ProgressTracker-module_warning__CphtR","items":"ProgressTracker-module_items__eQwyX"};

/**
 * Progress tracker is a visual representation of a users progress through a set of steps. They inform the user of the number of steps required to complete a specified process.
 *
 * - {@link https://livingdesign.walmart.com/components/progress-tracker/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/progress-tracker/react/ React documentation}
 *
 * ### Active index
 *
 * Set the active index for the Progress Tracker through the `activeIndex` prop:
 *
 * ```tsx
 * <ProgressTracker activeIndex={2}>
 *   <ProgressTrackerItem>Item</ProgressTrackerItem>
 * </ProgressTracker>
 * ```
 *
 * ### Content
 *
 * The Progress Tracker accepts content as Progress Tracker Item components through the `children` props:
 *
 * ```tsx
 * <ProgressTracker>
 *   <ProgressTrackerItem>One</ProgressTrackerItem>
 *   <ProgressTrackerItem>Two</ProgressTrackerItem>
 * </ProgressTracker>
 * ```
 *
 * ### Variant
 *
 * The Progress Tracker comes with four variants:
 *
 * - `error`: Conveys a negative message to the user.
 * - `info`: Provides general information in a highlighted way.
 * - `success`: Conveys a positive message to the user.
 * - `warning`: Draws the users attention to something that may affect their experience and maybe require their attention.
 *
 * Set the Progress Tracker's variant with the `variant` prop:
 *
 * ```tsx
 * <ProgressTracker variant="error">
 *   <ProgressTrackerItem>Error</ProgressTrackerItem>
 * </ProgressTracker>
 * <ProgressTracker variant="info">
 *   <ProgressTrackerItem>Info</ProgressTrackerItem>
 * </ProgressTracker>
 * <ProgressTracker variant="success">
 *   <ProgressTrackerItem>Success</ProgressTrackerItem>
 * </ProgressTracker>
 * <ProgressTracker variant="warning">
 *   <ProgressTrackerItem>Warning</ProgressTrackerItem>
 * </ProgressTracker>
 * ```
 */

const ProgressTracker = props => {
  const _a = applyCommonProps(props, ProgressTracker, true),
        {
    activeIndex = 0,
    children,
    className,
    variant = "info"
  } = _a,
        rest = __rest(_a, ["activeIndex", "children", "className", "variant"]);

  const lastIndex = React__default.Children.count(children) - 1;
  let clampedIndex;

  if (lastIndex >= 0 && activeIndex > lastIndex) {
    clampedIndex = lastIndex;
  } else if (activeIndex < 0) {
    clampedIndex = 0;
  } else {
    clampedIndex = activeIndex;
  }

  return jsxs("div", Object.assign({
    className: classNames(style$i.progressTracker, variant === "error" && style$i.error, variant === "info" && style$i.info, variant === "success" && style$i.success, variant === "warning" && style$i.warning, className)
  }, rest, {
    children: [jsx("div", Object.assign({
      className: style$i.trackContainer
    }, {
      children: jsx("span", Object.assign({
        className: style$i.track
      }, {
        children: jsx("span", {
          className: style$i.indicator,
          style: {
            width: `${100 * clampedIndex / lastIndex}%`
          }
        }, void 0)
      }), void 0)
    }), void 0), jsx("div", Object.assign({
      className: style$i.items,
      role: "list"
    }, {
      children: React__default.Children.map(children, (child, index) => {
        var _a;

        if (!React__default.isValidElement(child)) {
          return null;
        }

        return React__default.cloneElement(child, Object.assign(Object.assign({}, child.props), {
          a11yIndicatorLabel: (_a = child.props.a11yIndicatorLabel) !== null && _a !== void 0 ? _a : activeIndex > index ? "Complete," : activeIndex === index ? "Current," : "Not complete,",
          className: classNames(activeIndex >= index && style$j.isActive, activeIndex === index && style$j.isCurrent, variant === "error" && style$j.error, variant === "info" && style$j.info, variant === "success" && style$j.success, variant === "warning" && style$j.warning, child.props.className),
          key: index,
          role: "listitem",
          style: Object.assign(Object.assign({}, child.props.style), {
            width: `${index === 0 || index === lastIndex ? 50 / lastIndex : 100 / lastIndex}%`
          })
        }));
      })
    }), void 0)]
  }), void 0);
};
ProgressTracker.displayName = "ProgressTracker";

/**
 * Progress Tracker Items are the components that make up the children for Progress Trackers.
 *
 * - {@link https://livingdesign.walmart.com/components/progress-tracker/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/progress-tracker/react/ React documentation}
 *
 * ### A11y indicator label
 *
 * The Progress Tracker Item accepts an optional accessible indicator label through the `a11yIndicatorLabel` props:
 *
 * ```tsx
 * <ProgressTrackerItem a11yIndicatorLabel="Accessibility" />
 * ```
 *
 * ### Content
 *
 * The Progress Tracker Item accepts optional content through the `children` prop:
 *
 * ```tsx
 * <ProgressTrackerItem>Item</ProgressTrackerItem>
 * ```
 *
 */

const ProgressTrackerItem = props => {
  const _a = applyCommonProps(props, ProgressTrackerItem, true),
        {
    a11yIndicatorLabel,
    children,
    className
  } = _a,
        rest = __rest(_a, ["a11yIndicatorLabel", "children", "className"]);

  return jsxs("span", Object.assign({
    className: classNames(style$j.progressTrackerItem, children && style$j.hasLabel, className)
  }, rest, {
    children: [jsx(VisuallyHidden, {
      children: a11yIndicatorLabel
    }, void 0), jsxs("svg", Object.assign({
      "aria-hidden": true,
      className: style$j.step,
      viewBox: "0 0 16 16",
      xmlns: "http://www.w3.org/2000/svg"
    }, {
      children: [jsx("circle", {
        className: style$j.stepOuter,
        cx: "8",
        cy: "8",
        r: "7"
      }, void 0), jsx("circle", {
        className: style$j.stepInner,
        cx: "8",
        cy: "8",
        r: "4"
      }, void 0)]
    }), void 0), children]
  }), void 0);
};
ProgressTrackerItem.displayName = "ProgressTrackerItem";

var style$h = {"label":"Radio-module_label__-1gEH","checked":"Radio-module_checked__D-6cx","disabled":"Radio-module_disabled__OekAP","input":"Radio-module_input__MgVS0","radioInput":"Radio-module_radioInput__YUUZ-","radioInputOuter":"Radio-module_radioInputOuter__53TlA","radioInputInner":"Radio-module_radioInputInner__dG4rA","labelText":"Radio-module_labelText__RDlSx"};

/**
 * Radio buttons represent a group of mutually exclusive choices, compared to checkboxes that allow users to make one or more selections from a group. In use cases where only one selection of a group is allowed, use the radio button component instead of the checkbox.
 *
 * @see {@link https://livingdesign.walmart.com/components/radio/react/}
 */

const Radio = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Radio, true),
        {
    a11yLabelledBy,
    checked = false,
    className,
    disabled = false,
    id: initialId,
    label,
    name,
    onChange,
    radioProps = {},
    value
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "checked", "className", "disabled", "id", "label", "name", "onChange", "radioProps", "value"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`Radio` accessibility violation. `Radio` requires a `label` OR an `a11yLabelledBy`.");

  const {
    className: radioClassName
  } = radioProps,
        radioRest = __rest(radioProps, ["className"]);

  const id = useSSRSafeId(initialId);
  return jsxs("label", Object.assign({
    className: classNames(style$h.label, checked && style$h.checked, disabled && style$h.disabled, className),
    htmlFor: id
  }, rest, {
    children: [jsx("input", Object.assign({
      checked: checked,
      className: classNames(style$h.input, radioClassName),
      disabled: disabled,
      id: id,
      name: name,
      onChange: onChange,
      ref: ref,
      type: "radio"
    }, a11yLabelledBy && {
      "aria-labelledby": a11yLabelledBy
    }, value && {
      value
    }, radioRest), void 0), jsxs("svg", Object.assign({
      "aria-hidden": true,
      className: style$h.radioInput,
      viewBox: "0 0 20 20",
      xmlns: "http://www.w3.org/2000/svg"
    }, {
      children: [jsx("circle", {
        className: style$h.radioInputOuter,
        cx: "10",
        cy: "10"
      }, void 0), jsx("circle", {
        className: style$h.radioInputInner,
        cx: "10",
        cy: "10"
      }, void 0)]
    }), void 0), label && jsx("span", Object.assign({
      className: style$h.labelText
    }, {
      children: label
    }), void 0)]
  }), void 0);
});
Radio.displayName = "Radio";

var style$g = {"large":"RatingStar-module_large__WU8Ih","small":"RatingStar-module_small__yR9SL","star":"RatingStar-module_star__CyuOr","empty":"RatingStar-module_empty__8QVyi","filled":"RatingStar-module_filled__jwe63","halfFilled":"RatingStar-module_halfFilled__JK5DU","fill":"RatingStar-module_fill__HjXCm"};

/**
 * @private
 */

const RatingStar = props => {
  const {
    className,
    size = "small",
    variant = "empty"
  } = props,
        rest = __rest(props, ["className", "size", "variant"]);

  return jsx("svg", Object.assign({
    className: classNames(style$g.star, size === "large" && style$g.large, size === "small" && style$g.small, variant === "empty" && style$g.empty, variant === "filled" && style$g.filled, variant === "halfFilled" && style$g.halfFilled, className),
    viewBox: "0 0 22 22",
    xmlns: "http://www.w3.org/2000/svg"
  }, rest, {
    children: variant === "halfFilled" ? jsxs(Fragment, {
      children: [jsx("path", {
        d: "M11 2.174a.825.825 0 0 0-.74.478L8.375 7.424l-5.662.628a.816.816 0 0 0-.463.247c-.322.34-.32.89.006 1.227l3.494 4.023-1.174 6.039a.905.905 0 0 0 .066.562c.2.43.697.61 1.108.399l5.25-3.5V2.174Z",
        className: style$g.fill
      }, void 0), jsx("path", {
        className: style$g.empty,
        d: "M11 2.002v15.5M13.895 7.082l.17.354.387.062 6.226.999c.011.001.03.008.047.034.018.026.03.063.023.106a.133.133 0 0 1-.034.073l-4.476 4.64-.274.284.08.387 1.32 6.467a.15.15 0 0 1-.017.109c-.017.027-.036.036-.048.039a.04.04 0 0 1-.015 0 .05.05 0 0 1-.017-.005L11.342 17.6 11 17.424l-.342.175-5.925 3.032a.046.046 0 0 1-.02.006c-.005 0-.01 0-.018-.003-.013-.005-.034-.017-.05-.05a.155.155 0 0 1-.01-.096l1.322-6.467.079-.387-.274-.284-4.477-4.64a.136.136 0 0 1-.035-.096c0-.041.015-.074.035-.095a.067.067 0 0 1 .037-.022l6.226-.999.387-.062.17-.354 2.831-5.892c.016-.033.036-.045.05-.05a.045.045 0 0 1 .017-.003c.004 0 .01.002.02.007a.1.1 0 0 1 .041.046l2.83 5.892Z"
      }, void 0)]
    }, void 0) : jsx("path", {
      d: "m13.562 7.028.17.354.387.062 6.226.999c.011.002.029.008.047.034.018.026.03.063.023.106a.133.133 0 0 1-.034.073l-4.476 4.64-.274.284.079.387 1.321 6.467c.009.042 0 .08-.017.109-.018.028-.036.036-.049.04a.04.04 0 0 1-.014 0 .05.05 0 0 1-.017-.006l-5.926-3.032-.341-.175-.342.175-5.926 3.032a.046.046 0 0 1-.02.006c-.004 0-.01 0-.017-.003-.013-.004-.034-.017-.05-.05a.155.155 0 0 1-.01-.096l1.322-6.467.079-.387-.274-.284-4.477-4.64a.136.136 0 0 1-.035-.095c0-.042.014-.075.034-.096a.067.067 0 0 1 .037-.022l6.226-.999.388-.062.17-.354 2.83-5.892c.016-.033.037-.045.05-.05a.045.045 0 0 1 .018-.003c.004 0 .01.002.02.007a.1.1 0 0 1 .04.046l2.832 5.892Z"
    }, void 0)
  }), void 0);
};

var style$f = {"container":"Rating-module_container__Nxtol","large":"Rating-module_large__g3dCk","small":"Rating-module_small__2HFt6"};

/**
 * Ratings provide insight into how well a product or service has been received by those who have bought or used it previously.
 *
 * @see {@link https://livingdesign.walmart.com/components/rating/react/}
 */

const Rating = props => {
  const _a = applyCommonProps(props, Rating),
        {
    className,
    size = "small",
    value = 0
  } = _a,
        rest = __rest(_a, ["className", "size", "value"]);

  return jsx("div", Object.assign({
    className: classNames(style$f.container, size === "large" && style$f.large, size === "small" && style$f.small, className)
  }, rest, {
    children: [1, 2, 3, 4, 5].map(count => {
      if (value >= count - 0.25) {
        return jsx(RatingStar, {
          size: size,
          variant: "filled"
        }, count);
      } else if (value >= count - 0.75) {
        return jsx(RatingStar, {
          size: size,
          variant: "halfFilled"
        }, count);
      }

      return jsx(RatingStar, {
        size: size
      }, count);
    })
  }), void 0);
};
Rating.displayName = "Rating";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentSelectIconIconSize = "medium";
const componentSelectLeadingIconIconSize = "medium";

var style$e = {"root":"Select-module_root__MRudU","label":"Select-module_label__wjIDe","container":"Select-module_container__HpsS6","disabled":"Select-module_disabled__yuRIB","error":"Select-module_error__6o8Xm","value":"Select-module_value__h-9kJ","large":"Select-module_large__jPrJp","hasLeadingIcon":"Select-module_hasLeadingIcon__cVd0h","small":"Select-module_small__Rpw43","icon":"Select-module_icon__AJseD","leadingIcon":"Select-module_leadingIcon__qy3Kv","trailingIcon":"Select-module_trailingIcon__ZvxoS","helperText":"Select-module_helperText__ZOwGL"};

/**
 * Select gives users the ability to make a single selection from a number of predefined options. It is usually found in forms.
 *
 * @see {@link https://livingdesign.walmart.com/components/select/react/}
 */

const Select = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Select),
        {
    children,
    className,
    disabled = false,
    error,
    helperText,
    id: initialId,
    label,
    leadingIcon,
    onChange,
    selectProps,
    size = "large",
    value
  } = _a,
        rest = __rest(_a, ["children", "className", "disabled", "error", "helperText", "id", "label", "leadingIcon", "onChange", "selectProps", "size", "value"]);

  const id = useSSRSafeId(initialId);
  const helperId = useSSRSafeId();
  const helperContent = error || helperText;
  return jsxs("div", Object.assign({
    className: classNames(style$e.root, disabled && style$e.disabled, error && style$e.error, leadingIcon && style$e.hasLeadingIcon, size === "large" && style$e.large, size === "small" && style$e.small, className)
  }, rest, {
    children: [jsx(FormLabel, Object.assign({
      className: style$e.label,
      disabled: disabled,
      htmlFor: id,
      size: size
    }, {
      children: label
    }), void 0), jsxs("div", Object.assign({
      className: style$e.container
    }, {
      children: [leadingIcon && jsx("span", Object.assign({
        "aria-hidden": true,
        className: classNames(style$e.icon, style$e.leadingIcon)
      }, {
        children: React.isValidElement(leadingIcon) ? React.cloneElement(leadingIcon, {
          size: componentSelectLeadingIconIconSize
        }) : leadingIcon
      }), void 0), jsx("select", Object.assign({
        "aria-describedby": helperContent ? helperId : undefined,
        disabled: disabled,
        id: id,
        onChange: onChange,
        ref: ref,
        value: value
      }, selectProps, {
        className: classNames(style$e.value, selectProps === null || selectProps === void 0 ? void 0 : selectProps.className)
      }, {
        children: children
      }), void 0), jsx(CaretDown, {
        className: classNames(style$e.icon, style$e.trailingIcon),
        size: componentSelectIconIconSize
      }, void 0)]
    }), void 0), !!helperContent && jsx(FormHelperText, Object.assign({
      className: style$e.helperText,
      disabled: disabled,
      hasError: !!error,
      id: helperId
    }, {
      children: helperContent
    }), void 0)]
  }), void 0);
});
Select.displayName = "Select";

var style$d = {"sideNavigation":"SideNavigation-module_sideNavigation__9Y9Nu","list":"SideNavigation-module_list__vfLB2"};

/**
 * Side navigation provides a list of primary links that navigate users to a URL. Links should be grouped together in sections based on related content or categories. A section title can proceed a list of links.
 *
 * @see {@link https://livingdesign.walmart.com/components/side-navigation/react/}
 */

const SideNavigation = props => {
  const _a = applyCommonProps(props, SideNavigation, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("nav", Object.assign({
    className: classNames(style$d.sideNavigation, className)
  }, rest, {
    children: jsx("ul", Object.assign({
      className: style$d.list
    }, {
      children: React.Children.map(children, (child, index) => jsx("li", {
        children: child
      }, index))
    }), void 0)
  }), void 0);
};
SideNavigation.displayName = "SideNavigation";

var style$c = {"sideNavigationItem":"SideNavigationItem-module_sideNavigationItem__3wBrn","isCurrent":"SideNavigationItem-module_isCurrent__Y5sVr","leading":"SideNavigationItem-module_leading__Q5zQ0"};

/**
 * @see {@link https://livingdesign.walmart.com/components/side-navigation/react/}
 */

const SideNavigationItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, SideNavigationItem, true),
        {
    className,
    children,
    leading,
    isCurrent = false,
    target
  } = _a,
        rest = __rest(_a, ["className", "children", "leading", "isCurrent", "target"]);

  return jsxs("a", Object.assign({
    className: classNames(style$c.sideNavigationItem, isCurrent && style$c.isCurrent, className),
    ref: ref
  }, isCurrent && {
    "aria-current": "page"
  }, getTarget(target), rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$c.leading
    }, {
      children: leading
    }), void 0), children]
  }), void 0);
});
SideNavigationItem.displayName = "SideNavigationItem";

var style$b = {"skeleton":"Skeleton-module_skeleton__c1rBI","rectangle":"Skeleton-module_rectangle__JSyI0","rounded":"Skeleton-module_rounded__hEIH6"};

/**
 * A skeleton is a low-fidelity shape that approximates a user interface element and indicates loading of content. A group of skeletons roughly matching a loaded screen can improve perceived responsiveness when loading data is slow.
 *
 * @see {@link https://livingdesign.walmart.com/components/skeleton/react/}
 */

const Skeleton = props => {
  const _a = applyCommonProps(props, Skeleton, true),
        {
    className,
    height,
    style: styleProp,
    variant = "rectangle",
    width
  } = _a,
        rest = __rest(_a, ["className", "height", "style", "variant", "width"]);

  return jsx("div", Object.assign({
    className: classNames(style$b.skeleton, variant === "rectangle" && style$b.rectangle, variant === "rounded" && style$b.rounded, className),
    style: Object.assign(Object.assign({}, styleProp), {
      height,
      width
    })
  }, rest), void 0);
};
Skeleton.displayName = "Skeleton";

var style$a = {"skeletonText":"SkeletonText-module_skeletonText__ctHXO","skeleton":"SkeletonText-module_skeleton__ytKK7"};

/**
 * @see {@link https://livingdesign.walmart.com/components/skeleton/react/#skeleton-text}
 */

const SkeletonText = props => {
  const _a = applyCommonProps(props, SkeletonText, true),
        {
    className,
    lines = 3
  } = _a,
        rest = __rest(_a, ["className", "lines"]);

  return jsx("div", Object.assign({
    className: classNames(style$a.skeletonText, className)
  }, rest, {
    children: Array.from(new Array(lines)).map((_, index) => jsx(Skeleton, {
      UNSAFE_className: style$a.skeleton
    }, index))
  }), void 0);
};
SkeletonText.displayName = "SkeletonText";

var style$9 = {"container":"Spinner-module_container__qaa66","gray":"Spinner-module_gray__6-dV1","white":"Spinner-module_white__q13wX","large":"Spinner-module_large__zUWo7","small":"Spinner-module_small__e9TMc","pill":"Spinner-module_pill__CQMvt","pill2":"Spinner-module_pill2__wDy8-","pill3":"Spinner-module_pill3__HVSzz","pill4":"Spinner-module_pill4__OuMER","pill5":"Spinner-module_pill5__CUqvm","pill6":"Spinner-module_pill6__kRoni"};

/**
 * Spinners visually indicate that a process is taking place for an indeterminate amount of time.
 *
 * @see {@link https://livingdesign.walmart.com/components/spinner/react/}
 */

const Spinner = props => {
  const _a = applyCommonProps(props, Spinner, true),
        {
    a11yLabel = "Loading…",
    className,
    color = "gray",
    size = "large",
    spinnerProps = {}
  } = _a,
        rest = __rest(_a, ["a11yLabel", "className", "color", "size", "spinnerProps"]);

  const uniqueTitleId = useSSRSafeId();
  return jsx("span", Object.assign({
    className: classNames(style$9.container, size === "large" && style$9.large, size === "small" && style$9.small, className),
    role: "status"
  }, rest, {
    children: jsxs("svg", Object.assign({
      "aria-labelledby": uniqueTitleId,
      className: classNames(color === "gray" && style$9.gray, color === "white" && style$9.white),
      role: "img",
      viewBox: "0 0 48 48",
      xmlns: "http://www.w3.org/2000/svg"
    }, spinnerProps, {
      children: [jsx("title", Object.assign({
        id: uniqueTitleId
      }, {
        children: a11yLabel
      }), void 0), jsx("defs", {
        children: jsx("path", {
          d: "m22.1 15.967-.97-10.988C21.13 3.892 22.4 3 23.998 3c1.601 0 2.873.892 2.873 1.979L25.9 15.967c-.11.662-.918 1.173-1.902 1.173-.98 0-1.789-.511-1.897-1.173z",
          id: "ld-spinner-pill"
        }, void 0)
      }, void 0), jsx("use", {
        className: style$9.pill,
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill2),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill3),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill4),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill5),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill6),
        href: "#ld-spinner-pill"
      }, void 0)]
    }), void 0)
  }), void 0);
};
Spinner.displayName = "Spinner";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentSpotIconContainerSizeSmallIconSize = "medium";
const componentSpotIconContainerSizeLargeIconSize = "large";

var style$8 = {"spotIcon":"SpotIcon-module_spotIcon__gCyVU","blue":"SpotIcon-module_blue__xtL-C","white":"SpotIcon-module_white__dTvpY","large":"SpotIcon-module_large__4jPht","small":"SpotIcon-module_small__XO6g2"};

const spotIconIconSize = {
  large: componentSpotIconContainerSizeLargeIconSize,
  small: componentSpotIconContainerSizeSmallIconSize
};
const SpotIcon = props => {
  const _a = applyCommonProps(props, SpotIcon),
        {
    children,
    className,
    color = "blue",
    size = "small"
  } = _a,
        rest = __rest(_a, ["children", "className", "color", "size"]);

  const child = React.Children.only(children);
  return jsx("div", Object.assign({
    className: classNames(style$8.spotIcon, color === "blue" && style$8.blue, color === "white" && style$8.white, size === "small" && style$8.small, size === "large" && style$8.large, className)
  }, rest, {
    children: React.cloneElement(child, {
      size: spotIconIconSize[size]
    })
  }), void 0);
};

var style$7 = {"styledText":"StyledText-module_styledText__zw2us","blue":"StyledText-module_blue__tdLvf","gray":"StyledText-module_gray__EowCP","green":"StyledText-module_green__lbXLg","leading":"StyledText-module_leading__s2NEP","small":"StyledText-module_small__pTFGg","large":"StyledText-module_large__3AEjR"};

/**
 * Styled text visually enhances copy and can help convey meaning by adding color and/or an optional icon.
 *
 * @see {@link https://livingdesign.walmart.com/components/styled-text/react/}
 */

const StyledText = props => {
  const _a = applyCommonProps(props, StyledText, true),
        {
    children,
    className,
    color = "gray",
    leading,
    size = "small"
  } = _a,
        rest = __rest(_a, ["children", "className", "color", "leading", "size"]);

  return jsxs("span", Object.assign({
    className: classNames(style$7.styledText, className, color === "blue" && style$7.blue, color === "gray" && style$7.gray, color === "green" && style$7.green, size === "large" && style$7.large, size === "small" && style$7.small)
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$7.leading
    }, {
      children: leading
    }), void 0), children]
  }), void 0);
};
StyledText.displayName = "StyledText";

var style$6 = {"pill":"Switch-module_pill__A76MA","indicator":"Switch-module_indicator__ISGIc","on":"Switch-module_on__A0IIP","label":"Switch-module_label__IZrgs"};

/**
 * Switches are generally used to activate or deactivate (on/off) a specific setting.
 *
 * @see {@link https://livingdesign.walmart.com/components/switch/react/}
 */

const Switch = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Switch),
        {
    a11yLabelledBy,
    className,
    isOn = false,
    disabled = false,
    label,
    onClick
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "className", "isOn", "disabled", "label", "onClick"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`Switch` accessibility violation. `Switch` requires a `label` OR an `a11yLabelledBy`.");
  return jsxs("button", Object.assign({
    "aria-checked": isOn,
    className: classNames(style$6.pill, isOn && style$6.on, className),
    disabled: disabled,
    onClick: onClick,
    ref: ref,
    role: "switch",
    type: "button"
  }, a11yLabelledBy && {
    "aria-labelledby": a11yLabelledBy
  }, rest, {
    children: [jsx("span", {
      className: classNames(style$6.indicator, isOn && style$6.on)
    }, void 0), label && jsx("span", Object.assign({
      className: classNames(style$6.label, isOn && style$6.on)
    }, {
      children: label
    }), void 0)]
  }), void 0);
});
Switch.displayName = "Switch";

var style$5 = {"tabNavigation":"TabNavigation-module_tabNavigation__yIB28","list":"TabNavigation-module_list__YSw1o","tabNavigationItemContainer":"TabNavigation-module_tabNavigationItemContainer__vEfWC"};

/**
 * Tab Navigation allows for page-level navigation between sets of content, with a selected state, typically used at the top of the screen.
 *
 * @see {@link https://livingdesign.walmart.com/components/tab-navigation/react/}
 */

const TabNavigation = props => {
  const _a = applyCommonProps(props, TabNavigation),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("nav", Object.assign({
    className: classNames(style$5.tabNavigation, className)
  }, rest, {
    children: jsx("ul", Object.assign({
      className: style$5.list
    }, {
      children: React.Children.map(children, (child, index) => jsx("li", Object.assign({
        className: style$5.tabNavigationItemContainer
      }, {
        children: child
      }), index))
    }), void 0)
  }), void 0);
};
TabNavigation.displayName = "TabNavigation";

var style$4 = {"tabNavigationItem":"TabNavigationItem-module_tabNavigationItem__ve6Rm","isCurrent":"TabNavigationItem-module_isCurrent__qLbhx","label":"TabNavigationItem-module_label__ovp4O","leadingIcon":"TabNavigationItem-module_leadingIcon__12A6i","trailing":"TabNavigationItem-module_trailing__OV52k"};

/**
 * @see {@link https://livingdesign.walmart.com/components/tab-navigation/react/}
 */

const TabNavigationItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, TabNavigationItem, true),
        {
    className,
    children,
    isCurrent = false,
    leadingIcon: initialLeadingIcon,
    target,
    trailing
  } = _a,
        rest = __rest(_a, ["className", "children", "isCurrent", "leadingIcon", "target", "trailing"]);

  const leadingIcon = React.isValidElement(initialLeadingIcon) ? React.cloneElement(initialLeadingIcon, {
    size: "small"
  }) : initialLeadingIcon;
  return jsxs("a", Object.assign({
    className: classNames(style$4.tabNavigationItem, isCurrent && style$4.isCurrent, className),
    ref: ref
  }, isCurrent && {
    "aria-current": "page"
  }, getTarget(target), rest, {
    children: [leadingIcon && jsx("span", Object.assign({
      className: style$4.leadingIcon
    }, {
      children: leadingIcon
    }), void 0), jsx("span", Object.assign({
      className: style$4.label
    }, {
      children: children
    }), void 0), trailing && jsx("span", Object.assign({
      className: style$4.trailing
    }, {
      children: trailing
    }), void 0)]
  }), void 0);
});
TabNavigationItem.displayName = "TabNavigationItem";

var style$3 = {"tag":"Tag-module_tag__mfgBD","leading":"Tag-module_leading__WYgPQ","primary":"Tag-module_primary__K1mU5","red":"Tag-module_red__zFnaw","spark":"Tag-module_spark__yULuF","green":"Tag-module_green__gF6Ns","blue":"Tag-module_blue__8eJnG","purple":"Tag-module_purple__FZvNO","gray":"Tag-module_gray__VBaCb","secondary":"Tag-module_secondary__HdyGZ","tertiary":"Tag-module_tertiary__upWXH"};

/**
 * Tags are used to draw a customer’s focus to item traits such as availability, status, or media rating. Items may be products, fulfillment slots, or something else. They are visual only and non-interactive.
 *
 * @see {@link https://livingdesign.walmart.com/components/tag/react/}
 */

const Tag = props => {
  const _a = applyCommonProps(props, Tag, true),
        {
    children,
    className,
    color = "blue",
    leading,
    variant = "secondary"
  } = _a,
        rest = __rest(_a, ["children", "className", "color", "leading", "variant"]);

  return jsxs("span", Object.assign({
    className: classNames(style$3.tag, color === "red" && style$3.red, color === "spark" && style$3.spark, color === "green" && style$3.green, color === "blue" && style$3.blue, color === "purple" && style$3.purple, color === "gray" && style$3.gray, variant === "primary" && style$3.primary, variant === "secondary" && style$3.secondary, variant === "tertiary" && style$3.tertiary, className)
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$3.leading
    }, {
      children: leading
    }), void 0), children]
  }), void 0);
};
Tag.displayName = "Tag";

var style$2 = {"root":"TextArea-module_root__0NGLO","label":"TextArea-module_label__21yyA","container":"TextArea-module_container__8Vcbe","disabled":"TextArea-module_disabled__lUOJR","readOnly":"TextArea-module_readOnly__jjKkG","error":"TextArea-module_error__s3sif","value":"TextArea-module_value__OuYIU","large":"TextArea-module_large__7j2ur","small":"TextArea-module_small__08oJF","helperTextContainer":"TextArea-module_helperTextContainer__DOs4E","maxLength":"TextArea-module_maxLength__UR5bj"};

/**
 * Text areas allow for the input of freeform, often multi-line, text.
 *
 * @see {@link https://livingdesign.walmart.com/components/text-area/react/}
 */

const TextArea = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, TextArea),
        {
    className,
    disabled = false,
    error,
    helperText,
    id: initialId,
    label,
    maxLength,
    onChange,
    placeholder,
    readOnly = false,
    size = "large",
    textAreaProps,
    value = ""
  } = _a,
        rest = __rest(_a, ["className", "disabled", "error", "helperText", "id", "label", "maxLength", "onChange", "placeholder", "readOnly", "size", "textAreaProps", "value"]);

  const id = useSSRSafeId(initialId);
  const helperId = useSSRSafeId();
  const [screenReaderMessage, setScreenReaderMessage] = React.useState("");
  /* eslint-disable-next-line react-hooks/exhaustive-deps */

  const setDelayedScreenReaderMessage = React.useMemo(() => debounce(setScreenReaderMessage, 1500), []);
  React.useEffect(() => {
    if (!maxLength) {
      return () => undefined;
    }

    const message = `${maxLength - value.length} characters left.`;
    setDelayedScreenReaderMessage(message);
    return () => {
      setDelayedScreenReaderMessage.cancel();
    };
  }, [maxLength, setDelayedScreenReaderMessage, value]);
  const hasHelperContent = !!(error || helperText || maxLength);
  return jsxs("div", Object.assign({
    className: classNames(style$2.root, disabled && style$2.disabled, error && style$2.error, readOnly && style$2.readOnly, size === "large" && style$2.large, size === "small" && style$2.small, className)
  }, rest, {
    children: [jsx(FormLabel, Object.assign({
      className: style$2.label,
      disabled: disabled,
      htmlFor: id,
      size: size
    }, {
      children: label
    }), void 0), jsx("div", Object.assign({
      className: style$2.container
    }, {
      children: jsx("textarea", Object.assign({
        "aria-describedby": hasHelperContent ? helperId : undefined,
        disabled: disabled,
        id: id,
        maxLength: maxLength,
        onChange: onChange,
        placeholder: placeholder,
        readOnly: readOnly,
        ref: ref,
        value: value
      }, textAreaProps, {
        className: classNames(style$2.value, textAreaProps === null || textAreaProps === void 0 ? void 0 : textAreaProps.className)
      }), void 0)
    }), void 0), jsxs("div", Object.assign({
      id: helperId
    }, {
      children: [hasHelperContent && jsxs("div", Object.assign({
        className: style$2.helperTextContainer
      }, {
        children: [jsx(FormHelperText, Object.assign({
          disabled: disabled,
          hasError: !!error
        }, {
          children: error || helperText
        }), void 0), maxLength && jsxs("span", Object.assign({
          "aria-hidden": true,
          className: style$2.maxLength
        }, {
          children: [value.length, " / ", maxLength]
        }), void 0)]
      }), void 0), jsx(VisuallyHidden, Object.assign({
        "aria-atomic": true,
        "aria-live": "polite"
      }, {
        children: screenReaderMessage
      }), void 0)]
    }), void 0)]
  }), void 0);
});
TextArea.displayName = "TextArea";

/**
 * Do not edit directly
 * Generated on Fri, 30 Sep 2022 17:14:26 GMT
 */
const componentTextFieldLeadingIconIconSize = "medium";

var style$1 = {"root":"TextField-module_root__a9bmc","label":"TextField-module_label__llDqH","container":"TextField-module_container__1fzJk","disabled":"TextField-module_disabled__fPPJL","readOnly":"TextField-module_readOnly__C3Q3B","error":"TextField-module_error__YXWVF","value":"TextField-module_value__vKTmm","large":"TextField-module_large__7KChS","hasLeadingIcon":"TextField-module_hasLeadingIcon__IqKhR","small":"TextField-module_small__UqWi8","icon":"TextField-module_icon__S-SHv","leadingIcon":"TextField-module_leadingIcon__1WNgq","trailing":"TextField-module_trailing__G8oq3","helperText":"TextField-module_helperText__Cx0XG"};

const autoComplete = {
  email: "email",
  password: "new-password",
  tel: "tel"
};
/**
 * Text fields allow users to enter and edit text.
 *
 * @see {@link https://livingdesign.walmart.com/components/text-field/react/}
 */

const TextField = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, TextField),
        {
    className,
    disabled = false,
    error,
    helperText,
    id: initialId,
    label,
    leadingIcon,
    onChange,
    placeholder,
    readOnly = false,
    size = "large",
    textFieldProps,
    trailing,
    type = "text",
    value = ""
  } = _a,
        rest = __rest(_a, ["className", "disabled", "error", "helperText", "id", "label", "leadingIcon", "onChange", "placeholder", "readOnly", "size", "textFieldProps", "trailing", "type", "value"]);

  const id = useSSRSafeId(initialId);
  const helperId = useSSRSafeId();
  const helperContent = error || helperText;
  return jsxs("div", Object.assign({
    className: classNames(style$1.root, disabled && style$1.disabled, error && style$1.error, leadingIcon && style$1.hasLeadingIcon, readOnly && style$1.readOnly, size === "large" && style$1.large, size === "small" && style$1.small, className)
  }, rest, {
    children: [jsx(FormLabel, Object.assign({
      className: style$1.label,
      disabled: disabled,
      htmlFor: id,
      size: size
    }, {
      children: label
    }), void 0), jsxs("div", Object.assign({
      className: style$1.container
    }, {
      children: [leadingIcon && jsx("span", Object.assign({
        "aria-hidden": true,
        className: classNames(style$1.icon, style$1.leadingIcon)
      }, {
        children: React.isValidElement(leadingIcon) ? React.cloneElement(leadingIcon, {
          size: componentTextFieldLeadingIconIconSize
        }) : leadingIcon
      }), void 0), jsx("input", Object.assign({
        "aria-describedby": helperContent ? helperId : undefined,
        autoComplete: type && autoComplete[type],
        disabled: disabled,
        id: id,
        onChange: onChange,
        placeholder: placeholder,
        readOnly: readOnly,
        ref: ref,
        type: type,
        value: value
      }, textFieldProps, {
        className: classNames(style$1.value, textFieldProps === null || textFieldProps === void 0 ? void 0 : textFieldProps.className)
      }), void 0), trailing && jsx("span", Object.assign({
        className: classNames(style$1.icon, style$1.trailing)
      }, {
        children: trailing
      }), void 0)]
    }), void 0), !!helperContent && jsx(FormHelperText, Object.assign({
      className: style$1.helperText,
      disabled: disabled,
      id: helperId,
      hasError: !!error
    }, {
      children: helperContent
    }), void 0)]
  }), void 0);
});
TextField.displayName = "TextField";

var style = {"wizardFooter":"WizardFooter-module_wizardFooter__2Wyma","wizardContent":"WizardFooter-module_wizardContent__92KpB","wizardPrevious":"WizardFooter-module_wizardPrevious__cYplS","wizardNext":"WizardFooter-module_wizardNext__Q9wF0"};

/**
 * Wizards provide context for the process such as name of the process, total steps, and current step, as well as navigation between steps.
 *
 * - {@link https://livingdesign.walmart.com/components/wizard/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/wizard/react/ React documentation}
 */

const WizardFooter = props => {
  const _a = applyCommonProps(props, WizardFooter),
        {
    children,
    className,
    nextButtonProps,
    previousButtonProps
  } = _a,
        rest = __rest(_a, ["children", "className", "nextButtonProps", "previousButtonProps"]);

  return jsxs("div", Object.assign({
    className: classNames(style.wizardFooter, className)
  }, rest, {
    children: [children && jsx("div", Object.assign({
      className: style.wizardContent
    }, {
      children: children
    }), void 0), jsx("div", Object.assign({
      className: style.wizardPrevious
    }, {
      children: jsx(Button, Object.assign({
        children: "Previous"
      }, previousButtonProps, {
        size: "medium",
        variant: "secondary"
      }), void 0)
    }), void 0), jsx("div", Object.assign({
      className: style.wizardNext
    }, {
      children: jsx(Button, Object.assign({
        children: "Continue"
      }, nextButtonProps, {
        size: "medium",
        variant: "primary"
      }), void 0)
    }), void 0)]
  }), void 0);
};
WizardFooter.displayName = "WizardFooter";

export { A11yAnnouncementContext, A11yAnnouncementProvider, Alert, BOGLE_BASELINE_RATIO, Badge, Banner, Body, BottomSheet, OverlayCloseButton as BottomSheetCloseButton, Breadcrumb, BreadcrumbItem, Button, ButtonGroup, Callout, Caption, Card, CardActions, CardContent, CardHeader, CardMedia, Checkbox, Chip, ChipGroup, Collapse, Container, DataTable, DataTableBody, DataTableBulkActions, DataTableCell, DataTableCellActions, DataTableCellActionsMenu, DataTableCellActionsMenuItem, DataTableCellSelect, DataTableCellStatus, DataTableHead, DataTableHeader, DataTableHeaderSelect, DataTableRow, Display, Divider, ErrorMessage, FocusTrap, FormGroup, Grid, GridColumn, Heading, IconButton, LineClamp, Link, List, ListItem, LivingDesignProvider, Menu, MenuItem, Metric, Modal, Nudge, Panel, Popover, ProgressIndicator, ProgressTracker, ProgressTrackerItem, Radio, Rating, Select, SideNavigation, SideNavigationItem, Skeleton, SkeletonText, SnackbarContext, SnackbarProvider, Spinner, SpotIcon, StyledText, Switch, TabNavigation, TabNavigationItem, Tag, TextArea, TextField, VisuallyHidden, WizardFooter, useA11yAnnouncement, useCollapseTransition, useLockBodyScroll, useOnClick, useOnClickInside, useOnClickOutside, useOnKeyDown, useSnackbar };
